import React, { useState, useEffect, useLayoutEffect, useCallback, useRef,  useMemo} from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  IconButton,
  Tooltip,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  styled,
  Collapse,
  Select,
  MenuItem,
  Popper,
  Paper,
  ClickAwayListener,
} from "@mui/material";
import {
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as CircleIcon,
  PlayArrow as PlayArrowIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Upload as UploadIcon,
  Download as DownloadIcon,
  Movie as MovieIcon,
  AutoAwesomeMotion as AutoAwesomeMotionIcon,
  OndemandVideo as OndemandVideoIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  CloudUpload as CloudUploadIcon,
  DragIndicator as DragIndicatorIcon,
  KeyboardArrowUp as ArrowUpIcon,
  KeyboardArrowDown as ArrowDownIcon,
  Close as CloseIcon,
  YouTube as YouTubeIcon,
  Edit as EditIcon,
  NavigateBefore as NavigateBeforeIcon,
  NavigateNext as NavigateNextIcon,
  GridView as GridViewIcon,
  ViewList as ViewListIcon,
  ErrorOutline as ErrorIcon,
  Stop as StopIcon,
  AudioFile as AudioFileIcon,
  VideoFile as VideoFileIcon,
  Description as DescriptionIcon,
  Link as LinkIcon,
} from "@mui/icons-material";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  rectSortingStrategy,
} from "@dnd-kit/sortable";
import { saveAs } from "file-saver";

// Components
import MeGenBreadcrumbs, { BreadcrumbItem } from "./common/MeGenBreadcrumbs";
import TextOverlayEditor from "./common/TextOverlayEditor";
import ImageOverlayEditor from "./common/ImageOverlayEditor";
import ImageAssetsPanel from "./common/ImageAssetsPanel";
import {
  clampReferences,
  defaultLabel,
  effectiveImageModel,
  enabledModels,
  findImageModel,
  referenceCap,
  sceneAllowsReferences,
} from "./common/imageModelCatalog";
import {
  ASPECT_RATIO_OPTIONS,
  DEFAULT_ASPECT_RATIO,
  DEFAULT_IMAGE_STYLE,
  DEFAULT_VOICE_NAME,
} from "./common/megenDefaults";
import { sceneBlocksVideoGenerate } from "./common/videoModeIssues";
import { versionAssetTypesFor } from "./common/segmentVersions";
import { externalVideoLinkOf } from "./common/externalVideo";
import {
  downloadActions,
  downloadErrorMessage,
  downloadFilename,
  episodeSceneSources,
  isAbortError,
  isReviewSegmentUrl,
  mediaExtFromUrl,
  zipEntriesFor,
  EpisodeMediaKind,
} from "./common/episodeDownload";
import { buildScriptTextFile, SCRIPT_TEXT_MIME, spokenScripts } from "./common/scriptDownload";
import {
  DEFAULT_VOICE_GUIDE,
  hydrateDirectInputVoicePrompt,
  hydrateVoicePrompt,
  synchronizeVoicePrompt,
} from "./common/voicePrompt";
import { buildZipFromUrls, fetchMediaBlob } from "./common/browserZipDownload";
import ModConfirmDelete from "./common/ModConfirmDelete";
import ModMeGenAiUsage from "./common/ModMeGenAiUsage";
import AiUsageButton, { FetchAiUsage } from "./common/AiUsageButton";

// Utils
import {
  AUDIO_PADDING_MAX,
  buildCleanedScript as buildCleanedScriptPure,
  buildDirectDraftItem,
  mergeScriptFromPoll,
  parsePadding,
  resolveRunName,
} from "./scriptPayload";
import CardMeGenCostModal from "./CardMeGenCostModal";
import CardMeGenSortableScene from "./CardMeGenSortableScene";
import CardMeGenSettingsSection from "./CardMeGenSettingsSection";
import CardMeGenFinalVideoDirectScript from "./CardMeGenFinalVideoDirectScript";

// Hooks
import useRunPolling, { PollingEvent } from "./useRunPolling";

// Utils
import moment from "moment";

// Interfaces
import {
  Event,
  getDirectStaticUrl,
  getStaticUrl,
  getUploadedStaticUrl,
  getOutputStaticUrl,
} from "./MeGenInterface";
import { isStoppedByUser } from "./MeGenRunStatus";
import { ApiError, isClientError } from "./common/apiErrors";
import { closeVersionPanelOn } from "./common/segmentVersions";

// Types
import {
  DirectSceneItem,
  DirectScriptInput,
  DirectScriptGraphState,
  FinalScriptData,
  ScriptWithImageItem,
  SeriesOption,
  STEPS_DIRECT,
  STEPS_VDO,
  StartGenerateDirectScriptPayload,
  TextOverlay,
  ImageOverlay,
  ImageAsset,
  DirectScriptOutputResponse,
  SegmentVersionResponse,
  AssetType,
  YouTubeChannel,
  ScriptToSceneGraphState,
  STEPS_SCRIPT2SCENE,
  RefineScriptToScenePayload,
  RefineScriptToSceneResponse,
  CheckCostResponse,
  CostDetails,
  RegenerateItem,
  MediaStyle,
  UpdateTitlePayload,
  CharacterSerializer,
  GenerateCharacterPayload,
  UploadCharacterPayload,
  DirectScriptProgress,
  VoiceSerializer,
  ImageModelCatalog,
  VideoModelCatalog,
} from "./types/MeGenTypes";

import CONFIG from "config/config";

// Props
// ── Image Model ──
// list / default / ความสามารถ / เพดานรูปอ้างอิง มาจาก catalog หลังบ้าน (props.imageModelCatalog) เท่านั้น
// ห้าม hardcode ชื่อโมเดลหรือ default ที่นี่ — ดู ./common/imageModelCatalog
// ── Video Model ──
// เหมือนกัน แต่ dropdown อยู่ใน CardMeGenSettingsSection ⇒ ไฟล์นี้แค่รับ props.videoModelCatalog แล้วส่งต่อ

export type CardGenerateDirectScriptProps = {
  projectId: number;
  projectName: string;
  runTitle?: string; // title จาก run list ของ project (fallback สำหรับ breadcrumb)
  seriesId?: number;
  seriesName?: string;
  seriesList?: SeriesOption[];
  threadId: string | null;
  isRunning?: boolean;
  defaultFinalOutput?: boolean;
  canEdit?: boolean; // false = viewer ของโปรเจกต์ที่แชร์มา — ไม่ยิง endpoint ที่ต้องเป็น editor เองโดยไม่มี user กด · ไม่ส่ง = แก้ได้
  isScriptToSceneRunning?: boolean;
  youtubeChannels?: YouTubeChannel[];
  characterList?: CharacterSerializer[];
  isCharacterGenerating?: boolean;
  onNavigateBack?: () => void;
  canViewAiUsage?: boolean;
  onFetchAiUsage?: FetchAiUsage;
  aiUsageUserNameMap?: Record<string, string>;
  onDirectGenerate?: (
    threadId: string | null,
    projectId: number,
    seriesId: number | null,
    data: DirectScriptInput,
    dryRun: boolean,
  ) => void;
  mediaStyleList?: MediaStyle[];
  voiceList?: VoiceSerializer[];
  imageModelCatalog?: ImageModelCatalog | null; // null = catalog ยังไม่มา/โหลดไม่สำเร็จ
  videoModelCatalog?: VideoModelCatalog | null; // null = catalog ยังไม่มา/โหลดไม่สำเร็จ
  onGenerateVideo?: (threadId: string, data: StartGenerateDirectScriptPayload) => void;
  onGenerateVideoFinal?: (threadId: string, data: StartGenerateDirectScriptPayload) => void;
  onUpdateScript?: (threadId: string, data: FinalScriptData) => void;
  onUploadYoutube?: (
    threadId: string,
    credentialId: number | null,
    onSuccess?: (newState: DirectScriptGraphState, isGenerating: boolean) => void,
  ) => void;
  onUploadVideo?: (
    threadId: string,
    itemId: string,
    file: File,
    onSuccess?: (url: string) => void,
    onError?: () => void,
  ) => void;
  onUploadImage?: (
    threadId: string,
    itemId: string,
    file: File,
    onSuccess?: (url: string) => void,
    onError?: () => void,
  ) => void;
  onUploadImageOverlay?: (
    threadId: string,
    itemId: string,
    file: File,
    onSuccess?: (url: string) => void,
    onError?: () => void,
  ) => void;
  onDeleteImageAsset?: (
    threadId: string,
    filename: string,
    onSuccess?: (assets: ImageAsset[]) => void,
    onError?: () => void,
  ) => void;
  onUploadImageAsset?: (
    threadId: string,
    file: File,
    onSuccess?: (assets: ImageAsset[]) => void,
    onError?: () => void,
  ) => void;
  onGetImageAssets?: (
    threadId: string,
    onSuccess?: (assets: ImageAsset[]) => void,
    onError?: () => void,
  ) => void;
  onGeneratingChange?: (isGenerating: boolean) => void;
  // การ์ดนี้ไม่มี snack ของตัวเอง — CardMeGenMain ส่งข้อความเข้า errorMessage ของ CARD_NAME ให้แทน
  // ไม่ส่ง handler มา = ถอยไปใช้ window.alert เพื่อไม่ให้ความผิดพลาดเงียบหายไป
  onDownloadError?: (message: string) => void;
  onGetSegmentVersions?: (
    threadId: string,
    segmentId: string,
    assetType: AssetType,
    onSuccess: (data: SegmentVersionResponse) => void,
    opts?: { onError?: (err: ApiError) => void; silent?: boolean },
  ) => void;
  onSetCurrentVersion?: (
    threadId: string,
    segmentId: string,
    assetType: AssetType,
    version: number,
    onSuccess?: () => void,
  ) => void;
  onCheckCost?: (
    threadId: string,
    data: StartGenerateDirectScriptPayload,
    onSuccess?: (result: CheckCostResponse) => void,
    onError?: (err: ApiError) => void,
  ) => void,
  onGenerateScene?: (
    threadId: string,
    data: StartGenerateDirectScriptPayload,
    onSuccess?: () => void,
  ) => void;
  onStopRun?: (
    threadId: string,
    onSuccess?: () => void,
  ) => void;
  onUpdateTitle?: (
    threadId: string,
    data: UpdateTitlePayload,
    onSuccess?: (title: string) => void,
    onError?: () => void,
  ) => void;
  onGetCharacterList?: (seriesId: number) => void;
  onGenerateCharacter?: (payload: GenerateCharacterPayload) => void;
  onUploadCharacter?: (payload: UploadCharacterPayload) => void;
  onDeleteCharacter?: (characterId: number, seriesId: number) => void;
  // Script2Scene (Pre-Step)
  onCreateScriptToSceneRun?: (
    projectId: number,
    seriesId: number | null,
    onSuccess: (threadId: string) => void,
  ) => void;
  onStartScriptToScene?: (
    threadId: string,
    script: string,
    onSuccess?: (threadId: string) => void,
  ) => void;
  onGetScriptToSceneState?: (
    threadId: string,
    onSuccess?: (state: ScriptToSceneGraphState, isGenerating: boolean) => void,
  ) => void;
  onRefineScriptToScene?: (
    threadId: string,
    data: RefineScriptToScenePayload,
    onSuccess?: (result: RefineScriptToSceneResponse) => void,
    onError?: () => void,
  ) => void,
  //
  onEvent?: (e: Event) => void;
  BillingAndPackageSequence: any;
  runSequence: any;
  selectedDivisionId: any;
  finalScript: any;
};

// ─── Constants ───────────────────────────────────────────────────────────────

const KEN_BURNS_EFFECTS = [
  { key: 1, value: "off",         text: "No Effect (Disable)" },
  { key: 2, value: "random",      text: "Random Effect" },
  { key: 3, value: "zoom_in",     text: "Zoom In" },
  { key: 4, value: "zoom_out",    text: "Zoom Out" },
  { key: 5, value: "pan_left",    text: "Pan Left" },
  { key: 6, value: "pan_right",   text: "Pan Right" },
  { key: 7, value: "pan_top",     text: "Pan Top" },
  { key: 8, value: "pan_bottom",  text: "Pan Bottom" },
];

// ─── Styled Components ───────────────────────────────────────────────────────

const Container = styled(Box)({
  padding: "0 10px",
  height: "100%"
});

const SectionCard = styled(Box)({
  backgroundColor: "white",
  borderRadius: "0.75rem",
  boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
  padding: "1rem",
  marginBottom: "0.95rem",
});

const CollapseCard = styled(Collapse)({})

const SectionTitle = styled(Typography)({
  fontSize: "1.125rem",
  fontWeight: 700,
  color: "#1e293b",
  marginBottom: "1rem",
});

const FormLabel = styled(Typography)({
  fontSize: "0.875rem",
  fontWeight: 500,
  color: "#64748b",
  marginBottom: "0.5rem",
});

const StyledTextField = styled(TextField)({
  "& .MuiOutlinedInput-root": {
    backgroundColor: "white",
    borderRadius: "0.5rem",
    "& fieldset": { borderColor: "#e2e8f0" },
    "&:hover fieldset": { borderColor: "#cbd5e1" },
    "&.Mui-focused fieldset": { borderColor: "#f382a0", borderWidth: "1px" },
  },
  "& .MuiInputBase-input": {
    padding: "12px 14px",
    fontSize: "0.875rem",
    color: "#334155",
  },
});

const ActionButton = styled(Button)({
  textTransform: "none",
  fontWeight: 600,
  fontSize: "0.875rem",
  padding: "10px 24px",
  borderRadius: "9999px",
});

const PrimaryButton = styled(ActionButton)({
  backgroundColor: "#3b82f6",
  color: "white",
  "&:hover": { backgroundColor: "#2563eb" },
  "&:disabled": { backgroundColor: "#94a3b8" },
});

const SuccessButton = styled(ActionButton)({
  backgroundColor: "#f382a0",
  color: "white",
  "&:hover": { backgroundColor: "#c75578" },
  "&:disabled": { backgroundColor: "#94a3b8" },
});

const DangerButton = styled(ActionButton)({
  backgroundColor: "#ef4444",
  color: "white",
  "&:hover": { backgroundColor: "#dc2626" },
  // ปุ่มนี้ถูกปิดระหว่างดาวน์โหลดแล้ว ต้องมีสีของสถานะปิดไม่งั้นดูเหมือนกดได้อยู่
  "&:disabled": { backgroundColor: "#94a3b8" },
});

// sx ร่วมของทุกปุ่มบน toolbar ของ Scene Preview — ยกมาจาก inline sx เดิมของปุ่ม New Scene ทั้งดุ้น
// เพื่อให้ปุ่มดาวน์โหลดที่เพิ่มเข้ามาหน้าตาตรงกันเป๊ะ และแก้สไตล์ของทั้งแถวได้ที่เดียว
const PANEL_TOOLBAR_BUTTON_SX = {
  textTransform: "none",
  fontSize: "0.75rem",
  fontWeight: 600,
  color: "#475569",
  borderRadius: "0.375rem",
  px: 1,
  py: 0.5,
  minWidth: 0,
  "&:hover": { backgroundColor: "#f1f5f9", color: "#db2777" },
} as const;

// บอก host ของ "URL ที่โหลดจริง" ในข้อความ error ไม่ใช่ของ API_URL เพราะ API_URL ว่างได้ตอน dev
// (ตอนนั้นจะยิงผ่าน proxy ของ dev server) และ URL ของหลังบ้านเป็น path ใต้ API_URL อยู่แล้ว
// ⇒ อ่านจาก URL จริงได้ host ที่ตรงกับคำขอที่พังเสมอ · fallback เป็น host ของหน้าเมื่อ URL ว่าง/ไม่ valid
const apiHostForMessage = (fetchedUrl: string): string => {
  try {
    return new URL(fetchedUrl, window.location.origin).host;
  } catch {
    return window.location.host;
  }
};

// งานดาวน์โหลดทั้งตอนที่กำลังทำอยู่ — เก็บ threadId ของตอนที่กดไว้ด้วย เพื่อตรวจตอนงานเสร็จว่ายังเป็น run เดิม
type EpisodeDownloadJob = { threadId: string; controller: AbortController };

const SceneCard = styled(Box)({
  backgroundColor: "white",
  borderRadius: "0.75rem",
  border: "1px solid #e2e8f0",
  padding: "1rem",
  marginBottom: "1rem",
  position: "relative",
});

const SceneHeader = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  marginBottom: "0.75rem",
  padding: "0.5rem 0.75rem",
  backgroundColor: "#f8fafc",
  borderRadius: "0.5rem",
  borderBottom: "1px solid #e2e8f0",
});

const DeleteSceneButton = styled(IconButton)({
  color: "#ef4444",
  padding: "4px",
  "&:hover": { backgroundColor: "#fef2f2" },
});

const StepIndicator = styled(Box)<{ status: "done" | "active" | "pending" | "error" | "skipped" }>(({ status }) => ({
  width: "18px",
  height: "18px",
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  border: "1.5px solid",
  borderColor:
    status === "done" ? "#22c55e" :
      status === "active" ? "#f382a0" :
        status === "error" ? "#ef4444" :
          "#cbd5e1",
  backgroundColor:
    status === "done" ? "#dcfce7" :
      status === "active" ? "#fdf2f8" :
        status === "error" ? "#fef2f2" :
          "#f8fafc",
  color:
    status === "done" ? "#22c55e" :
      status === "active" ? "#f382a0" :
        status === "error" ? "#ef4444" :
          "#cbd5e1",
  transition: "all 0.2s ease",
}));

const StepRow = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "0.5rem",
  padding: "0.3rem 0.5rem",
  borderRadius: "0.375rem",
  marginBottom: "0.125rem",
  "&:hover": { backgroundColor: "#f8fafc" },
});

const StepLabel = styled(Typography)<{ status: "done" | "active" | "pending" | "error" | "skipped" }>(({ status }) => ({
  flex: 1,
  fontSize: "0.75rem",
  fontWeight: status === "active" || status === "error" ? 600 : 400,
  color:
    status === "done" ? "#475569" :
      status === "active" ? "#f382a0" :
        status === "error" ? "#ef4444" :
          "#94a3b8",
}));

const TextOverlaysTooltip = styled(Box)({
  padding: "0.625rem 1rem",
  backgroundColor: "#fdf2f8",
  border: "1px solid #f9a8d4",
  borderRadius: "0.5rem",
  marginBottom: "1rem",
});

// ─── PowerPoint Layout Styled Components ──────────────────────────────────────

const ReviewPanelContainer = styled(Box)({
  display: "flex",
  maxHeight: "65vh",
  height: "calc(100vh - 380px)",
  minHeight: "300px",
  borderRadius: "0.5rem",
  overflow: "hidden",
  border: "1px solid #e2e8f0",
  "@media (max-width: 900px)": {
    flexDirection: "column",
    maxHeight: "none",
    minHeight: "200px",
  },
});

const SceneListPanel = styled(Box)({
  display: "flex",
  flexDirection: "column",
  backgroundColor: "#f8fafc",
  flexShrink: 0,
  "@media (max-width: 900px)": {
    width: "100% !important",
    maxHeight: "240px",
  },
});

const SceneListScrollArea = styled(Box)({
  flex: 1,
  overflowY: "auto",
  padding: "0.5rem",
});

const SceneListItem = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isActive",
})<{ isActive?: boolean }>(({ isActive }) => ({
  display: "flex",
  alignItems: "center",
  gap: "0.5rem",
  padding: "0.375rem",
  marginBottom: "0.375rem",
  borderRadius: "0.5rem",
  cursor: "pointer",
  backgroundColor: isActive ? "#fdf2f8" : "transparent",
  border: isActive ? "2px solid #f9a8d4" : "2px solid transparent",
  transition: "all 0.15s ease",
  "& .scene-delete-btn": {
    opacity: 0,
  },
  "&:hover .scene-delete-btn": {
    opacity: 1,
  },
  "&:hover": {
    backgroundColor: isActive ? "#fdf2f8" : "#f1f5f9",
    borderColor: isActive ? "#f9a8d4" : "#e2e8f0",
  },
}));

const PanelDragHandle = styled(Box)({
  width: "6px",
  cursor: "col-resize",
  backgroundColor: "#e2e8f0",
  flexShrink: 0,
  position: "relative",
  transition: "background-color 0.15s",
  "&:hover": {
    backgroundColor: "#cbd5e1",
  },
  "&::after": {
    content: '""',
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "2px",
    height: "32px",
    backgroundColor: "#94a3b8",
    borderRadius: "1px",
  },
  "@media (max-width: 900px)": {
    display: "none",
  },
});

const SceneContentPanel = styled(Box)({
  flex: 1,
  overflowY: "auto",
  padding: "1rem",
  backgroundColor: "white",
  minWidth: 0,
});



// ─── Component ────────────────────────────────────────────────────────────────

const CardGenerateDirectScript: React.FC<CardGenerateDirectScriptProps> = (props) => {
  const initialSeriesId = props.seriesId;
  const seriesList = props.seriesList ?? [];
  const isRunning = props.isRunning ?? false;
  const canEdit = props.canEdit ?? true; // viewer = false (ดู common/projectRole.ts)
  const youtubeChannels = props.youtubeChannels ?? [];

  // ── Progress collapse ──
  const [isProgressExpanded, setIsProgressExpanded] = useState(false);
  const [expandedStepId, setExpandedStepId] = useState<string | null>(null);
  const [isStopping, setIsStopping] = useState<boolean>(false);

  // ── Form State ──
  const [directTitle, setDirectTitle] = useState("");
  const [selectedImageStyle, setSelectedImageStyle] = useState<string>(DEFAULT_IMAGE_STYLE);
  const [aspectRatio, setAspectRatio] = useState<string>(DEFAULT_ASPECT_RATIO);
  const [selectedVoiceName, setSelectedVoiceName] = useState<string>(DEFAULT_VOICE_NAME);
  const [directItems, setDirectItems] = useState<DirectSceneItem[]>([
    { id: "1", script: "", voice_guide: DEFAULT_VOICE_GUIDE, voice_script: "", image_prompt: "" },
  ]);
  const [selectedSeriesId, setSelectedSeriesId] = useState<number | "">(initialSeriesId || "");

  // ── Edit Title State ──
  const [isEditingTitle, setIsEditingTitle] = useState<boolean>(false);
  const [titleDraft, setTitleDraft] = useState<string>("");
  const [isAiUsageOpen, setIsAiUsageOpen] = useState(false);
  const [titleOverride, setTitleOverride] = useState<string | null>(null);

  // ── Review State ──
  const [scriptWithImage, setScriptWithImage] = useState<FinalScriptData | null>(null);

  // ── NEW: Input / Review collapse state ──
  const [isInputCollapsed, setIsInputCollapsed] = useState(false);
  const [isReviewCollapsed, setIsReviewCollapsed] = useState(false);

  // ── Edit Mode / Final Output Mode in Scene Preview Section ---
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [isFinalOutputMode, setIsFinalOutputMode] = useState<boolean>(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [goToInputMap, setGoToInputMap] = useState<Record<string, string>>({});
  const [deleteModalItemId, setDeleteModalItemId] = useState<string | null>(null);

  // ── PowerPoint Layout: selected scene + resizable panel ---
  const [selectedSceneIndex, setSelectedSceneIndex] = useState<number | "final">(0);
  const [panelWidth, setPanelWidth] = useState(280);
  const [isGridMode, setIsGridMode] = useState(false);
  const isDraggingRef = useRef(false);
  const dragStartXRef = useRef(0);
  const dragStartWidthRef = useRef(0);

  // Track if first-time auto-collapse of input has happened
  const hasAutoCollapsedRef = useRef(false);
  const hasDefaultedAudioOnlyRef = useRef(false);

  // ── Loading State ──
  const [loadingDraft, setLoadingDraft] = useState(false);
  const [loadingVideo, setLoadingVideo] = useState(false);
  const [uploadingVideoItemId, setUploadingVideoItemId] = useState<string | null>(null);
  const [uploadingImageItemId, setUploadingImageItemId] = useState<string | null>(null);

  // ── Editing State ──
  const [isEditingInput, setIsEditingInput] = useState(false);
  const [isUserEditing, setIsUserEditing] = useState(false);

  // ── Force Regen Map ──
  const [forceRegenMap, setForceRegenMap] = useState<Record<string, string[]>>({});

  // ── Generate as Final checkbox ──
  const [generateAsFinal, setGenerateAsFinal] = useState(!!props.defaultFinalOutput);

  // ── Generate as Audio Only checkbox ──
  const [generateAsAudioOnly, setGenerateAsAudioOnly] = useState(!!props.defaultAudioOnly);
  // Run-level default audio padding (วินาที) — เก็บเป็น string เพื่อพิมพ์ทศนิยมได้
  const [globalPaddingStart, setGlobalPaddingStart] = useState<string>("0");
  const [globalPaddingEnd, setGlobalPaddingEnd] = useState<string>("0");
  // ติ๊ก = ใช้ค่ากลางกับทุก scene (ช่อง padding ใน scene ถูก disable) / ไม่ติ๊ก = กำหนดอิสระต่อ scene
  const [useGlobalPadding, setUseGlobalPadding] = useState<boolean>(false);

  // ── Local State ──
  const [graphState, setGraphState] = useState<DirectScriptGraphState | undefined>(undefined);
  const [outputData, setOutputData] = useState<DirectScriptOutputResponse | undefined>(undefined);

  // ── Episode Download State ──
  // ref กันดับเบิลคลิกแบบ sync (setState ยังไม่มีผลทันก่อน onClick ครั้งที่สอง) และเป็นที่จับ AbortController
  const episodeDownloadJobRef = useRef<EpisodeDownloadJob | null>(null);
  // state คู่กันไว้วาด spinner/ปิดปุ่มเท่านั้น เพราะการเขียน ref ไม่ทำให้ re-render
  const [downloadJob, setDownloadJob] = useState<EpisodeMediaKind | null>(null);
  const [downloadProgress, setDownloadProgress] = useState<{ done: number; total: number } | null>(null);

  // ── Versions State ──
  const [versionMap, setVersionMap] = useState<
    Record<string, Record<AssetType, SegmentVersionResponse>>
  >({});
  const [selectingVersionKey, setSelectingVersionKey] = useState<{
    itemId: string;
    assetType: AssetType;
  } | null>(null);
  const [pendingVersionMap, setPendingVersionMap] = useState<
    Record<string, Record<AssetType, number | null>>
  >({});
  const [showUploadModal, setShowUploadModal] = useState<boolean>(false);

  // ── Generate Scene (per scene)
  const [generatingSceneItemId, setGeneratingSceneItemId] = useState<string | null>(null);
  const [pendingGenerateSceneItemId, setPendingGenerateSceneItemId] = useState<string | null>(null);
  const [isGeneratingScene, setIsGeneratingScene] = useState<boolean>(false);
  const [showGenActionModal, setShowGenActionModal] = useState<boolean>(false);
  const [videoModeIssueScenes, setVideoModeIssueScenes] = useState<number[] | null>(null);

  // ── Image Assets (thread-scoped, lazy fetch + cache) ──
  const [imageAssets, setImageAssets] = useState<ImageAsset[]>([]);
  const [isLoadingAssets, setIsLoadingAssets] = useState<boolean>(false);
  const [isUploadingAsset, setIsUploadingAsset] = useState<boolean>(false);
  // asset ที่กำลังขอลบ (เปิด modal ยืนยัน) + ตัวที่กำลังลบจริง
  const [pendingDeleteAsset, setPendingDeleteAsset] = useState<ImageAsset | null>(null);
  const [deletingAssetFilename, setDeletingAssetFilename] = useState<string | null>(null);
  const hasFetchedAssetsRef = useRef<boolean>(false);
  const [pendingGenScenePayload, setPendingGenScenePayload] = useState<StartGenerateDirectScriptPayload | null>(null);

  const genSceneBtnRef = useRef<HTMLButtonElement | null>(null);

  // ── Generate Cost Modal
  const [showCostModal, setShowCostModal] = useState<boolean>(false);
  const [loadingCostCheck, setLoadingCostCheck] = useState<boolean>(false);
  const [costDetails, setCostDetails] = useState<CostDetails | null>(null);
  const [regenerateItems, setRegenerateItems] = useState<RegenerateItem[]>([]);
  const [pendingGeneratePayload, setPendingGeneratePayload] = useState<StartGenerateDirectScriptPayload | null>(null);

  // ── Script2Scene State ──
  const [scriptText, setScriptText] = useState<string>("");
  const [isScriptToSceneLoading, setIsScriptToSceneLoading] = useState<boolean>(false);
  const [scriptToSceneThreadId, setScriptToSceneThreadId] = useState<string | null>(null);
  const [scriptToSceneProgress, setScriptToSceneProgress] =
    useState<ScriptToSceneGraphState | null>(null);
  const [isScriptToSceneGenerating, setIsScriptToSceneGenerating] = useState<boolean>(false);
  const [isScriptToScenePending, setIsScriptToScenePending] = useState<boolean>(false); // รอprocess

  // ── Refine Script State ──
  const [refinePrompt, setRefinePrompt] = useState<string>("");
  const [isRefining, setIsRefining] = useState<boolean>(false);

  // ── Use Animated State ──
  const [localUseAnimated, setLocalUseAnimated] = useState<Record<string, boolean>>({});

  // ── DND ──
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setDirectItems((prev) => {
      const oldIndex = prev.findIndex((item) => item.id === active.id);
      const newIndex = prev.findIndex((item) => item.id === over.id);
      const reordered = arrayMove(prev, oldIndex, newIndex);
      return reordered.map((item, i) => ({ ...item, id: `${i + 1}` }));
    });
  };

  // ── Derived State ──
  // catalog โมเดล image — null = ยังไม่โหลด/โหลดไม่สำเร็จ (ห้ามซ่อนหรือล้างข้อมูลของ user)
  const imageModelCatalog = props.imageModelCatalog ?? null;
  // catalog โมเดล video — ส่งต่อลง CardMeGenSettingsSection ซึ่งเป็นที่อยู่ของ dropdown
  const videoModelCatalog = props.videoModelCatalog ?? null;

  // hasReviewData: true เมื่อ draft เสร็จแล้ว (มี scriptWithImage)
  const hasReviewData = (scriptWithImage?.items?.length || 0) > 0;
  const isScriptToSceneDone = directItems.some((item) => hydrateVoicePrompt(item).voice_script.trim() !== "");

  // ─── Polling ────────────────────────────────────────────────────────────────

  // Use refs for props that are objects/functions to avoid recreating the
  // callback on every render, which would otherwise destabilize useRunPolling.
  const onGeneratingChangeRef = useRef(props.onGeneratingChange);
  const runSequenceRef = useRef(props.runSequence);
  const billingSequenceRef = useRef(props.BillingAndPackageSequence);
  const onEventRef = useRef(props.onEvent);
  const isUserEditingRef = useRef(isUserEditing);
  useEffect(() => { isUserEditingRef.current = isUserEditing; });
  useEffect(() => { onGeneratingChangeRef.current = props.onGeneratingChange; });
  useEffect(() => { runSequenceRef.current = props.runSequence; });
  useEffect(() => { billingSequenceRef.current = props.BillingAndPackageSequence; });
  useEffect(() => { onEventRef.current = props.onEvent; });

  const handlePollingSuccess = useCallback(
    (newState: any, isGenerating: boolean, outputState?: DirectScriptOutputResponse) => {
      if (!isGenerating) {
        const hasAnyProgress = newState?.progress
          && Object.keys(newState.progress).length > 0;
        if (hasAnyProgress) {
          setIsGeneratingScene(false);
          setGeneratingSceneItemId(null);
        }
      }
      if (billingSequenceRef.current?.sequenceIndex === "Action") {
        runSequenceRef.current?.({ sequence: "BillingAndPackage", action: "GET_CREDIT_WALLET" });
      }
      setGraphState((prev) => {
        if (prev === newState) return prev;
        if (prev && JSON.stringify(prev) === JSON.stringify(newState)) return prev;
        return newState;
      });
      if (outputState) setOutputData(outputState);
      onGeneratingChangeRef.current?.(isGenerating);
    },
    // Stable — all mutable values accessed via refs, no props in the dep array.
    [],
  );

  const handleScriptToSceneSuccess = useCallback(
    (newState: ScriptToSceneGraphState, isGenerating: boolean) => {
      setScriptToSceneProgress(newState);
      if (!isGenerating) {
        setIsScriptToSceneGenerating(false);
        setIsScriptToSceneLoading(false);
        setIsScriptToScenePending(false);
        if (newState.values?.raw_scripts_images) {
          const raw = newState.values.raw_scripts_images;
          setDirectTitle(raw.title_podcast || "");
          setDirectItems(
            raw.items.map((item) => hydrateDirectInputVoicePrompt({
              id: item.id,
              script: item.script,
              voice_guide: item.voice_guide,
              voice_script: item.voice_script,
              image_prompt: item.visual,
            })),
          );
        }
      }
    },
    [],
  );

  // 4xx ตอน poll (run ถูกลบ / ไม่มีสิทธิ์) = คำตอบชัดเจนแล้ว — flag ฝั่งการ์ดที่ล้างเฉพาะใน onSuccess
  // จะค้ำ useRunPolling ให้ยิงต่อทุก 30 วิ และ snack เด้งซ้ำทุกรอบหลัง auto-hide จึงต้องปิดที่นี่
  // (network/5xx ไม่ถึง callback นี้ — handler ปล่อยให้ลองรอบถัดไปเหมือนเดิม)
  const handlePollingError = useCallback(() => {
    setIsGeneratingScene(false);
    setGeneratingSceneItemId(null);
  }, []);

  const handleScriptToSceneError = useCallback(() => {
    setIsScriptToSceneGenerating(false);
    setIsScriptToSceneLoading(false);
    setIsScriptToScenePending(false);
  }, []);

  // Script2vdo polling
  useRunPolling({
    threadId: props.threadId,
    isGenerating: (isRunning || isGeneratingScene) && !isScriptToScenePending,
    eventMessage: "HandleGetDirectScriptState",
    onEvent: props.onEvent! as (e: PollingEvent) => void,
    onSuccess: handlePollingSuccess,
    onError: handlePollingError,
  });

  // Script2Scene polling
  useRunPolling({
    threadId: scriptToSceneThreadId,
    isGenerating: props.isScriptToSceneRunning || isScriptToSceneGenerating,
    eventMessage: "HandleGetScriptToSceneState",
    onEvent: props.onEvent as (e: PollingEvent) => void,
    onSuccess: handleScriptToSceneSuccess,
    onError: handleScriptToSceneError,
  });

  // ─── Effects ────────────────────────────────────────────────────────────────

  useEffect(() => {
    if (props.finalScript?.aspect_ratio) {
      setAspectRatio(props.finalScript?.aspect_ratio)
    }
  }, [props.finalScript])

  // Fetch Remain Credit
  useEffect(() => {
    if (props.BillingAndPackageSequence?.sequenceIndex === "Action") {
      props.runSequence({ sequence: "BillingAndPackage", action: "GET_CREDIT_WALLET" });
    }
  }, [props.BillingAndPackageSequence?.sequenceIndex, props.selectedDivisionId])

  // Fetch final state เมื่อ polling หยุด
  const isAnyGenerating = isRunning || isGeneratingScene;
  const prevIsAnyGeneratingRef = useRef(isAnyGenerating);
  useEffect(() => {
    const wasGenerating = prevIsAnyGeneratingRef.current;
    prevIsAnyGeneratingRef.current = isAnyGenerating;
    if (!(wasGenerating && !isAnyGenerating && props.threadId)) return;

    const threadId = props.threadId;
    const timeoutId = setTimeout(() => {
      onEventRef.current?.({
        message: "HandleGetDirectScriptState",
        params: {
          threadId,
          onSuccess: (
            newState: any,
            stillGenerating: boolean,
            outputState?: DirectScriptOutputResponse,
          ) => {
            setGraphState((prev) => {
              if (prev && JSON.stringify(prev) === JSON.stringify(newState)) return prev;
              return newState;
            });
            if (outputState) setOutputData(outputState);
            if (stillGenerating) setIsGeneratingScene(true);
          },
        },
      });
    }, 100); // หน่วงสั้น ๆ เผื่อ state รอบสุดท้ายฝั่ง backend ยังไม่นิ่ง (trigger คือ isAnyGenerating true→false ฝั่งเรา)

    return () => clearTimeout(timeoutId);
  }, [isAnyGenerating, props.threadId]);

  // Sync Input Mode จาก graphState (raw_scripts_images)
  useEffect(() => {
    if (!isEditingInput && graphState?.values?.raw_scripts_images) {
      const rawData = graphState.values.raw_scripts_images;
      setDirectTitle(rawData.title_podcast || rawData.title || "");
      setDirectItems(
        (rawData.items || []).map((item) => hydrateDirectInputVoicePrompt({
          id: item?.id || "",
          script: item?.script || "",
          voice_guide: item?.voice_guide,
          voice_script: item?.voice_script,
          image_prompt: item?.visual || "",
        })),
      );
      setSelectedSeriesId(graphState.series_id || "");
    }
  }, [graphState?.values?.raw_scripts_images, graphState?.series_id, isEditingInput]);

  // Sync scriptWithImage จาก graphState
  useEffect(() => {
    if (!graphState?.values) return;
    const values = graphState.values;
    if ((values.final_script?.items?.length || 0) > 0) {
      const incomingScript = values.final_script!;

      setScriptWithImage((prev) => mergeScriptFromPoll(prev, incomingScript, isUserEditingRef.current));
    }
  }, [graphState?.values]);

  // Auto-collapse Input Section ครั้งแรกที่ hasReviewData เป็น true
  useEffect(() => {
    if (hasReviewData && !hasAutoCollapsedRef.current) {
      hasAutoCollapsedRef.current = true;
      setIsInputCollapsed(true);
    }
  }, [hasReviewData]);

  // Sync "Generate Audio Only" จาก graphState
  // ยกเว้นตอนที่ผู้เรียกกำหนดค่าเริ่มต้นมาเอง (defaultAudioOnly) — run เสียงล้วนไม่ควรลากให้ติ๊กกลับมา
  useEffect(() => {
    if (props.defaultAudioOnly !== undefined) {
      return;
    }

    if (!hasReviewData) {
      hasDefaultedAudioOnlyRef.current = false;
      return;
    }
    const ao =
      graphState?.values?.audio_only ??
      graphState?.values?.final_script?.audio_only;
    if (typeof ao === "boolean") {
      hasDefaultedAudioOnlyRef.current = true;
      setGenerateAsAudioOnly(ao);
    } else if (!hasDefaultedAudioOnlyRef.current) {
      hasDefaultedAudioOnlyRef.current = true;
      setGenerateAsAudioOnly(true);
    }
  }, [
    props.defaultAudioOnly,
    hasReviewData,
    graphState?.values?.audio_only,
    graphState?.values?.final_script?.audio_only,
  ]);

  // Sync run-level Audio Padding จาก graphState (backend เก็บค่าที่ใช้ run ล่าสุด)
  useEffect(() => {
    const ps = graphState?.values?.padding_start;
    const pe = graphState?.values?.padding_end;
    if (typeof ps === "number") setGlobalPaddingStart(String(ps));
    if (typeof pe === "number") setGlobalPaddingEnd(String(pe));
    if ((typeof ps === "number" && ps > 0) || (typeof pe === "number" && pe > 0)) {
      setUseGlobalPadding(true);
    }
  }, [graphState?.values?.padding_start, graphState?.values?.padding_end]);

  // Reset loading เมื่อ isRunning = false
  useEffect(() => {
    if (!isRunning) {
      setLoadingDraft(false);
      setLoadingVideo(false);
    }
  }, [isRunning]);

  // Draft done → setScriptWithImage via graphState sync (ด้านบน)
  useEffect(() => {
    if (graphState?.progress?.["s2v_save_result_to_json"]?.status === "completed") {
      setLoadingDraft(false);
    }
  }, [graphState?.progress]);

  // Reset isScriptToScenePending
  useEffect(() => {
    if (isScriptToScenePending && graphState?.values?.raw_scripts_images && !isRunning) {
      setIsScriptToScenePending(false);
    }
  }, [graphState?.values?.raw_scripts_images, isRunning]);

  // Clamp selectedSceneIndex when scene count changes
  useEffect(() => {
    if (scriptWithImage?.items) {
      setSelectedSceneIndex((prev) => {
        if (prev === "final") return prev;
        if (typeof prev === "number" && prev >= scriptWithImage.items.length) {
          return Math.max(0, scriptWithImage.items.length - 1);
        }
        return prev;
      });
    }
  }, [scriptWithImage?.items?.length]);

  // Build payload ที่ clean สำหรับส่งให้ onUpdateScript
  // ผูก state ของหน้าจอ (audio only / use animated / global padding) เข้ากับฟังก์ชัน pure
  const buildCleanedScript = (script: FinalScriptData, audioOnlyOverride?: boolean): FinalScriptData =>
    buildCleanedScriptPure(script, {
      audioOnly: audioOnlyOverride ?? generateAsAudioOnly,
      useAnimated: localUseAnimated,
      useGlobalPadding,
    });

  // ── Image Assets: lazy fetch ครั้งเดียวเมื่อมี scene ใดเลือก Reference-to-Image model ──
  const needsImageAssets = !!scriptWithImage?.items?.some((item) =>
    sceneAllowsReferences(item, imageModelCatalog),
  );

  useEffect(() => {
    if (!needsImageAssets || !props.threadId) return;
    if (hasFetchedAssetsRef.current) return;

    hasFetchedAssetsRef.current = true;
    setIsLoadingAssets(true);
    props.onGetImageAssets?.(
      props.threadId,
      (assets) => {
        setIsLoadingAssets(false);
        setImageAssets(assets);
      },
      () => {
        setIsLoadingAssets(false);
        hasFetchedAssetsRef.current = false;
      },
    );
  }, [needsImageAssets, props.threadId]);

  // reset cache เมื่อเปลี่ยน thread
  useEffect(() => {
    hasFetchedAssetsRef.current = false;
    setImageAssets([]);
  }, [props.threadId]);

  // ── จัด reference_images ให้ตรงกับ model ของ scene: ล้างเมื่อโมเดลไม่รองรับ / ตัดเมื่อเกิน max_input_images ──
  useEffect(() => {
    // ยังไม่มี catalog = ตอบไม่ได้ว่าโมเดลไหนรับรูป/รับกี่รูป → ห้ามแตะ (กัน wipe ทุก scene ตอน render แรก)
    if (!imageModelCatalog || !scriptWithImage?.items?.length) return;

    const fixes = scriptWithImage.items
      .map((item, i) => ({ i, next: clampReferences(item, imageModelCatalog) }))
      .filter((fix): fix is { i: number; next: string[] } => fix.next !== null);

    if (fixes.length === 0) return;
    fixes.forEach(({ i, next }) => updateScriptItem(i, "reference_images", next));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scriptWithImage?.items, imageModelCatalog]);

  // Auto-save script changes (debounced)
  // effect นี้ไม่รู้ว่า "user แก้จริง" หรือเปล่า — ยิงเองตอนเปิด run ที่จบแล้วด้วย (isRunning true→false หลัง poll แรก
  // + scriptWithImage ได้ object ใหม่จาก mergeScriptFromPoll) ⇒ viewer ต้องไม่ auto-save: POST /script2vdo/{id}/update
  // เป็น editor-only (403 "Editor role required" ทุกครั้งที่เปิด)
  useEffect(() => {
    if (!canEdit) {
      setIsUserEditing(false); // ไม่มีอะไรจะบันทึก — อย่าปล่อย flag ค้าง true (ตัวเดียวที่ reset คือ timer ด้านล่าง)
      return;
    }
    if (!hasReviewData || !scriptWithImage?.items || !props.threadId || loadingVideo || isRunning) return;
    const timeout = setTimeout(() => {
      const cleanedScript = buildCleanedScript(scriptWithImage);
      props.onUpdateScript?.(props.threadId as string, cleanedScript);
      setIsUserEditing(false);
    }, 1500);
    return () => clearTimeout(timeout);
  }, [canEdit, scriptWithImage, hasReviewData, loadingVideo, isRunning]);

  // Toggle "Generate Audio Only"
  const handleToggleAudioOnly = (checked: boolean) => {
    setGenerateAsAudioOnly(checked);
    if (!props.threadId || !scriptWithImage) return;
    props.onUpdateScript?.(props.threadId, buildCleanedScript(scriptWithImage, checked));
  };

  // ref กัน response เก่าของ run ก่อนหน้าเขียนทับ versionMap หลังสลับ thread (pattern เดียวกับ onEventRef ด้านบน)
  const threadIdRef = useRef(props.threadId);
  useEffect(() => {
    threadIdRef.current = props.threadId;
  });

  // สลับ run = ทิ้งงานดาวน์โหลดของ run ก่อนหน้า (ไฟล์ที่ได้จะเป็นของ run ผิด)
  // abort ต้องอยู่ใน layout effect: React 16 flush passive effect (useEffect) เป็นทาสก์แยกหลัง commit
  // ⇒ มีช่องให้ fetch ของ run เก่า settle หลัง commit ที่เปลี่ยน props.threadId แต่ก่อน cleanup รัน
  // ตอนนั้น signal ยังไม่ถูก abort และ ref ยังชี้ job เดิม ⇒ isStale() ผ่าน แล้วเซฟไฟล์ของ run ผิด
  // cleanup ทำแค่ abort + ล้าง ref เท่านั้น ห้าม setState เพราะ cleanup รันตอน unmount ด้วย
  useLayoutEffect(
    () => () => {
      episodeDownloadJobRef.current?.controller.abort();
      episodeDownloadJobRef.current = null;
    },
    [props.threadId]
  );

  // ส่วนที่เป็น setState แยกไว้ใน passive effect ตามเดิม — แค่รีเซ็ต spinner/progress ไม่ต้องรีบก่อน paint
  useEffect(() => {
    setDownloadJob(null);
    setDownloadProgress(null);
  }, [props.threadId]);

  // scene ที่เลือก + output ของ scene นั้น — หาแบบ id ไม่ใช่ index: index ของ output รอบก่อนไม่ตรงกับ script
  // หลังเพิ่ม/ลบ/ย้าย scene (เดิมเช็ค outputData.items[selectedSceneIndex] แต่ยิงด้วย activeItem.id
  // → ขอ version ของ segment ที่หลังบ้านยังไม่มี → 404 → snack เด้งมั่ว)
  const activeItemId =
    typeof selectedSceneIndex === "number" ? scriptWithImage?.items?.[selectedSceneIndex]?.id : undefined;
  const activeOutput = activeItemId ? outputData?.items?.find((o) => o.id === activeItemId) : undefined;
  const activeAudioUrl = activeOutput?.audio_url;
  const activeImageUrl = activeOutput?.image_url;

  // Auto-fetch versions ของ scene ที่เลือก — เฉพาะ asset ที่ "มีของแล้ว" ใน output ล่าสุด
  // deps เป็น url ของ asset เอง ⇒ ยิงเมื่อของมาถึงจริง
  // (เดิมผูกกับ scriptWithImage.items ซึ่งมาก่อน outputData ตอนเปิด run)
  // viewer ไม่ยิง: GET /version/... เป็น editor-only — โดน 403 แน่ ๆ ทุกครั้งที่สลับ scene
  useEffect(() => {
    if (!canEdit || isRunning || isGeneratingScene || !activeItemId || !props.threadId || !props.onGetSegmentVersions) return;
    const threadId = props.threadId;
    const itemId = activeItemId;

    versionAssetTypesFor(activeOutput).forEach((assetType) => {
      if (versionMap[itemId]?.[assetType]) return;
      props.onGetSegmentVersions!(
        threadId,
        itemId,
        assetType,
        (data) => {
          if (threadIdRef.current !== threadId) return; // สลับ run ไปแล้ว — ทิ้ง
          if (data.versions.length === 0) return; // ยังไม่มี version — ไม่ cache จะได้ขอใหม่หลัง scene นี้ถูก generate
          setVersionMap((prev) => ({ ...prev, [itemId]: { ...prev[itemId], [assetType]: data } }));
          setPendingVersionMap((prev) => ({ ...prev, [itemId]: { ...prev[itemId], [assetType]: data.current } }));
        },
        { silent: true }, // งานเบื้องหลัง — พังแล้วเงียบ (เช่น viewer โดน 403 ทุกครั้งที่สลับ scene)
      );
    });
  }, [canEdit, isRunning, isGeneratingScene, activeItemId, activeAudioUrl, activeImageUrl]);

  // Sync Local Use Animated Form OutputData
  useEffect(() => {
    if (outputData?.items?.length) {
      setLocalUseAnimated((prev) => {
        const next = { ...prev };
        let changed = false;
        outputData.items.forEach((item) => {
          if (next[item.id] === undefined && (item as any).use_animated !== undefined) {
            next[item.id] = !!(item as any).use_animated;
            changed = true;
          }
        });
        return changed ? next : prev;
      });
    }
  }, [outputData]);

  // Close Popper on Scroll
  useEffect(() => {
    if (!showGenActionModal) return;

    const handleScroll = () => setShowGenActionModal(false);

    window.addEventListener("scroll", handleScroll, true);
    return () => window.removeEventListener("scroll", handleScroll, true);
  }, [showGenActionModal]);

  // Update characterList
  useEffect(() => {
    if (!selectedSeriesId) return;

    props.onGetCharacterList?.(selectedSeriesId);
  }, [selectedSeriesId]);

  // ─── Helpers ─────────────────────────────────────────────────────────────────

  // ชื่อ run สำหรับ breadcrumb/หัวข้อ — ไล่ตามลำดับเดียวกับ title ใน run list ของ backend (/runs)
  // ก่อนจะ fallback เป็น "Script XXXXXXXX"
  const runName = useMemo(
    () =>
      resolveRunName({
        titleOverride,
        finalScriptTitle: graphState?.values?.final_script?.title_podcast,
        rawTitlePodcast: graphState?.values?.raw_scripts_images?.title_podcast,
        rawTitle: graphState?.values?.raw_scripts_images?.title,
        youtubeTitle: graphState?.values?.youtube_title,
        directTitle,
        runTitle: props.runTitle,
        threadId: props.threadId,
      }),
    [titleOverride, graphState?.values, directTitle, props.runTitle, props.threadId]
  );

  const breadcrumbItems: BreadcrumbItem[] = [
    { label: props.projectName, path: `/project/${props.projectId}` },
    ...(props.seriesName ? [{ label: props.seriesName, path: `/series/${initialSeriesId}` }] : []),
    { label: runName },
  ];

  const getStepStatus = (stepId: string): "done" | "active" | "pending" | "error" => {
    const stepData = graphState?.progress?.[stepId];

    if (!stepData) return "pending";

    if (stepData.status === "completed" || stepData.skipped) return "done";
    if (stepData.status === "running") return "active";
    if (stepData.status === "failed") return "error";

    return "pending";
  };

  const isReadyForUpload =
    graphState?.next?.includes("upload_to_youtube") ||
    graphState?.progress?.["create_thumbnail"]?.status === "completed";

  // Master label map รวมทุก step ที่รู้จัก *** กรณีมี Step อะไรเพิ่มให้เพิ่มจากตรงนี้ + กรณีมี seq ให้ใช้ seq แทน
  const STEP_LABEL_MAP: Record<string, string> = {
    // STEPS_DIRECT
    s2v_ai_image_generator: "Generate Prompts",
    s2v_merge_script_image: "Merge Data",
    s2v_save_result_to_json: "Finalize Draft",
    // STEPS_VDO
    load_data: "Load Data",
    process_items: "Process Items (AI)",
    tts_generation: "TTS Generation",
    image_generation: "Image Generation",
    generate_assets: "Generate Assets",
    merge_media: "Merge Media (FFmpeg)",
    prepare_youtube_metadata: "Prepare Metadata",
    create_thumbnail: "Create Thumbnail",
    upload_to_youtube: "Upload to YouTube",
  };

  const SCRIPT2SCENE_STEP_IDS_SET = new Set(STEPS_SCRIPT2SCENE.map((s) => s.id));

  const getActiveSteps = (): { id: string; label: string }[] => {
    const progressKeys = graphState?.progress ? Object.keys(graphState.progress) : [];
    const filteredKeys = progressKeys.filter((k) => !SCRIPT2SCENE_STEP_IDS_SET.has(k as any));

    if (filteredKeys.length > 0) {
      // Build steps จาก progress keys พร้อม label จาก map
      // return filteredKeys.map((id) => ({
      //   id,
      //   label: STEP_LABEL_MAP[id] ?? id.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
      // }));
      return filteredKeys
        .sort((a: string, b: string) => {
          const idxA = graphState?.progress?.[a]?.step_index ?? 999;
          const idxB = graphState?.progress?.[b]?.step_index ?? 999;

          return idxA - idxB;
        })
        .map((id) => ({
          id,
          label: STEP_LABEL_MAP[id] ?? id.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
        }));
    }

    if (graphState?.values?.items) return [...STEPS_VDO];
    return [...STEPS_DIRECT];
  };

  const mediaVersion = moment(graphState?.created_at).format("DDMMYYYY_HHmmss") || "0";

  const buildUrl = useCallback(
    (path: string) => getDirectStaticUrl(props.threadId, path),
    [props.threadId]
  );

  const buildOutputUrl = useCallback(
    (path: string | null | undefined) => getOutputStaticUrl(path),
    []
  );

  const buildVideoUrl = useCallback(
    (path: string | null | undefined) => {
      const base = getOutputStaticUrl(path);
      return base ? `${base}${base.includes("?") ? "&" : "?"}v=${mediaVersion}` : "";
    },
    [mediaVersion]
  );

  // ดึงชื่อไฟล์ (ไม่รวมนามสกุล) มาใช้เป็น remount key
  const getAssetVersionKey = (path?: string | null): string => {
    if (!path) return "";
    const file = path.split("/").pop() || "";
    return file.replace(/\.[^/.]+$/, "");
  };

  // ดึง "เลข version" จากชื่อไฟล์
  const getAssetVersionNumber = (path?: string | null): number | null => {
    if (!path) return null;
    const m = path.match(/_v(\d+)(?=\.[^/.]+$)/);
    return m ? parseInt(m[1], 10) : null;
  };

  // Badge "ver. N" — อิงจากชื่อไฟล์ของ asset ที่แสดงจริง
  const renderVersionBadge = (rawPath?: string | null, fallbackCurrent?: number | null) => {
    const ver = getAssetVersionNumber(rawPath) ?? fallbackCurrent;
    if (ver == null) return null;
    return (
      <Box sx={{ display: "inline-flex", alignItems: "center", fontSize: "0.65rem", fontWeight: 600, color: "#db2777", backgroundColor: "#fce7f3", border: "1px solid #f9a8d4", borderRadius: "9999px", px: 0.75, lineHeight: 1, height: "20px" }}>
        ver. {ver}
      </Box>
    );
  };

  const finalVideoUrl = useMemo(() => {
    if (outputData?.final_video_url) return buildVideoUrl(outputData.final_video_url);
    const rawFinalVideoUrl = graphState?.values?.final_video_path
      ? getStaticUrl(graphState.values.final_video_path)
      : "";
    return rawFinalVideoUrl ? buildUrl(rawFinalVideoUrl) : "";
  }, [outputData?.final_video_url, graphState?.values?.final_video_path]);

  // ไฟล์รายฉากของทั้งตอน (เสียง/วิดีโอ segment) สำหรับปุ่ม zip บน toolbar
  // ลำดับต้องเป็นลำดับฉากบนจอ ไม่ใช่ลำดับใน /output.items (ซึ่งเรียงตามตอนที่ generate เสร็จ)
  // จับคู่ฉากกับ output ด้วย id ตรง ๆ แบบ 1:1 และเช็คว่า URL อยู่ใต้ thread ปัจจุบันจริง
  // (outputData ไม่เคยถูกเคลียร์ตอนสลับ run ⇒ ไฟล์ของ run ก่อนหน้าค้างอยู่ได้)
  const episodeAudio = useMemo(
    () =>
      episodeSceneSources(
        scriptWithImage?.items ?? [],
        outputData?.items ?? [],
        props.threadId,
        buildOutputUrl,
        "audio_url"
      ),
    [scriptWithImage?.items, outputData?.items, props.threadId, buildOutputUrl]
  );

  const episodeVideo = useMemo(
    () =>
      episodeSceneSources(
        scriptWithImage?.items ?? [],
        outputData?.items ?? [],
        props.threadId,
        buildOutputUrl,
        "segment_video_url"
      ),
    [scriptWithImage?.items, outputData?.items, props.threadId, buildOutputUrl]
  );

  // จำนวน scene ที่เปิดใช้และมีข้อความให้อ่านออกเสียงจริง (หลังตัดบล็อก DIRECTOR_NOTES ออก)
  // ใช้ตัดสินว่าปุ่ม Download Script กดได้ไหม — ต้องเป็นเกณฑ์เดียวกับที่ใช้ประกอบไฟล์ ไม่งั้นกดแล้วได้ไฟล์ว่าง
  const spokenSceneCount = useMemo(
    () => spokenScripts(scriptWithImage?.items).length,
    [scriptWithImage?.items]
  );

  // เงื่อนไขเปิด/ปิดปุ่ม + ข้อความ tooltip รวมไว้ที่ helper ตัวเดียว (pure ⇒ มีเทสต์ครอบ)
  const downloadActionsState = useMemo(
    () =>
      downloadActions({
        finalUrl: finalVideoUrl,
        threadId: props.threadId,
        renderMode: outputData?.render_mode ?? graphState?.render_mode,
        // isAnyGenerating รวม generate รายฉากด้วย: กด Generate Scene แล้ว isRunning ยังไม่พลิกจนกว่า poll ถัดไป
        // ถ้าปิดแค่ isRunning จะดาวน์โหลดเสียงเทคเก่าของฉากนั้นได้ระหว่างรอ
        isBusy: loadingVideo || isAnyGenerating,
        activeJob: downloadJob,
        audio: { missing: episodeAudio.missing.length, total: episodeAudio.total },
        video: { missing: episodeVideo.missing.length, total: episodeVideo.total },
        script: { count: spokenSceneCount },
      }),
    [
      finalVideoUrl,
      props.threadId,
      outputData?.render_mode,
      graphState?.render_mode,
      loadingVideo,
      isAnyGenerating,
      downloadJob,
      episodeAudio,
      episodeVideo,
      spokenSceneCount,
    ]
  );

  // ชนิด/สถานะ review ของไฟล์ final ที่ผ่านการเช็คแล้วว่าอยู่ใต้ thread ปัจจุบัน — ห้ามอ่านจาก finalVideoUrl ตรง ๆ
  // (graphState กับ outputData มาคนละ poll ได้ ⇒ URL ของ run ก่อนอาจค้างอยู่ แล้วโหลด mp3 ของ run อื่นในชื่อ run นี้)
  const finalMediaKind = downloadActionsState.finalKind;
  const finalIsReview = downloadActionsState.review;

  const isSelectingVersion = (itemId: string, assetType: AssetType) =>
    selectingVersionKey?.itemId === itemId && selectingVersionKey?.assetType === assetType;

  const sceneIndexMap = useMemo(() => {
    const map: Record<string, number> = {};

    (scriptWithImage?.items || []).forEach((item, index) => {
      map[item.id] = index + 1
    });

    return map;
  }, [scriptWithImage?.items]);

  // ─── Handlers ────────────────────────────────────────────────────────────────

  const handleRefine = () => {
    if (!refinePrompt.trim() || !props.threadId) return;

    setIsRefining(true);

    const payload: RefineScriptToScenePayload = {
      current_data: {
        title_podcast: directTitle,
        items: directItems.map(item => {
          const voice = synchronizeVoicePrompt(item);
          return {
            id: item.id,
            script: voice.script,
            voice_guide: voice.voice_guide,
            voice_script: voice.voice_script,
            visual: item.image_prompt,
          };
        }),
      },
      prompt: refinePrompt,
    };

    props.onRefineScriptToScene?.(
      props.threadId,
      payload,
      (result) => {
        if (result.values?.raw_scripts_images) {
          const raw = result.values.raw_scripts_images;

          setDirectTitle(raw.title_podcast || "");
          setDirectItems(raw.items.map(item => hydrateDirectInputVoicePrompt({
            id: item.id,
            script: item.script,
            voice_guide: item.voice_guide,
            voice_script: item.voice_script,
            image_prompt: item.visual,
          })));
        }

        setRefinePrompt("");
        setIsRefining(false);
      },
      () => { setIsRefining(false); },
    );
  };

  const handleEditModeReorder = (oldIndex: number, newIndex: number) => {
    if (!scriptWithImage) return;

    setScriptWithImage((prev) => {
      if (!prev) return prev;

      return {
        ...prev,
        items: arrayMove([...prev.items], oldIndex, newIndex),
      };
    });
  };

  const handleEditModeDelete = (itemId: string) => {
    if (!scriptWithImage || scriptWithImage.items.length <= 1) return;

    setScriptWithImage((prev) => {
      if (!prev) return prev;

      return {
        ...prev,
        items: prev.items.filter((item) => item.id !== itemId),
      };
    });

    setDeleteModalItemId(null);

    // Adjust selected scene index after deletion
    setSelectedSceneIndex((prev) => {
      if (prev === "final") return prev;
      if (typeof prev === "number") {
        const deletedIndex = scriptWithImage.items.findIndex((i) => i.id === itemId);
        const newLength = scriptWithImage.items.length - 1;
        if (prev >= newLength) return Math.max(0, newLength - 1);
        if (deletedIndex >= 0 && prev > deletedIndex) return prev - 1;
      }
      return prev;
    });
  };

  const handleEditModeAdd = () => {
    if (!scriptWithImage) return;

    const defaultStyle = props.mediaStyleList?.[0];
    const defaultVoice = props.voiceList?.[0];

    setScriptWithImage((prev) => {
      if (!prev) return prev;

      const newId = `${Date.now()}`

      return {
        ...prev,
        items: [
          ...prev.items,
          {
            id: newId,
            headline: "",
            script: "", // todo --- default script เสียง ตอน New Scene
            voice_guide: DEFAULT_VOICE_GUIDE,
            voice_script: "",
            voice_name: defaultVoice?.name || DEFAULT_VOICE_NAME,
            image_prompt: "",
            scene_description: "",
            image_style: defaultStyle?.value || DEFAULT_IMAGE_STYLE,
            image_style_prompt: defaultStyle?.prompt || "",
            ken_burns_direction: "off",
          },
        ],
      };
    });
  };

  const handleGoTo = (itemId: string, currentIndex: number) => {
    if (!scriptWithImage) return;

    const val = goToInputMap[itemId];

    if (!val) return;

    const targetPos = parseInt(val, 10);
    const total = scriptWithImage.items.length;

    if (isNaN(targetPos) || targetPos < 1 || targetPos > total || targetPos - 1 === currentIndex) return;

    handleEditModeReorder(currentIndex, targetPos - 1);

    setGoToInputMap((prev) => ({ ...prev, [itemId]: "" }));
  };

  const handleSubmitScriptToScene = async () => {
    if (!scriptText.trim()) return;
    setIsScriptToSceneLoading(true);
    setIsScriptToSceneGenerating(false);
    setIsScriptToScenePending(true);
    setScriptToSceneProgress(null);

    const startPolling = (newThreadId: string) => {
      setScriptToSceneThreadId(newThreadId);
      setIsScriptToSceneGenerating(true);
    };

    if (!scriptToSceneThreadId) {
      props.onCreateScriptToSceneRun?.(
        props.projectId,
        selectedSeriesId === "" ? null : selectedSeriesId,
        (newThreadId) => {
          props.onStartScriptToScene?.(newThreadId, scriptText, () => startPolling(newThreadId));
        },
      );
    } else {
      props.onStartScriptToScene?.(scriptToSceneThreadId, scriptText, () =>
        startPolling(scriptToSceneThreadId),
      );
    }
  };

  const handleAddScene = () => {
    setDirectItems((prev) => [
      ...prev,
      { id: `${prev.length + 1}`, script: "", voice_guide: DEFAULT_VOICE_GUIDE, voice_script: "", image_prompt: "" },
    ]);
  };

  const handleRemoveScene = (index: number) => {
    if (directItems.length <= 1) return;
    setDirectItems((prev) =>
      prev.filter((_, i) => i !== index).map((item, i) => ({ ...item, id: `${i + 1}` })),
    );
  };

  const handleMoveScene = (index: number, direction: "up" | "down") => {
    setDirectItems((prev) => {
      const newItems = [...prev];
      const targetIndex = direction === "up" ? index - 1 : index + 1;
      if (targetIndex < 0 || targetIndex >= newItems.length) return prev;
      [newItems[index], newItems[targetIndex]] = [newItems[targetIndex], newItems[index]];
      return newItems.map((item, i) => ({ ...item, id: `${i + 1}` }));
    });
  };

  const handleSceneChange = (index: number, field: keyof DirectSceneItem, value: string) => {
    setDirectItems((prev) => {
      const newItems = [...prev];
      newItems[index] = { ...newItems[index], [field]: value ?? "" };
      return newItems;
    });
  };

  const handleDraft = () => {
    if (!directTitle?.trim()) return;

    const seriesIdToSend = selectedSeriesId === "" ? null : selectedSeriesId;

    const payload: DirectScriptInput = {
      title: directTitle,
      image_style: selectedImageStyle || undefined,
      image_style_prompt: props.mediaStyleList?.find((s) => s.value === selectedImageStyle)?.prompt || "",
      aspect_ratio: aspectRatio || undefined,
      voice_name: selectedVoiceName || undefined,
      audio_only: true,// default as "true"
      items: directItems.map(buildDirectDraftItem),
    };

    setLoadingDraft(true);
    setIsEditingInput(false);
    setScriptWithImage(null);
    setForceRegenMap({});

    // Reset auto-collapse ref so Input will auto-collapse again after new draft
    hasAutoCollapsedRef.current = false;

    setIsProgressExpanded(false);

    props.onDirectGenerate?.(props.threadId, props.projectId, seriesIdToSend, payload, true);
  };

  const handleSaveTitle = () => {
    if (!titleDraft.trim() || !props.threadId) return;

    props.onUpdateTitle?.(
      props.threadId,
      { title: titleDraft },
      (title: string) => {
        setDirectTitle(title);
        setTitleOverride(title);
        setIsEditingTitle(false);
      },
      () => {},
    );
  };

  const handleStopGenerate = () => {
    if (!props.threadId) return;

    setIsStopping(true);

    props.onStopRun?.(
      props.threadId,
      () => setIsStopping(false)
    );
  };

  // ── Build Payload (For Generate Video) ──
  const buildPayload = (
    isFinal: boolean = false,
    audioOnly: boolean = false,
  ): StartGenerateDirectScriptPayload | null => {
    if (!scriptWithImage) return null;

    const validItems = scriptWithImage.items.filter((item) => hydrateVoicePrompt(item).voice_script.trim());
    if (validItems.length === 0) return null;

    const manualScript: FinalScriptData = {
      generated_at: scriptWithImage.generated_at,
      type: scriptWithImage.type || "script2vdo",
      title_podcast: scriptWithImage.title_podcast,
      thumbnail_prompt: scriptWithImage.thumbnail_prompt,
      items: validItems.map((item) => {
        const voice = synchronizeVoicePrompt(item);
        return {
        id: item.id,
        headline: item.headline || "",
        script: voice.script,
        voice_guide: voice.voice_guide,
        voice_script: voice.voice_script,
        voice_name: item.voice_name || DEFAULT_VOICE_NAME,
        scene_description: item.scene_description,
        image_style: item.image_style,
        image_style_prompt: item.image_style_prompt || "",
        image_prompt: item.image_style_prompt
          ? item.scene_description + "\n" + item.image_style_prompt
          : item.scene_description || "",
        use_animated: localUseAnimated[item.id] ?? false,
        ...(item.anim_prompt?.trim() && {
          is_animated: true,
          anim_prompt: item.anim_prompt,
          anim_mode: item.anim_mode || "single",
          ...(item.character_id != null && { character_id: item.character_id }),
        }),
        ...(item.is_uploaded && {
          is_uploaded: true,
          uploaded_video_url: item.uploaded_video_url || "",
        }),
        ...(item.is_image_uploaded && {
          is_image_uploaded: true,
          uploaded_image_url: item.uploaded_image_url || "",
        }),
        ...(item.text_overlays &&
          item.text_overlays.length > 0 && { text_overlays: item.text_overlays }),
        ...(item.image_overlays &&
          item.image_overlays.length > 0 && { image_overlays: item.image_overlays }),
        ken_burns_direction: item.ken_burns_direction || "off",
        ...(!useGlobalPadding && item.padding_start != null && { padding_start: item.padding_start }),
        ...(!useGlobalPadding && item.padding_end != null && { padding_end: item.padding_end }),
        // ไม่ได้เลือก = ไม่ส่ง key ให้หลังบ้านใช้ default ของตัวเอง
        // (หลังบ้าน hash "" เท่ากับไม่ส่ง key ⇒ signature เท่าเดิม ไม่มีสื่อเก่า regen)
        ...(item.image_model && { image_model: item.image_model }),
        ...(item.video_model && { video_model: item.video_model }),
        ...(item.reference_images &&
          item.reference_images.length > 0 && { reference_images: item.reference_images }),
        };
      }),
    };

    const forceRegen: Record<string, string[]> = {};
    Object.entries(forceRegenMap).forEach(([itemId, regenTypes]) => {
      if (regenTypes && regenTypes.length > 0) forceRegen[itemId] = regenTypes;
    });

    return {
      aspect_ratio: aspectRatio || undefined,
      manual_script: manualScript,
      padding_start: useGlobalPadding ? parsePadding(globalPaddingStart) ?? 0 : 0,
      padding_end: useGlobalPadding ? parsePadding(globalPaddingEnd) ?? 0 : 0,
      ...(audioOnly ? { audio_only: true } : { audio_only: false }),
      ...(!isFinal
        ? { force_regen: Object.keys(forceRegen).length > 0 ? forceRegen : undefined }
        : {}),
    };
  };

  // ── UNIFIED Generate Video Handler ──
  const handleGenerateVideo = (payload: StartGenerateDirectScriptPayload) => {
    if (!props.threadId) return;

    setIsProgressExpanded(false);
    setLoadingVideo(true);
    setIsEditingInput(false);
    // viewer: ข้าม pre-save (editor-only) — ให้ /start ตอบ 403 ครั้งเดียว ไม่เด้ง snack ซ้อนสอง
    if (canEdit) props.onUpdateScript?.(props.threadId, payload.manual_script);
    setForceRegenMap({});

    if (generateAsFinal) {
      props.onGenerateVideoFinal?.(props.threadId, payload);
    } else {
      props.onGenerateVideo?.(props.threadId, payload);
    }
  };

  // ── Generate Cost Check ──
  const getVideoModeIssues = (onlyItemId?: string): number[] => {
    if (!scriptWithImage) return [];

    return scriptWithImage.items
      .map((item, i) => ({ item, sceneNo: i + 1 }))
      .filter(({ item }) => {
        if (onlyItemId && item.id !== onlyItemId) return false;

        // ตัวกฎอยู่ที่ common/videoModeIssues.ts (pure + มีเทสต์) — ที่นี่แค่รวบรวมข้อมูลจาก state
        const outputItem = outputData?.items.find((o: any) => o.id === item.id);
        return sceneBlocksVideoGenerate(item, {
          useAnimated: localUseAnimated[item.id] ?? false,
          hasVideo: !!(
            item.uploaded_video_url ||
            outputItem?.ai_video_url ||
            outputItem?.uploaded_video_url
          ),
          catalog: videoModelCatalog,
        });
      })
      .map(({ sceneNo }) => sceneNo);
  };

  const blockIfVideoModeIssues = (onlyItemId?: string): boolean => {
    const issues = getVideoModeIssues(onlyItemId);
    if (issues.length === 0) return false;

    setVideoModeIssueScenes(issues);
    return true;
  };

  const handleRemoveUploadedVideo = (index: number) => {
    if (!window.confirm("ต้องการลบวิดีโอที่อัปโหลดหรือไม่?")) return;
    updateScriptItem(index, "uploaded_video_url", "");
    updateScriptItem(index, "is_uploaded", false);
  };

  const handleRemoveUploadedImage = (index: number) => {
    if (!window.confirm("ต้องการลบรูปภาพที่อัปโหลดหรือไม่?")) return;
    updateScriptItem(index, "uploaded_image_url", "");
    updateScriptItem(index, "is_image_uploaded", false);
  };

  const handleClickGenerateVideo = () => {
    if (!scriptWithImage || !props.threadId) return;

    if (blockIfVideoModeIssues()) return;

    const payload = buildPayload(generateAsFinal, generateAsAudioOnly);

    if (!payload) {
      alert("ไม่มี Scripts ที่ถูกต้อง");
      return;
    }

    setPendingGeneratePayload(payload);
    setLoadingCostCheck(true);

    props.onCheckCost?.(
      props.threadId,
      payload,
      (result) => {
        setLoadingCostCheck(false);
        setCostDetails(result.cost_details);
        setRegenerateItems(result.regenerate_items);
        setShowCostModal(true);
      },
      (err) => {
        setLoadingCostCheck(false);
        // 4xx = หลังบ้านปฏิเสธคำขอนี้แล้ว (ข้อความขึ้น snack จาก handler) — ยิง start ซ้ำด้วย payload เดิมก็โดนปฏิเสธเหมือนกัน
        // หรือแย่กว่านั้นคือข้ามด่านเช็คเครดิตไปเริ่ม run ที่เสียเงินจริง → หยุด และเก็บ forceRegenMap ไว้ให้แก้แล้วกดใหม่
        if (isClientError(err)) {
          setPendingGeneratePayload(null);
          return;
        }
        // *** fallback: generate ตรงๆ ถ้า check ไม่ได้เพราะ network/5xx (ถ้าหลังบ้านล้มจริง start จะฟ้องเอง)
        handleGenerateVideo(payload);
      }
    );
  };

  const handleClickGenerateScene = (itemId: string) => {
    if (!scriptWithImage || !props.threadId) return;

    // Block เฉพาะเมื่อ scene ที่กดมีปัญหา
    if (blockIfVideoModeIssues(itemId)) return;

    const payload = buildPayload(false, generateAsAudioOnly);
    if (!payload) return;

    const scenePayload: StartGenerateDirectScriptPayload = {
      ...payload,
      generate_item_id: itemId,
    };

    setPendingGenScenePayload(scenePayload);
    setShowGenActionModal(true);
  };

  const handleConfirmGenAction = (actions: string[]) => {
    if (!pendingGenScenePayload || !props.threadId) return;

    setShowGenActionModal(false);

    const payloadWithActions: StartGenerateDirectScriptPayload = {
      ...pendingGenScenePayload,
      actions,
    };

    setPendingGeneratePayload(payloadWithActions);
    setPendingGenerateSceneItemId(payloadWithActions.generate_item_id!);
    setLoadingCostCheck(true);

    props.onCheckCost?.(
      props.threadId,
      payloadWithActions,
      (result) => {
        setLoadingCostCheck(false);
        setCostDetails(result.cost_details);
        setRegenerateItems(result.regenerate_items);
        setShowCostModal(true);
      },
      (err) => {
        setLoadingCostCheck(false);
        if (isClientError(err)) {
          setPendingGeneratePayload(null);
          setPendingGenerateSceneItemId(null); // spinner ต่อ scene ผูกกับค่านี้
          return;
        }
        handleGenerateScene(payloadWithActions);
      },
    );
  };

  const handleGenerateScene = (payload: StartGenerateDirectScriptPayload) => {
    if (!props.threadId || !payload.generate_item_id) return;

    setGeneratingSceneItemId(payload.generate_item_id);
    setPendingGenerateSceneItemId(null);
    setIsGeneratingScene(true);

    props.onGenerateScene?.(
      props.threadId,
      payload,
      () => {
        // setGeneratingSceneItemId(null);
      },
    );
  };

  const handleConfirmGenerate = () => {
    const payload = pendingGeneratePayload;
    if (!payload) return;
    setShowCostModal(false);
    // ล้างทิ้งตั้งแต่ตรงนี้ (อ่านเข้า local ไปแล้ว) ไม่งั้น payload เก่าค้างข้ามรอบ
    setPendingGeneratePayload(null);

    if (payload.generate_item_id) {
      handleGenerateScene(payload);
    } else {
      handleGenerateVideo(payload);
    }
  };

  const updateScriptItem = (
    index: number,
    field: string,
    value: string | boolean | number | undefined | TextOverlay[] | ImageOverlay[] | string[],
  ) => {
    if (!scriptWithImage) return;
    setIsUserEditing(true);
    setScriptWithImage((prev) => {
      if (!prev) return prev;
      const newItems = [...prev.items];
      newItems[index] = { ...newItems[index], [field]: value };
      return { ...prev, items: newItems };
    });
  };

  // ── Toggle scene active/inactive (Grid View only) ──
  const handleToggleSceneActive = (itemId: string, nextActive: boolean) => {
    if (!scriptWithImage || !props.threadId) return;

    const updatedItems = scriptWithImage.items.map((item) =>
      item.id === itemId ? { ...item, active: nextActive } : item,
    );
    const updatedScript: FinalScriptData = { ...scriptWithImage, items: updatedItems };

    setScriptWithImage(updatedScript);

    props.onUpdateScript?.(props.threadId, buildCleanedScript(updatedScript));
  };

  // ── Image Assets handlers ──
  // toggle เลือก/ยกเลิกรูปอ้างอิงของ scene (คลิกซ้ำ = เอาออก)
  const handleToggleReferenceImage = (index: number, filename: string) => {
    if (!scriptWithImage) return;
    const item = scriptWithImage.items[index];
    const current = item?.reference_images || [];
    const cap = referenceCap(effectiveImageModel(item ?? {}, imageModelCatalog), imageModelCatalog);
    const next = current.includes(filename)
      ? current.filter((f) => f !== filename)
      : current.length >= cap
        ? current
        : [...current, filename];
    updateScriptItem(index, "reference_images", next);
  };

  const handleUploadImageAssetFile = (file: File) => {
    if (!props.threadId || !props.onUploadImageAsset) return;
    setIsUploadingAsset(true);
    props.onUploadImageAsset(
      props.threadId,
      file,
      (assets) => {
        setIsUploadingAsset(false);
        hasFetchedAssetsRef.current = true;
        setImageAssets(assets);
      },
      () => setIsUploadingAsset(false),
    );
  };

  // เอา filename ที่ถูกลบออกจาก reference_images ของทุก scene (กันส่ง filename ที่ไม่มี)
  const removeReferenceImageFromAllScenes = (filename: string) => {
    setScriptWithImage((prev) => {
      if (!prev) return prev;
      let changed = false;
      const items = prev.items.map((item) => {
        if (!item.reference_images?.includes(filename)) return item;
        changed = true;
        return {
          ...item,
          reference_images: item.reference_images.filter((f) => f !== filename),
        };
      });
      return changed ? { ...prev, items } : prev;
    });
  };

  const handleRequestDeleteAsset = (filename: string) => {
    const asset = imageAssets.find((a) => a.filename === filename);
    if (!asset) return;
    setPendingDeleteAsset(asset);
  };

  const handleConfirmDeleteAsset = () => {
    const asset = pendingDeleteAsset;
    if (!asset || !props.threadId || !props.onDeleteImageAsset) return;

    setPendingDeleteAsset(null);
    setDeletingAssetFilename(asset.filename);

    props.onDeleteImageAsset(
      props.threadId,
      asset.filename,
      (assets) => {
        setDeletingAssetFilename(null);
        hasFetchedAssetsRef.current = true;
        setImageAssets(assets);
        removeReferenceImageFromAllScenes(asset.filename);
      },
      () => setDeletingAssetFilename(null),
    );
  };

  const handleUploadOverlayFile = (
    overlayIndex: string,
    file: File,
    onSuccess: (url: string) => void,
    onError: () => void,
  ) => {
    if (!props.threadId || !props.onUploadImageOverlay) {
      onError();
      return;
    }
    props.onUploadImageOverlay(props.threadId, overlayIndex, file, onSuccess, onError);
  };

  const handleUploadVideoFile = (index: number, itemId: string, file: File) => {
    if (!props.threadId || !props.onUploadVideo) return;
    setUploadingVideoItemId(itemId);
    props.onUploadVideo(
      props.threadId,
      itemId,
      file,
      (url: string) => {
        updateScriptItem(index, "uploaded_video_url", url);
        setUploadingVideoItemId(null);
      },
      () => {
        setUploadingVideoItemId(null);
      },
    );
  };

  const handleUploadImageFile = (index: number, itemId: string, file: File) => {
    if (!props.threadId || !props.onUploadImage) return;
    setUploadingImageItemId(itemId);
    props.onUploadImage(
      props.threadId,
      itemId,
      file,
      (url: string) => {
        updateScriptItem(index, "uploaded_image_url", url);
        setUploadingImageItemId(null);
      },
      () => {
        setUploadingImageItemId(null);
      },
    );
  };

  const handleUploadYoutube = () => {
    if (!props.threadId) return;
    const doUpload = (credentialId: number | null) => {
      props.onUploadYoutube?.(props.threadId as string, credentialId, (newState: DirectScriptGraphState) => {
        setGraphState((prev) => {
          if (prev && JSON.stringify(prev) === JSON.stringify(newState)) return prev;
          return newState;
        });
      });
    };
    if (!youtubeChannels || youtubeChannels.length === 0) {
      if (!window.confirm("Are you sure you want to upload this video to YouTube?")) return;
      doUpload(null);
      return;
    }
    if (youtubeChannels.length === 1) {
      if (!window.confirm(`Upload to "${youtubeChannels[0].channel_name}"?`)) return;
      doUpload(Number(youtubeChannels[0].id));
      return;
    }
    setShowUploadModal(true);
  };


  // ความผิดพลาดของการดาวน์โหลดต้องถึงผู้ใช้เสมอ — ขึ้น SnackMessage ของ parent ถ้ามี handler
  // ไม่งั้นถอยไป window.alert (ของเดิมล็อกไว้ที่คอนโซลเฉย ๆ ผู้ใช้จึงไม่รู้ว่าดาวน์โหลดพลาด)
  const notifyDownloadError = (message: string) => {
    if (props.onDownloadError) props.onDownloadError(message);
    else window.alert(message);
  };

  // runner ตัวเดียวของทั้งสี่ปุ่ม ("final" = ไฟล์ทั้งตอนไฟล์เดียวในมุมมอง Final Output,
  // "audio-zip"/"video-zip" = โหลดไฟล์รายฉากของทุกฉากแบบ bulk แล้วรวมเป็น zip ไฟล์เดียว,
  // "script" = ข้อความที่อ่านออกเสียงของทุกฉากที่เปิดใช้เป็น .txt ประกอบในเครื่องล้วน ๆ ไม่ยิง request
  //   แต่ใช้กติกากันดับเบิลคลิก/สลับ run ชุดเดียวกัน)
  // - จับ url / รายการไฟล์ใน zip / ชื่อไฟล์ / threadId ไว้ตั้งแต่ตอนกด เพราะระหว่างโหลด (นานได้เป็นสิบวินาที)
  //   ผู้ใช้อาจสลับ run แล้ว state ในการ์ดเปลี่ยนหมด ถ้าอ่านค่าตอนงานจบจะได้ไฟล์/ชื่อไฟล์ของ run อื่น
  // - isStale() กันเซฟไฟล์ของ run ที่ผู้ใช้ไม่ได้เปิดอยู่แล้ว (abort / สลับ thread / มีงานใหม่มาแทน)
  // - ใช้ saveAs ของ file-saver แทนการสร้าง <a> + revokeObjectURL เอง เพราะการ revoke ทันทีหลัง click
  //   ทำให้ไฟล์ใหญ่ถูกตัดกลางคันในบางเบราว์เซอร์ (ของเดิมเป็นแบบนั้น)
  const startEpisodeDownload = async (kind: EpisodeMediaKind) => {
    const threadId = props.threadId;
    if (!threadId || episodeDownloadJobRef.current) return;

    const action =
      kind === "script"
        ? downloadActionsState.script
        : kind === "audio-zip"
        ? downloadActionsState.audioZip
        : kind === "video-zip"
        ? downloadActionsState.videoZip
        : downloadActionsState.final;
    if (!action.enabled) return;

    let url = "";
    let entries: { url: string; name: string }[] = [];
    let fileName = "";
    let scenes: ScriptWithImageItem[] = [];

    if (kind === "final") {
      if (!finalVideoUrl) return;
      url = finalVideoUrl;
      // ชนิดไฟล์ final รู้ได้จากนามสกุลเท่านั้น (run Audio Only ส่ง mp3 มาใน field เดียวกับวิดีโอ)
      // ไม่ต้องมี fallback: มาถึงตรงนี้ได้ก็ต่อเมื่อ downloadActionsState.final เปิด ซึ่งต้องมี finalKind
      // และ finalKind จะไม่เป็น null เว้นแต่ URL มีนามสกุลอยู่แล้ว ⇒ ext ว่างเป็นไปไม่ได้
      const ext = mediaExtFromUrl(url);
      fileName = downloadFilename({ runName, ext, review: finalIsReview });
    } else if (kind === "script") {
      // จับรายการฉากไว้ตอนกดด้วยเหตุผลเดียวกับ url/entries — งานที่ค้างอยู่ต้องได้ไฟล์ของ run ที่กด
      scenes = scriptWithImage?.items ?? [];
      fileName = downloadFilename({ runName, ext: "txt", review: false, suffix: "_script" });
    } else if (kind === "audio-zip") {
      // sceneCount = จำนวนฉากทั้งหมดบนจอ (ไม่ใช่จำนวนไฟล์) เพราะเลขที่เติม 0 ต้องกว้างพอสำหรับเลขฉากที่มากที่สุด
      entries = zipEntriesFor(episodeAudio.sources, scriptWithImage?.items?.length ?? 0, "wav");
      fileName = downloadFilename({ runName, ext: "zip", review: false, suffix: "_audio" });
    } else {
      entries = zipEntriesFor(episodeVideo.sources, scriptWithImage?.items?.length ?? 0, "mp4");
      // _review ดูจากชื่อไฟล์ segment ที่มีอยู่จริง ไม่ใช่ render_mode ของ state (ซึ่งเป็นของรอบที่กำลังจะเรนเดอร์)
      fileName = downloadFilename({
        runName,
        ext: "zip",
        review: isReviewSegmentUrl(episodeVideo.sources[0]?.url),
        suffix: "_video",
      });
    }

    const job: EpisodeDownloadJob = { threadId, controller: new AbortController() };
    episodeDownloadJobRef.current = job;
    setDownloadJob(kind);
    setDownloadProgress(null);

    const isStale = () =>
      job.controller.signal.aborted ||
      threadIdRef.current !== job.threadId ||
      episodeDownloadJobRef.current !== job;

    try {
      const blob =
        kind === "script"
          ? new Blob([buildScriptTextFile(scenes).content], { type: SCRIPT_TEXT_MIME })
          : kind === "final"
          ? await fetchMediaBlob(url, job.controller.signal)
          : await buildZipFromUrls(entries, {
              signal: job.controller.signal,
              onProgress: (p) => {
                if (!isStale()) setDownloadProgress(p);
              },
            });
      if (isStale()) return;
      saveAs(blob, fileName);
    } catch (err) {
      // ผู้ใช้ยกเลิกเอง (สลับ run / unmount) ไม่ใช่ความผิดพลาด ⇒ เงียบไว้
      if (isAbortError(err) || isStale()) return;
      notifyDownloadError(
        downloadErrorMessage(err, kind, {
          origin: window.location.origin,
          apiHost: apiHostForMessage(kind === "final" ? url : entries[0]?.url ?? ""),
        })
      );
    } finally {
      // ล้างสถานะเฉพาะตอนที่งานนี้ยังเป็นงานปัจจุบัน ไม่งั้นจะไปล้างสถานะของงานที่เริ่มใหม่
      if (episodeDownloadJobRef.current === job) {
        episodeDownloadJobRef.current = null;
        setDownloadJob(null);
        setDownloadProgress(null);
      }
    }
  };

  const handleDownloadFinalFile = () => {
    void startEpisodeDownload("final");
  };

  const handleDownloadAllAudio = () => {
    void startEpisodeDownload("audio-zip");
  };

  const handleDownloadAllVideo = () => {
    void startEpisodeDownload("video-zip");
  };

  const handleDownloadScript = () => {
    void startEpisodeDownload("script");
  };

  const handleOpenSelectVersion = (itemId: string, assetType: AssetType) => {
    if (!props.threadId || !props.onGetSegmentVersions) return;
    setSelectingVersionKey({ itemId, assetType });
    props.onGetSegmentVersions(
      props.threadId,
      itemId,
      assetType,
      (data) => {
        setVersionMap((prev) => ({ ...prev, [itemId]: { ...prev[itemId], [assetType]: data } }));
        setPendingVersionMap((prev) => ({
          ...prev,
          [itemId]: { ...prev[itemId], [assetType]: data.current },
        }));
      },
      // โหลดไม่ได้ (เช่น ไม่ใช่ editor → 403) ข้อความขึ้น snack จาก handler — ปิด panel ไม่งั้น spinner ค้าง
      // ปิดเฉพาะ panel ของคลิกนี้: error ที่มาช้าต้องไม่ปิด panel ของ scene ที่ผู้ใช้เพิ่งเปิด
      { onError: () => setSelectingVersionKey((prev) => closeVersionPanelOn(prev, { itemId, assetType })) },
    );
  };

  const handleSaveVersion = (itemId: string, assetType: AssetType) => {
    if (!props.threadId || !props.onSetCurrentVersion) return;
    const version = pendingVersionMap[itemId]?.[assetType];
    if (!version) return;
    props.onSetCurrentVersion(props.threadId, itemId, assetType, version, () => {
      setVersionMap((prev) => ({
        ...prev,
        [itemId]: {
          ...prev[itemId],
          [assetType]: { ...prev[itemId][assetType], current: version },
        },
      }));
      const itemIndex = scriptWithImage?.findIndex((it) => it.id === itemId);
      if (itemIndex !== undefined && itemIndex !== -1) {
        if (assetType === "image") {
          updateScriptItem(itemIndex, "uploaded_image_url", "");
          updateScriptItem(itemIndex, "is_image_uploaded", false);
        } else if (assetType === "video") {
          updateScriptItem(itemIndex, "uploaded_video_url", "");
          updateScriptItem(itemIndex, "is_uploaded", false);
        }
      }
      setSelectingVersionKey(null);
      if (props.onEvent && props.threadId) {
        props.onEvent({
          message: "HandleGetDirectScriptState",
          params: {
            threadId: props.threadId,
            onSuccess: (
              newState: DirectScriptGraphState,
              _: boolean,
              outputState?: DirectScriptOutputResponse,
            ) => {
              setGraphState((prev) => {
                if (prev && JSON.stringify(prev) === JSON.stringify(newState)) return prev;
                return newState;
              });
              if (outputState) setOutputData(outputState);
            },
          },
        });
      }
    });
  };

  // ─── Render: Header Card ──────────────────────────────────────────────────────

  // ─── Render: Progress Steps ───────────────────────────────────────────────────
  const renderProgressSteps = () => {
    if (!graphState?.progress || Object.keys(graphState.progress).length === 0) return null;

    const progressKeys = Object.keys(graphState.progress);
    const hasOnlyScriptToSceneSteps =
      progressKeys.length > 0 &&
      progressKeys.every((key: any) => SCRIPT2SCENE_STEP_IDS_SET.has(key));
    if (hasOnlyScriptToSceneSteps) return null;

    const steps = getActiveSteps();

    // ── Derived progress data ──────────────────────────────────────────────────
    const STOPPED_MESSAGE = "Generation stopped by user";

    const doneSteps = steps.filter((s) => {
      const status = getStepStatus(s.id);
      return status === "done";
    });
    const activeStep = steps.find((s) => graphState?.progress?.[s.id]?.status === "running");
    const errorSteps = steps.filter((s) => graphState?.progress?.[s.id]?.status === "failed" && !graphState?.progress?.[s.id]?.skipped);
    const totalStepsFromData = graphState?.progress
      ? Object.values(graphState.progress as Record<string, DirectScriptProgress>)[0]?.total_steps
      : undefined;
    const totalSteps = totalStepsFromData ?? steps.length;
    const doneCount = doneSteps.length;

    // values.error มี variant อื่นด้วย ("Lipsync generation stopped by user" — client.py:1946) จึงเทียบด้วย helper ไม่ใช่ ===
    const isStopped = errorSteps.some(
      (s) => graphState?.progress?.[s.id]?.detail === STOPPED_MESSAGE
    ) || isStoppedByUser(graphState?.values?.error);
    const hasError = errorSteps.length > 0 && !isStopped;

    const progressPct = totalSteps > 0
      ? Math.round(((doneCount + (activeStep ? 0.5 : 0)) / totalSteps) * 100)
      : 0;

    const isDone = !isRunning && doneCount > 0 && !activeStep && !hasError;
    const isAnyRunning = !!activeStep || isRunning;

    const totalDuration = graphState?.values?.step_durations
      ? Object.values(graphState.values.step_durations).reduce((sum, d) => sum + (d || 0), 0)
      : null;

    const activeStepData = activeStep ? graphState?.progress?.[activeStep.id] : null;
    const activeDetail = activeStepData?.current_item
      ? `Scene ${activeStepData.current_item}`
      : activeStepData?.detail || null;

    return (
      <SectionCard id="megen-gen-direct-progress-card">
        {/* ── Header ── */}
        <Box
          id="megen-gen-direct-progress-header"
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: isProgressExpanded ? 1.5 : 0,
            pb: isProgressExpanded ? 1 : 0,
            borderBottom: isProgressExpanded ? "1px solid #e2e8f0" : "none",
            cursor: "pointer",
          }}
          onClick={() => setIsProgressExpanded((prev) => !prev)}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <SectionTitle sx={{ mb: 0 }}>Progress Steps</SectionTitle>

            {/* Status badge */}
            {isAnyRunning && !isStopped && (
              <Box sx={{
                display: "flex", alignItems: "center", gap: 0.5,
                px: 1, py: 0.25, borderRadius: "9999px",
                backgroundColor: "#fdf2f8", border: "1px solid #f9a8d4",
              }}>
                <CircularProgress size={8} sx={{ color: "#f382a0" }} />
                <Typography sx={{ fontSize: "0.75rem", fontWeight: 600, color: "#db2777" }}>
                  Running
                </Typography>
              </Box>
            )}
            {isStopped && (
              <Box sx={{
                display: "flex", alignItems: "center", gap: 0.5,
                px: 1, py: 0.25, borderRadius: "9999px",
                backgroundColor: "#fff7ed", border: "1px solid #fed7aa",
              }}>
                <StopIcon sx={{ fontSize: 10, color: "#f97316" }} />
                <Typography sx={{ fontSize: "0.75rem", fontWeight: 600, color: "#f97316" }}>
                  Stopped
                </Typography>
              </Box>
            )}
            {hasError && !isAnyRunning && (
              <Box sx={{
                display: "flex", alignItems: "center", gap: 0.5,
                px: 1, py: 0.25, borderRadius: "9999px",
                backgroundColor: "#fef2f2", border: "1px solid #fecaca",
              }}>
                <ErrorIcon sx={{ fontSize: 10, color: "#ef4444" }} />
                <Typography sx={{ fontSize: "0.75rem", fontWeight: 600, color: "#ef4444" }}>
                  Error
                </Typography>
              </Box>
            )}
            {isDone && !isStopped && (
              <Box sx={{
                display: "flex", alignItems: "center", gap: 0.5,
                px: 1, py: 0.25, borderRadius: "9999px",
                backgroundColor: "#dcfce7", border: "1px solid #bbf7d0",
              }}>
                <CheckCircleIcon sx={{ fontSize: 10, color: "#16a34a" }} />
                <Typography sx={{ fontSize: "0.75rem", fontWeight: 600, color: "#16a34a" }}>
                  Done
                </Typography>
              </Box>
            )}
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {/* Stop Run Button */}
            {isAnyRunning && (
              <Button
                id="megen-gen-direct-stop-btn"
                size="small"
                startIcon={
                  isStopping
                    ? <CircularProgress size={12} sx={{ color: "white" }} />
                    : <StopIcon sx={{ fontSize: 14 }} />
                }
                onClick={(e) => {
                  e.stopPropagation();
                  handleStopGenerate();
                }}
                disabled={isStopping}
                sx={{
                  textTransform: "none",
                  fontSize: "0.95rem",
                  fontWeight: 600,
                  // px: 1.25,
                  px: 2,
                  py: 0.25,
                  borderRadius: "9999px",
                  backgroundColor: "#ef4444",
                  color: "white",
                  "&:hover": { backgroundColor: "#dc2626" },
                  "&:disabled": { backgroundColor: "#94a3b8" },
                  minWidth: "auto",
                  marginRight: "1rem"
                }}
              >
                {isStopping ? "Stopping..." : "Stop Run"}
              </Button>
            )}

            {/* Step count */}
            <Typography sx={{ fontSize: "0.75rem", color: "#94a3b8" }}>
              {doneCount} / {totalSteps} steps
              {isDone && totalDuration ? ` · ${totalDuration.toFixed(1)}s` : ""}
            </Typography>

            {/* Expand / Collapse icon */}
            <IconButton
              size="small"
              sx={{ color: "#64748b" }}
              onClick={(e) => {
                e.stopPropagation();
                setIsProgressExpanded((prev) => !prev);
              }}
            >
              {isProgressExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
        </Box>

        {/* ── Progress bar ── */}
        {!isProgressExpanded && (
          <Box>
            <Box sx={{
              height: 4, borderRadius: "9999px",
              backgroundColor: "#f1f5f9", overflow: "hidden",
              mb: (isAnyRunning || isDone || hasError) ? 0.5 : 0,
              mt: 1
            }}>
              <Box sx={{
                height: "100%",
                width: `${isDone ? 100 : progressPct}%`,
                borderRadius: "9999px",
                // *** สีแดงเมื่อ error
                backgroundColor: hasError ? "#ef4444" : isStopped ? "#f97316" : isDone ? "#22c55e" : "#f382a0",
                transition: "width 0.4s ease",
              }} />
            </Box>

            {isAnyRunning && activeStep && !hasError && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                <Box sx={{ width: 6, height: 6, borderRadius: "50%", backgroundColor: "#f382a0", flexShrink: 0 }} />
                <Typography sx={{ fontSize: "0.8125rem", fontWeight: 600, color: "#1e293b" }}>
                  {activeStep.label}
                </Typography>
                {activeDetail && (
                  <Typography sx={{ fontSize: "0.8125rem", color: "#64748b", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    — {activeDetail}
                  </Typography>
                )}
              </Box>
            )}

            {hasError && (
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.75, mt: 0.25 }}>
                <ErrorIcon sx={{ fontSize: 14, color: "#ef4444", mt: "1px", flexShrink: 0 }} />
                <Box sx={{ minWidth: 0 }}>
                  {errorSteps.map((step) => {
                    const errMsg = graphState?.progress?.[step.id]?.error;
                    const shortErr = errMsg
                      ? errMsg.length > 80 ? errMsg.slice(0, 80) + "..." : errMsg
                      : "เกิดข้อผิดพลาด";
                    return (
                      <Typography key={step.id} sx={{ fontSize: "0.8125rem", fontWeight: 600, color: "#ef4444", lineHeight: 1.4 }}>
                        {step.label}: <Typography component="span" sx={{ fontWeight: 400, fontSize: "0.8125rem", color: "#64748b" }}>{shortErr}</Typography>
                      </Typography>
                    );
                  })}
                </Box>
              </Box>
            )}
          </Box>
        )}

        {/* ── Step list ── */}
        <Collapse id="megen-gen-direct-progress-collapse" in={isProgressExpanded}>
          <Box>
            {steps.map((step) => {
              const status = getStepStatus(step.id);
              const progressData = graphState?.progress?.[step.id];
              const stepDuration = graphState?.values?.step_durations?.[step.id];
              const isExpanded = status === "active" || expandedStepId === step.id;

              return (
                <Box key={step.id}>
                  <StepRow
                    id={`megen-gen-direct-step-${step.id}`}
                    onClick={() => setExpandedStepId((prev) => (prev === step.id ? null : step.id))}
                    sx={{ cursor: "pointer", "&:hover": { backgroundColor: "#f1f5f9" } }}
                  >
                    <StepIndicator status={status}>
                      {status === "done" && <CheckCircleIcon sx={{ fontSize: 11 }} />}
                      {status === "active" && <CircularProgress size={9} sx={{ color: "#f382a0" }} />}
                      {status === "error" && <ErrorIcon sx={{ fontSize: 11 }} />}
                      {status === "pending" && <CircleIcon sx={{ fontSize: 8 }} />}
                    </StepIndicator>
                    <StepLabel status={status} sx={{ flex: 1 }}>
                      {step.label}
                    </StepLabel>
                    {status === "active" && progressData?.detail && (
                      <Typography sx={{ fontSize: "0.65rem", color: "#94a3b8", fontStyle: "italic" }}>
                        {progressData.detail}
                      </Typography>
                    )}
                    {status === "done" && stepDuration && (
                      <Typography sx={{ fontSize: "0.65rem", color: "#cbd5e1", fontFamily: "monospace" }}>
                        {Number(stepDuration).toFixed(1)}s
                      </Typography>
                    )}
                    {status === "active" && (
                      <Typography sx={{ fontSize: "0.65rem", color: "#f382a0", fontWeight: 600 }}>
                        Running
                      </Typography>
                    )}
                    {/* {status === "error" && (
                      <Typography sx={{ fontSize: "0.65rem", color: "#ef4444", fontWeight: 600 }}>
                        Error
                      </Typography>
                    )} */}
                    {status === "error" && (
                      <Typography sx={{ fontSize: "0.65rem", fontWeight: 600,
                        color: graphState?.progress?.[step.id]?.detail === STOPPED_MESSAGE ? "#f97316" : "#ef4444"
                      }}>
                        {graphState?.progress?.[step.id]?.detail === STOPPED_MESSAGE ? "Stopped" : "Error"}
                      </Typography>
                    )}
                  </StepRow>

                  {/* Step detail */}
                  <Collapse in={isExpanded && !!progressData}>
                    <Box sx={{
                      ml: "1.5rem", mt: 0, mb: "0.375rem",
                      p: "0.375rem 0.5rem", backgroundColor: "#f8fafc",
                      borderRadius: "0.375rem", borderLeft: "2px solid #f1f5f9",
                    }}>
                      {progressData && (progressData.total || 0) > 0 && (
                        <Box sx={{ mb: 0.5 }}>
                          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.25 }}>
                            <Typography sx={{ fontSize: "0.65rem", color: "#94a3b8" }}>Processing...</Typography>
                            <Typography sx={{ fontSize: "0.65rem", color: "#94a3b8" }}>
                              {progressData.current} / {progressData.total}
                            </Typography>
                          </Box>
                          <Box sx={{ height: 3, borderRadius: 3, backgroundColor: "#e2e8f0", overflow: "hidden" }}>
                            <Box sx={{
                              height: "100%",
                              width: `${((progressData.current || 0) / (progressData.total || 0)) * 100}%`,
                              backgroundColor: status === "done" ? "#22c55e" : "#f382a0",
                              borderRadius: 3, transition: "width 0.3s ease",
                            }} />
                          </Box>
                        </Box>
                      )}
                      {progressData?.current_item && status === "active" && (
                        <Typography sx={{ fontSize: "0.65rem", color: "#94a3b8", mb: 0.25 }}>
                          Scene: <strong>{progressData.current_item}</strong>
                        </Typography>
                      )}
                      {progressData?.logs && progressData.logs.length > 0 && (
                        // column-reverse + reverse array: ลำดับที่เห็นยังเป็น เก่า→ใหม่ แต่ scroll ปักที่บรรทัดล่าสุดขณะ log ไหลเข้า
                        <Box sx={{ mt: 0.5, maxHeight: "120px", overflowY: "auto", display: "flex", flexDirection: "column-reverse" }}>
                          {[...progressData.logs].reverse().map((log: string, i: number, arr: string[]) => (
                            <Typography key={arr.length - 1 - i} sx={{ fontSize: "0.6rem", fontFamily: "monospace", color: "#94a3b8", lineHeight: 1.4 }}>
                              {log}
                            </Typography>
                          ))}
                        </Box>
                      )}
                      {progressData?.error && (
                        <Box sx={{
                          mb: 1, p: 1,
                          backgroundColor: "#fef2f2", border: "1px solid #fecaca",
                          borderRadius: "0.375rem",
                          display: "flex", alignItems: "flex-start", gap: 0.75,
                        }}>
                          <ErrorIcon sx={{ fontSize: 14, color: "#ef4444", mt: "1px", flexShrink: 0 }} />
                          <Typography sx={{ fontSize: "0.75rem", color: "#dc2626", fontFamily: "monospace", wordBreak: "break-all", lineHeight: 1.5 }}>
                            {progressData.error}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Collapse>
                </Box>
              );
            })}
          </Box>
        </Collapse>
      </SectionCard>
    );
  };

  // ─── Render: Script2Scene Input ───────────────────────────────────────────────

  const renderScript2SceneContent = () => (
    <Box>
      <Box sx={{ mb: 2 }}>
        <FormLabel>Script (ภาษาไทย / English)</FormLabel>
        <StyledTextField
          fullWidth
          multiline
          rows={8}
          placeholder="ใส่สคริปต์ที่นี่..."
          value={scriptText}
          onChange={(e) => setScriptText(e.target.value)}
          disabled={isScriptToSceneLoading}
        />
      </Box>

      {scriptToSceneProgress && (
        <Box sx={{ mb: 2 }}>
          {STEPS_SCRIPT2SCENE.map((step) => {
            const stepStatus = scriptToSceneProgress.progress?.[step.id]?.status;
            const isDone = stepStatus === "done";
            const isActive = stepStatus === "start";
            return (
              <StepRow key={step.id}>
                <StepIndicator status={isDone ? "done" : isActive ? "active" : "pending"}>
                  {isDone && <CheckCircleIcon sx={{ fontSize: 16 }} />}
                  {isActive && <CircularProgress size={14} sx={{ color: "#f382a0" }} />}
                  {!isDone && !isActive && <CircleIcon sx={{ fontSize: 16 }} />}
                </StepIndicator>
                <StepLabel status={isDone ? "done" : isActive ? "active" : "pending"}>
                  {step.label}
                </StepLabel>
                {isDone && scriptToSceneProgress.values?.step_durations?.[step.id] && (
                  <Typography
                    sx={{ fontSize: "0.75rem", color: "#64748b", fontFamily: "monospace" }}
                  >
                    {Number(scriptToSceneProgress.values.step_durations[step.id]).toFixed(2)}s
                  </Typography>
                )}
              </StepRow>
            );
          })}
        </Box>
      )}

      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        <SuccessButton
          startIcon={
            isScriptToSceneLoading ? (
              <CircularProgress size={16} sx={{ color: "white" }} />
            ) : (
              <PlayArrowIcon />
            )
          }
          onClick={handleSubmitScriptToScene}
          disabled={isScriptToSceneLoading || !scriptText.trim()}
        >
          {isScriptToSceneLoading ? "กำลังสร้าง Scene ย่อย..." : "เริ่มต้นสร้าง Scene ย่อย"}
        </SuccessButton>
      </Box>
    </Box>
  );

  // ─── Render: Direct Script Input Form ────────────────────────────────────────

  const renderDirectScriptContent = () => (
    <Box>
      <Box sx={{ mb: 3 }}>
        <FormLabel>หัวข้อเรื่อง / Story Title</FormLabel>
        <StyledTextField
          fullWidth
          placeholder="e.g. How to make Spaghetti Carbonara"
          value={directTitle || ""}
          onChange={(e) => setDirectTitle(e.target.value)}
          disabled={loadingDraft}
        />
      </Box>

      <Box sx={{ mb: 3 }}>
        <FormLabel>Image Style</FormLabel>
        <Select
          fullWidth
          size="small"
          value={selectedImageStyle}
          onChange={(e) => setSelectedImageStyle(e.target.value)}
          disabled={loadingDraft}
          sx={{
            borderRadius: "0.5rem",
            fontSize: "0.875rem",
            mt: 0.5,
            "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e2e8f0" },
            "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#cbd5e1" },
          }}
        >
          {(props.mediaStyleList || []).map((style) => (
            <MenuItem key={style.value} value={style.value}>
              {style.text}
            </MenuItem>
          ))}
        </Select>
      </Box>

      <Box sx={{ mb: 3 }}>
        <FormLabel>สัดส่วนวิดีโอ / Video Ratio</FormLabel>
        <Select
          fullWidth
          size="small"
          value={aspectRatio}
          onChange={(e) => setAspectRatio(e.target.value)}
          disabled={loadingDraft}
          sx={{
            borderRadius: "0.5rem",
            fontSize: "0.875rem",
            mt: 0.5,
            "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e2e8f0" },
            "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#cbd5e1" },
          }}
        >
          {ASPECT_RATIO_OPTIONS.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </Select>
      </Box>

      <Box sx={{ mb: 2 }}>
        <FormLabel>เสียงพูด / Voice</FormLabel>
        <Select
          fullWidth
          size="small"
          value={selectedVoiceName}
          onChange={(e) => setSelectedVoiceName(e.target.value)}
          disabled={loadingDraft}
          sx={{
            borderRadius: "0.5rem",
            fontSize: "0.875rem",
            mt: 0.5,
            "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e2e8f0" },
            "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#cbd5e1" },
          }}
        >
          {(props.voiceList || []).map((voice) => (
            <MenuItem key={voice.name} value={voice.name}>
              {voice.name} ({voice.gender}, {voice.tone})
            </MenuItem>
          ))}
        </Select>
      </Box>

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext
          items={directItems.map((item) => item.id)}
          strategy={verticalListSortingStrategy}
        >
          {directItems.map((item, index) => (
            <CardMeGenSortableScene key={item.id} id={item.id}>
              {(dragHandleProps) => (
                <SceneCard>
                  <SceneHeader>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      <Tooltip title="คลิกแล้วลากเพื่อเปลี่ยนลำดับ">
                        <IconButton
                          size="small"
                          disabled={loadingDraft}
                          sx={{
                            cursor: "grab",
                            color: "#94a3b8",
                            padding: "4px",
                            "&:active": { cursor: "grabbing" },
                            "&:hover": { color: "#475569", backgroundColor: "#f1f5f9" },
                          }}
                          {...(() => {
                            const { color, ...rest } = dragHandleProps as any;
                            return rest;
                          })()}
                        >
                          <DragIndicatorIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="ย้าย Scene นี้ขึ้น 1 ลำดับ">
                        <span>
                          <IconButton
                            size="small"
                            disabled={loadingDraft || index === 0}
                            onClick={() => handleMoveScene(index, "up")}
                            sx={{
                              color: "#94a3b8",
                              padding: "4px",
                              "&:hover": { color: "#3b82f6", backgroundColor: "#dbeafe" },
                              "&:disabled": { color: "#e2e8f0" },
                            }}
                          >
                            <ArrowUpIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="ย้าย Scene นี้ลง 1 ลำดับ">
                        <span>
                          <IconButton
                            size="small"
                            disabled={loadingDraft || index === directItems.length - 1}
                            onClick={() => handleMoveScene(index, "down")}
                            sx={{
                              color: "#94a3b8",
                              padding: "4px",
                              "&:hover": { color: "#3b82f6", backgroundColor: "#dbeafe" },
                              "&:disabled": { color: "#e2e8f0" },
                            }}
                          >
                            <ArrowDownIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Typography sx={{ fontWeight: 600, color: "#1e293b" }}>
                        Scene {index + 1}
                      </Typography>
                    </Box>
                    {directItems.length > 1 && (
                      <Tooltip title="ลบ Scene นี้">
                        <DeleteSceneButton
                          onClick={() => handleRemoveScene(index)}
                          disabled={loadingDraft}
                        >
                          <DeleteIcon fontSize="small" />
                        </DeleteSceneButton>
                      </Tooltip>
                    )}
                  </SceneHeader>

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                      gap: 2,
                    }}
                  >
                    <Box>
                      <FormLabel>Voice Guide</FormLabel>
                      <StyledTextField
                        fullWidth
                        multiline
                        rows={3}
                        placeholder="Style: ...\nTone: ..."
                        value={item?.voice_guide || ""}
                        onChange={(e) => handleSceneChange(index, "voice_guide", e.target.value)}
                        disabled={loadingDraft}
                      />
                      <FormLabel sx={{ mt: 1 }}>Voice Script</FormLabel>
                      <StyledTextField
                        fullWidth
                        multiline
                        rows={4}
                        placeholder="สวัสดีครับ วันนี้เราจะมาทำ..."
                        value={item?.voice_script || ""}
                        onChange={(e) => handleSceneChange(index, "voice_script", e.target.value)}
                        disabled={loadingDraft}
                      />
                    </Box>
                    <Box>
                      <FormLabel>Scene Description</FormLabel>
                      <StyledTextField
                        fullWidth
                        multiline
                        rows={4}
                        placeholder="Ex. Chef is holding a plate of pasta..."
                        value={item?.image_prompt || ""}
                        onChange={(e) => handleSceneChange(index, "image_prompt", e.target.value)}
                        disabled={loadingDraft}
                      />
                    </Box>
                  </Box>
                </SceneCard>
              )}
            </CardMeGenSortableScene>
          ))}
        </SortableContext>
      </DndContext>

      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 2 }}>
        <ActionButton
          startIcon={<AddIcon />}
          onClick={handleAddScene}
          disabled={loadingDraft}
          sx={{
            backgroundColor: "#e2e8f0",
            color: "#475569",
            "&:hover": { backgroundColor: "#cbd5e1" },
          }}
        >
          เพิ่ม Scene
        </ActionButton>
      </Box>

      {props.threadId && (
        <Box sx={{ mt: 3, p: 2, backgroundColor: "#fdf2f8", borderRadius: "0.75rem", border: "1px solid #f9a8d4" }}>
          <FormLabel sx={{ color: "#db2777", fontWeight: 700, mb: 1, display: "block" }}>
            Refine Script with AI
          </FormLabel>
          <StyledTextField
            fullWidth
            multiline
            rows={3}
            placeholder="เช่น 'ปรับ Scene 1 กับ Scene 2 ให้ต่อเนื่องกัน' หรือ 'เปลี่ยน visual style ทุก Scene ให้เป็นแบบ 3D animation'"
            value={refinePrompt}
            onChange={(e) => setRefinePrompt(e.target.value)}
            disabled={isRefining}
            sx={{ mb: 1.5, "& .MuiOutlinedInput-root": { backgroundColor: "white" } }}
          />
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography sx={{ fontSize: "0.75rem", color: "#be185d", mt: 1 }}>
              * AI จะปรับ Script และ Visual เมื่อแจ้งเตือนการ Refine สำเร็จ
            </Typography>

            <SuccessButton
              startIcon={
                isRefining
                  ? <CircularProgress size={16} sx={{ color: "white" }} />
                  : <PlayArrowIcon />
              }
              onClick={handleRefine}
              disabled={isRefining || !refinePrompt.trim()}
            >
              {isRefining ? "กำลัง Refine..." : "Refine with AI"}
            </SuccessButton>
          </Box>
        </Box>
      )}
    </Box>
  );

  // ─── Render: Review Scene Items (Edit Mode) ───────────────────────────────────────────────

  const renderEditModeItems = () => {
    if (!scriptWithImage?.items) return null;

    const items = scriptWithImage.items;
    const total = items.length;

    const handleDragEndEditMode = (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      const oldIndex = items.findIndex((item) => item.id === active.id);
      const newIndex = items.findIndex((item) => item.id === over.id);
      handleEditModeReorder(oldIndex, newIndex);
    };

    return (
      <Box>
        {/* Hint bar */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2, px: 0.5 }}>
          <Typography sx={{ fontSize: "0.75rem", color: "#94a3b8" }}>
            {total} scene{total !== 1 ? "s" : ""}
          </Typography>
          <Box sx={{ width: 3, height: 3, borderRadius: "50%", backgroundColor: "#e2e8f0", flexShrink: 0 }} />
          <Typography sx={{ fontSize: "0.75rem", color: "#94a3b8" }}>
            ลากเพื่อเปลี่ยนลำดับ หรือใช้ปุ่ม ↑↓
          </Typography>
        </Box>

        {/* Sortable List */}
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEndEditMode}
        >
          <SortableContext
            items={items.map((item) => item.id)}
            strategy={verticalListSortingStrategy}
          >
            {items.map((item, index) => {
              const isFirst = index === 0;
              const isLast = index === total - 1;
              const isConfirmingDelete = deleteConfirmId === item.id;
              const goToVal = goToInputMap[item.id] || "";
              const voiceScript = hydrateVoicePrompt(item).voice_script;
              const scriptPreview = voiceScript
                ? voiceScript.length > 65
                  ? voiceScript.slice(0, 65) + "..."
                  : voiceScript
                : "(ยังไม่มี script)";

              return (
                <CardMeGenSortableScene key={item.id} id={item.id}>
                  {(dragHandleProps) => (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "stretch",
                        mb: 1,
                        border: "1px solid",
                        borderColor: isConfirmingDelete ? "#fecaca" : "#e2e8f0",
                        borderRadius: "0.75rem",
                        overflow: "hidden",
                        backgroundColor: "white",
                        transition: "border-color 0.15s, box-shadow 0.15s",
                        "&:hover": {
                          borderColor: isConfirmingDelete ? "#fca5a5" : "#f9a8d4",
                          boxShadow: "0 1px 4px rgba(219,39,119,0.07)",
                        },
                      }}
                    >
                      {/* ── Drag Handle ── */}
                      <Box
                        sx={{
                          width: 36,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          backgroundColor: "#f8fafc",
                          borderRight: "1px solid #f1f5f9",
                          cursor: "grab",
                          flexShrink: 0,
                          color: "#d1d5db",
                          transition: "all 0.15s",
                          "&:hover": { color: "#9ca3af", backgroundColor: "#f1f5f9" },
                          "&:active": { cursor: "grabbing" },
                        }}
                        {...(() => {
                          const { color, ...rest } = dragHandleProps as any;
                          return rest;
                        })()}
                      >
                        <DragIndicatorIcon sx={{ fontSize: 18 }} />
                      </Box>

                      {/* ── Scene Number Badge ── */}
                      <Box
                        sx={{
                          width: 40,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          backgroundColor: "#fdf2f8",
                          borderRight: "1px solid #fce7f3",
                          flexShrink: 0,
                        }}
                      >
                        <Typography sx={{ fontSize: "0.875rem", fontWeight: 700, color: "#db2777" }}>
                          {index + 1}
                        </Typography>
                      </Box>

                      {/* ── Main Content ── */}
                      <Box sx={{ flex: 1, px: 1.5, py: 1.25, minWidth: 0 }}>
                        {/* Title row */}
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.5 }}>
                          <Typography sx={{ fontSize: "0.875rem", fontWeight: 600, color: "#1e293b" }}>
                            Scene {index + 1}
                          </Typography>
                          {item.voice_name && (
                            <>
                              <Box sx={{ width: 3, height: 3, borderRadius: "50%", backgroundColor: "#e2e8f0", flexShrink: 0 }} />
                              <Typography sx={{ fontSize: "0.75rem", color: "#94a3b8" }}>
                                {item.voice_name}
                              </Typography>
                            </>
                          )}
                        </Box>

                        {/* Script preview */}
                        <Typography
                          sx={{
                            fontSize: "0.8125rem",
                            color: voiceScript ? "#64748b" : "#cbd5e1",
                            fontStyle: voiceScript ? "normal" : "italic",
                            lineHeight: 1.4,
                            mb: 1.25,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {scriptPreview}
                        </Typography>

                        {/* Go To row */}
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                          <Typography sx={{ fontSize: "0.7rem", color: "#94a3b8", whiteSpace: "nowrap" }}>
                            ย้ายไป Scene:
                          </Typography>
                          <Box
                            component="input"
                            type="number"
                            min={1}
                            max={total}
                            value={goToVal}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                              setGoToInputMap((prev) => ({ ...prev, [item.id]: e.target.value }))
                            }
                            onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                              if (e.key === "Enter") handleGoTo(item.id, index);
                            }}
                            placeholder={`1–${total}`}
                            sx={{
                              width: 56,
                              height: 26,
                              border: "1px solid #e2e8f0",
                              borderRadius: "0.375rem",
                              px: "6px",
                              fontSize: "0.75rem",
                              color: "#1e293b",
                              backgroundColor: "white",
                              outline: "none",
                              textAlign: "center",
                              "&:focus": { borderColor: "#f382a0", outline: "none" },
                              // remove number input arrows
                              "&::-webkit-outer-spin-button, &::-webkit-inner-spin-button": {
                                WebkitAppearance: "none",
                                margin: 0,
                              },
                              MozAppearance: "textfield",
                            }}
                          />
                          <Box
                            component="button"
                            onClick={() => handleGoTo(item.id, index)}
                            disabled={!goToVal.trim()}
                            sx={{
                              height: 26,
                              px: 1.25,
                              border: "1px solid",
                              borderColor: goToVal.trim() ? "#f9a8d4" : "#e2e8f0",
                              borderRadius: "0.375rem",
                              backgroundColor: goToVal.trim() ? "#fdf2f8" : "#f8fafc",
                              color: goToVal.trim() ? "#db2777" : "#cbd5e1",
                              fontSize: "0.7rem",
                              fontWeight: 600,
                              cursor: goToVal.trim() ? "pointer" : "default",
                              transition: "all 0.15s",
                              lineHeight: 1,
                              "&:hover": goToVal.trim() ? { backgroundColor: "#fce7f3" } : {},
                            }}
                          >
                            Go
                          </Box>
                        </Box>
                      </Box>

                      {/* ── Right Controls ── */}
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 0.25,
                          px: 0.75,
                          borderLeft: "1px solid #f1f5f9",
                          flexShrink: 0,
                        }}
                      >
                        {/* Up */}
                        <Tooltip title="ขึ้น 1 ลำดับ">
                          <span>
                            <IconButton
                              size="small"
                              disabled={isFirst}
                              onClick={() => handleEditModeReorder(index, index - 1)}
                              sx={{
                                width: 28,
                                height: 28,
                                color: isFirst ? "#e2e8f0" : "#94a3b8",
                                "&:hover": isFirst
                                  ? {}
                                  : { color: "#db2777", backgroundColor: "#fdf2f8" },
                              }}
                            >
                              <ArrowUpIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </span>
                        </Tooltip>

                        {/* Down */}
                        <Tooltip title="ลง 1 ลำดับ">
                          <span>
                            <IconButton
                              size="small"
                              disabled={isLast}
                              onClick={() => handleEditModeReorder(index, index + 1)}
                              sx={{
                                width: 28,
                                height: 28,
                                color: isLast ? "#e2e8f0" : "#94a3b8",
                                "&:hover": isLast
                                  ? {}
                                  : { color: "#db2777", backgroundColor: "#fdf2f8" },
                              }}
                            >
                              <ArrowDownIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </span>
                        </Tooltip>

                        {/* Delete — 2-step confirm */}
                        <Tooltip
                          title={
                            total <= 1
                              ? "ต้องมีอย่างน้อย 1 scene"
                              : isConfirmingDelete
                                ? "กดอีกครั้งเพื่อยืนยันลบ"
                                : "ลบ Scene นี้"
                          }
                        >
                          <span>
                            <IconButton
                              size="small"
                              disabled={total <= 1}
                              onClick={() => setDeleteModalItemId(item.id)}
                              onBlur={() => {
                                // cancel confirm เมื่อ focus ออก
                                if (isConfirmingDelete) setDeleteConfirmId(null);
                              }}
                              sx={{
                                width: 28,
                                height: 28,
                                ...(isConfirmingDelete
                                  ? {
                                    color: "#ef4444",
                                    backgroundColor: "#fef2f2",
                                    border: "1px solid #fecaca",
                                    "&:hover": { backgroundColor: "#fee2e2" },
                                  }
                                  : {
                                    color: total <= 1 ? "#e2e8f0" : "#94a3b8",
                                    "&:hover":
                                      total <= 1
                                        ? {}
                                        : { color: "#ef4444", backgroundColor: "#fef2f2" },
                                  }),
                              }}
                            >
                              <DeleteIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Box>
                    </Box>
                  )}
                </CardMeGenSortableScene>
              );
            })}
          </SortableContext>
        </DndContext>

        {/* Add Scene */}
        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1.5 }}>
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={handleEditModeAdd}
            sx={{
              textTransform: "none",
              fontSize: "0.8125rem",
              fontWeight: 600,
              color: "#db2777",
              border: "1px solid #f9a8d4",
              borderRadius: "9999px",
              px: 2,
              py: 0.5,
              backgroundColor: "#fdf2f8",
              "&:hover": { backgroundColor: "#fce7f3", borderColor: "#db2777" },
            }}
          >
            เพิ่ม Scene
          </Button>
        </Box>
      </Box>
    );
  };

  // ─── Render: Upload Image / Video Button
  const renderUploadImageButton = (
    item: any,
    index: number,
    isDisabled: boolean,
    uploadingImageItemId: string | null,
    handleUploadImageFile: (index: number, itemId: string, file: File) => void,
    updateScriptItem: (index: number, field: string, value: any) => void,
  ) => (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <SuccessButton
        component="label"
        size="small"
        startIcon={
          uploadingImageItemId === item.id
            ? <CircularProgress size={12} sx={{ color: "white" }} />
            : <CloudUploadIcon sx={{ fontSize: 12 }} />
        }
        disabled={uploadingImageItemId === item.id || isDisabled}
        sx={{ fontSize: "0.7rem", py: 0.25, px: 1.5 }}
      >
        {uploadingImageItemId === item.id ? "Uploading..." : "Upload Image"}
        <input
          type="file"
          accept="image/*"
          hidden
          onChange={(e) => {
            if (!e.target.files?.[0]) return;
            // set flag อัตโนมัติ
            updateScriptItem(index, "is_image_uploaded", true);
            handleUploadImageFile(index, item.id, e.target.files[0]);
          }}
        />
      </SuccessButton>
      {item.uploaded_image_url && uploadingImageItemId !== item.id && (
        <Typography sx={{ fontSize: "0.6rem", color: "#22c55e", fontWeight: 600 }}>✓ Uploaded</Typography>
      )}
    </Box>
  );

  const renderUploadVideoButton = (
    item: any,
    index: number,
    isDisabled: boolean,
    isItemUploading: boolean,
    handleUploadVideoFile: (index: number, itemId: string, file: File) => void,
    updateScriptItem: (index: number, field: string, value: any) => void,
  ) => (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <Button
        component="label"
        variant="outlined"
        size="small"
        startIcon={
          isItemUploading
            ? <CircularProgress size={12} />
            : <CloudUploadIcon sx={{ fontSize: 12 }} />
        }
        disabled={isItemUploading || isDisabled}
        sx={{
          textTransform: "none",
          fontSize: "0.6rem",
          fontWeight: 600,
          borderColor: "#ffa9c6",
          color: "#e91e63",
          backgroundColor: "#fce4ec",
          borderRadius: "9999px",
          py: 0.375,
          px: 1.5,
          "&:hover": { backgroundColor: "#fce4ec", borderColor: "#db2777" },
        }}
      >
        {isItemUploading ? "Uploading..." : "Upload My Video"}
        <input
          type="file"
          accept="video/*"
          hidden
          onChange={(e) => {
            if (!e.target.files?.[0]) return;
            // set flag อัตโนมัติ
            updateScriptItem(index, "is_uploaded", true);
            handleUploadVideoFile(index, item.id, e.target.files[0]);
          }}
        />
      </Button>
      {item.uploaded_video_url && !isItemUploading && (
        <Typography sx={{ fontSize: "0.6rem", color: "#22c55e", fontWeight: 600 }}>✓ Uploaded</Typography>
      )}
    </Box>
  );

  // ─── Render: Single Review Scene (extracted for PowerPoint layout reuse) ─────
  const renderSingleReviewScene = (item: any, index: number) => {
    const isDisabled = loadingVideo || isRunning || item.active === false;
    const isTextDisabled = loadingVideo || item.active === false;
    const outputItem = outputData?.items.find((output: any) => output.id === item.id);
    const isItemUploading = uploadingVideoItemId === item.id;

    const hasImageAsset = !!(outputItem?.uploaded_image_url || outputItem?.image_url);
    const hasVideoAsset = !!(outputItem?.uploaded_video_url || outputItem?.ai_video_url);
    const hasUploadedVideo = !!(item.uploaded_video_url);
    // ฉากที่ใช้คลิปจากลิงก์ภายนอก: เชื่อค่าจาก /output ก่อน แล้วค่อยถอยไปใช้ค่าใน script item
    const externalVideoLink = externalVideoLinkOf(item, outputItem);
    const hasExternalVideo = externalVideoLink !== null;
    const hasUploadedImage = !!(item.uploaded_image_url);

    const hasAnyPreview =
      hasImageAsset || hasVideoAsset || hasUploadedVideo || hasUploadedImage ||
      !!outputItem?.segment_video_url || !!outputItem?.audio_url;

    const useAnimation = !!(localUseAnimated[item.id] ?? false);

    const isCheckingThisScene = loadingCostCheck && pendingGenerateSceneItemId === item.id;
    const isGeneratingThisScene = generatingSceneItemId === item.id;
    const isThisSceneBusy = isCheckingThisScene || isGeneratingThisScene;

    const currentImageStyleKey = item.image_style || selectedImageStyle;
    const currentImageStyle = (props.mediaStyleList || []).find(s => s.value === currentImageStyleKey);
    const currentImageStylePrompt = item.image_style_prompt ?? (currentImageStyle?.prompt || "");
    const finalImagePrompt = item?.image_prompt || "";

    const currentVoiceName = item.voice_name || selectedVoiceName;
    // "" = Use Global Default (ไม่ส่ง key ให้หลังบ้าน)
    const currentImageModel = item.image_model || "";
    const useReferenceImages = sceneAllowsReferences(item, imageModelCatalog);
    const sceneReferenceCap = referenceCap(effectiveImageModel(item, imageModelCatalog), imageModelCatalog);
    const enabledImageModels = enabledModels(imageModelCatalog);
    // id ที่ item ถืออยู่แต่ไม่อยู่ใน list ที่เลือกได้ (ถูก disable ทีหลัง / legacy) — ต้อง render ไว้กัน MUI out-of-range
    const isOrphanImageModel =
      !!currentImageModel && !enabledImageModels.some((model) => model.id === currentImageModel);
    // ไม่มี catalog = ตอบไม่ได้ว่าโมเดลถูกปิดจริงไหม (gen ยังทำงานปกติ) → ไม่ติดป้าย
    const orphanSuffix = imageModelCatalog ? " (unavailable)" : "";
    const currentReferenceImages = item.reference_images || [];

    return (
      <SceneCard key={item.id} sx={isTextDisabled ? { opacity: 0.7, pointerEvents: "none" } : {}}>

        {/* Final Script | AI Image Prompt */}
        {!hasExternalVideo && (
          <>
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2 }}>
              <Box>
                <FormLabel sx={{ fontWeight: 700 }}>Voice</FormLabel>
                <Select
                  fullWidth
                  size="small"
                  value={currentVoiceName}
                  onChange={(e) => updateScriptItem(index, "voice_name", e.target.value)}
                  disabled={isTextDisabled}
                  sx={{ backgroundColor: "white", borderRadius: "0.5rem", mb: 1 }}
                >
                  {(props.voiceList || []).map((voice) => (
                    <MenuItem key={voice.name} value={voice.name}>
                      {voice.name} ({voice.gender}, {voice.tone})
                    </MenuItem>
                  ))}
                </Select>
                <FormLabel sx={{ fontWeight: 700 }}>
                  Audio Padding (sec)
                  <Typography component="span" sx={{ fontSize: "0.7rem", color: "#94a3b8", fontWeight: 400, ml: 1 }}>
                    {useGlobalPadding
                      ? "ใช้ค่า All Scenes อยู่ — ติ๊กออกที่ด้านล่างเพื่อกำหนดเอง"
                      : "ว่าง = 0"}
                  </Typography>
                </FormLabel>
                <Box id={`megen-scene-${item.id}-audio-padding`} sx={{ display: "flex", gap: 1, mb: 1 }}>
                  {(
                    [
                      { field: "padding_start", label: "Start (ก่อนพูด)", fallback: globalPaddingStart },
                      { field: "padding_end", label: "End (หลังพูด)", fallback: globalPaddingEnd },
                    ] as const
                  ).map(({ field, label, fallback }) => (
                    <TextField
                      key={field}
                      id={`megen-scene-${item.id}-${field}`}
                      size="small"
                      type="number"
                      label={label}
                      placeholder={useGlobalPadding ? String(parsePadding(fallback) ?? 0) : "0"}
                      InputLabelProps={{ shrink: true }}
                      value={useGlobalPadding ? "" : item[field] ?? ""}
                      onChange={(e) => updateScriptItem(index, field, parsePadding(e.target.value))}
                      disabled={isTextDisabled || useGlobalPadding}
                      inputProps={{ min: 0, max: AUDIO_PADDING_MAX, step: 0.1 }}
                      sx={{ flex: 1, backgroundColor: "white", "& .MuiOutlinedInput-root": { borderRadius: "0.5rem" } }}
                    />
                  ))}
                </Box>
                <FormLabel sx={{ fontWeight: 700 }}>Voice Guide</FormLabel>
                <StyledTextField fullWidth multiline rows={4} value={item.voice_guide ?? ""}
                  onChange={(e) => updateScriptItem(index, "voice_guide", e.target.value)} disabled={isTextDisabled}
                  placeholder="Style: ...\nTone: ..."
                  sx={{ "& .MuiOutlinedInput-root": { padding: "4px 8px" }, "& .MuiInputBase-input": { padding: "2px 4px" }, mb: 1 }} />
                <FormLabel sx={{ fontWeight: 700 }}>Voice Script</FormLabel>
                <StyledTextField fullWidth multiline rows={6} value={item.voice_script ?? ""}
                  onChange={(e) => updateScriptItem(index, "voice_script", e.target.value)} disabled={isTextDisabled}
                  sx={{ "& .MuiOutlinedInput-root": { padding: "4px 8px" }, "& .MuiInputBase-input": { padding: "2px 4px" } }} />
              </Box>
              <Box>
                <FormLabel sx={{ fontWeight: 700 }}>Image Model</FormLabel>
                <Select
                  fullWidth
                  size="small"
                  displayEmpty
                  value={currentImageModel}
                  onChange={(e) => updateScriptItem(index, "image_model", e.target.value)}
                  disabled={isTextDisabled}
                  sx={{ backgroundColor: "white", borderRadius: "0.5rem", mb: 1 }}
                >
                  <MenuItem value="">{defaultLabel(imageModelCatalog)}</MenuItem>
                  {enabledImageModels.map((model) => (
                    <MenuItem key={model.id} value={model.id}>
                      {model.label}
                    </MenuItem>
                  ))}
                  {isOrphanImageModel && (
                    <MenuItem key={currentImageModel} value={currentImageModel}>
                      {`${findImageModel(currentImageModel, imageModelCatalog)?.label || currentImageModel}${orphanSuffix}`}
                    </MenuItem>
                  )}
                </Select>
                <FormLabel sx={{ fontWeight: 700 }}>Scene Description</FormLabel>
                <StyledTextField fullWidth multiline rows={6} value={item.scene_description || ""}
                  onChange={(e) => updateScriptItem(index, "scene_description", e.target.value)} disabled={isTextDisabled}
                  sx={{ "& .MuiOutlinedInput-root": { padding: "4px 8px" }, "& .MuiInputBase-input": { padding: "2px 4px" } }} />
              </Box>
            </Box>

            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2, mt: 2 }}>
              <Box>
                <FormLabel sx={{ fontWeight: 700 }}>Image Style</FormLabel>
                <Select
                  fullWidth
                  size="small"
                  value={currentImageStyleKey}
                  onChange={(e) => {
                    const newStyleKey = e.target.value as string;
                    const selectedStyle = props.mediaStyleList?.find(s => s.value === newStyleKey);
                    updateScriptItem(index, "image_style", newStyleKey);
                    if (selectedStyle) {
                      updateScriptItem(index, "image_style_prompt", selectedStyle.prompt);
                    }
                  }}
                  disabled={isTextDisabled}
                  sx={{ backgroundColor: "white", borderRadius: "0.5rem", mb: 1 }}
                >
                  {props.mediaStyleList?.map((style) => (
                    <MenuItem key={style.value} value={style.value}>
                      {style.text}
                    </MenuItem>
                  ))}
                </Select>
                <StyledTextField
                  fullWidth multiline rows={6}
                  value={currentImageStylePrompt}
                  onChange={(e) => updateScriptItem(index, "image_style_prompt", e.target.value)}
                  disabled={isTextDisabled}
                  sx={{
                    "& .MuiOutlinedInput-root": { padding: "4px 8px" },
                    "& .MuiInputBase-input": { padding: "2px 4px" }
                  }}
                />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                {useReferenceImages && (
                  /* Reference-to-Image (Image Editing) — เปิดเมื่อโมเดลที่จะใช้จริงมีโหมด edit/ref ตาม catalog */
                  <Box sx={{ mb: 1.5 }}>
                    <ImageAssetsPanel
                      assets={imageAssets}
                      selected={currentReferenceImages}
                      onToggle={(filename) => handleToggleReferenceImage(index, filename)}
                      onUpload={handleUploadImageAssetFile}
                      onDelete={handleRequestDeleteAsset}
                      deletingFilename={deletingAssetFilename}
                      buildImageUrl={(url) => getOutputStaticUrl(url) || ""}
                      isLoading={isLoadingAssets}
                      isUploading={isUploadingAsset}
                      disabled={isTextDisabled}
                      maxSelected={sceneReferenceCap}
                    />
                  </Box>
                )}
                {/* read-only — ที่เดียวที่เห็น image_prompt ของ scene จึงต้องโชว์เสมอ */}
                <FormLabel sx={{ fontWeight: 700 }}>Final Image Prompt</FormLabel>
                <StyledTextField
                  fullWidth multiline rows={useReferenceImages ? 4 : 8.3}
                  value={finalImagePrompt}
                  disabled
                  sx={{
                    "& .MuiOutlinedInput-root": { padding: "4px 8px", backgroundColor: "#f8fafc" },
                    "& .MuiInputBase-input": { padding: "2px 4px", color: "#64748b" }
                  }}
                />
              </Box>
            </Box>
          </>
        )}

        {/* Settings */}
        {!hasExternalVideo && (
          <CardMeGenSettingsSection
            seriesId={selectedSeriesId || 0}
            item={item}
            index={index}
            outputItem={outputItem}
            isDisabled={isDisabled}
            isInputDisabled={isTextDisabled}
            updateScriptItem={updateScriptItem}
            videoModelCatalog={videoModelCatalog}
            onAnimPromptChange={() => setLocalUseAnimated((prev) => ({ ...prev, [item.id]: true }))}
            // Character props
            characterList={props.characterList || []}
            isCharacterGenerating={props.isCharacterGenerating || false}
            onGenerateCharacter={(payload: GenerateCharacterPayload) => {
              if (!selectedSeriesId) return;
              props.onGenerateCharacter?.({ ...payload, series_id: selectedSeriesId });
            }}
            onUploadCharacter={(payload: UploadCharacterPayload) => {
              if (!selectedSeriesId) return;
              props.onUploadCharacter?.({ ...payload, series_id: selectedSeriesId });
            }}
            onDeleteCharacter={(characterId) => {
              if (!selectedSeriesId) return;
              props.onDeleteCharacter?.(characterId, selectedSeriesId);
            }}
          />
        )}

        {/* ฉากจากลิงก์ภายนอก: ช่องตั้งค่า Voice…Video Model ถูกซ่อนเพราะไม่มีผลกับผลลัพธ์
            จึงต้องบอกเหตุผลไว้ตรงนี้ ไม่งั้นผู้ใช้จะเห็นเป็นฟอร์มหายไปเฉย ๆ */}
        {hasExternalVideo && (
          <Box
            id="megen-gen-direct-external-video-note"
            sx={{ p: 1.5, borderRadius: "0.5rem", backgroundColor: "#f8fafc", border: "1px solid #e2e8f0" }}
          >
            <Typography sx={{ fontSize: "0.8rem", fontWeight: 600, color: "#1e293b" }}>
              ฉากนี้ใช้วิดีโอจากลิงก์ภายนอก
            </Typography>
            <Typography sx={{ fontSize: "0.75rem", color: "#64748b" }}>
              ภาพและเสียงของฉากมาจากคลิปนี้ จึงไม่มีการตั้งค่า Voice / Image / Video Model
            </Typography>
            <Typography
              component="a"
              href={externalVideoLink}
              target="_blank"
              rel="noreferrer"
              sx={{
                display: "block",
                fontSize: "0.75rem",
                color: "#db2777",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {externalVideoLink}
            </Typography>
          </Box>
        )}

        {/* Text Overlays */}
        <Box sx={{ mt: 2, pt: 2, borderTop: "1px solid #f1f5f9" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}>
            <Checkbox
              size="small"
              checked={(item.text_overlays?.length || 0) > 0}
              onChange={(e) => {
                if (e.target.checked) {
                  updateScriptItem(index, "text_overlays", [{
                    id: Date.now().toString(), text: "",
                    style: { fontSize: 64, color: "#FFFFFF", font: "Sarabun" },
                  }]);
                } else {
                  if (!window.confirm("ต้องการลบ Text Overlays ทั้งหมดหรือไม่?")) return;
                  updateScriptItem(index, "text_overlays", []);
                }
              }}
              sx={{ "&.Mui-checked": { color: "#db2777" } }}
              disabled={isTextDisabled}
            />
            <FormLabel sx={{ color: "#db2777", fontWeight: 700, mb: 0 }}>Text Overlays</FormLabel>
            <Typography sx={{ fontSize: "0.75rem", color: "#94a3b8", ml: 0.5 }}>
              {(item.text_overlays?.length || 0) > 0 ? `(${item.text_overlays!.length} overlay)` : "(click to add)"}
            </Typography>
          </Box>
          {(item.text_overlays?.length || 0) > 0 && (
            <>
              <TextOverlaysTooltip>
                <Typography sx={{ fontSize: "0.8125rem", color: "#be185d", fontWeight: 500 }}>
                  • ทุกครั้งที่ทำการแก้ไขข้อความ จะ Auto-save ให้ทุกครั้ง
                </Typography>
                <Typography sx={{ fontSize: "0.8125rem", color: "#be185d", fontWeight: 500 }}>
                  • ต้องการดูวิดีโอ หรือ Scene Preview ที่มี Text Overlay ให้คลิก "สร้างวิดีโอ Review" ทุกครั้ง
                </Typography>
              </TextOverlaysTooltip>
              <TextOverlayEditor overlays={item.text_overlays || []}
                onChange={(overlays) => updateScriptItem(index, "text_overlays", overlays)} />
            </>
          )}
        </Box>

        {/* Image Overlays */}
        <Box sx={{ mt: 2, pt: 2, borderTop: "1px solid #f1f5f9" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}>
            <Checkbox
              size="small"
              checked={(item.image_overlays?.length || 0) > 0}
              onChange={(e) => {
                if (e.target.checked) {
                  updateScriptItem(index, "image_overlays", [{
                    id: Date.now().toString(),
                    url: "",
                    position: "bottom-right",
                    animation: "none",
                    start: 0,
                    duration: 0,
                  }]);
                } else {
                  if (!window.confirm("ต้องการลบ Image Overlays ทั้งหมดหรือไม่?")) return;
                  updateScriptItem(index, "image_overlays", []);
                }
              }}
              sx={{ "&.Mui-checked": { color: "#db2777" } }}
              disabled={isTextDisabled}
            />
            <FormLabel sx={{ color: "#db2777", fontWeight: 700, mb: 0 }}>Image Overlays</FormLabel>
            <Typography sx={{ fontSize: "0.75rem", color: "#94a3b8", ml: 0.5 }}>
              {(item.image_overlays?.length || 0) > 0 ? `(${item.image_overlays!.length} overlay)` : "(click to add)"}
            </Typography>
          </Box>
          {(item.image_overlays?.length || 0) > 0 && (
            <>
              <TextOverlaysTooltip>
                <Typography sx={{ fontSize: "0.8125rem", color: "#be185d", fontWeight: 500 }}>
                  • ทุกครั้งที่ทำการแก้ไขรูปภาพ จะ Auto-save ให้ทุกครั้ง
                </Typography>
                <Typography sx={{ fontSize: "0.8125rem", color: "#be185d", fontWeight: 500 }}>
                  • ต้องการดูวิดีโอ หรือ Scene Preview ที่มี Image Overlay ให้คลิก "สร้างวิดีโอ Review" ทุกครั้ง
                </Typography>
              </TextOverlaysTooltip>
              <ImageOverlayEditor
                overlays={item.image_overlays || []}
                onChange={(overlays) => updateScriptItem(index, "image_overlays", overlays)}
                onUploadFile={handleUploadOverlayFile}
                buildImageUrl={(url) => getOutputStaticUrl(url) || ""}
                disabled={isTextDisabled}
              />
            </>
          )}
        </Box>

        {/* Ken Burns Effect */}
        <Box sx={{ mt: 2, pl: 1, pt: 2, borderTop: "1px solid #f1f5f9" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
            <FormLabel sx={{ flexShrink: 0, color: "#db2777", fontWeight: 700, mb: 0, whiteSpace: "nowrap" }}>
              Ken Burns Effect
            </FormLabel>

            {item.is_uploaded || hasExternalVideo ? (
              <Typography sx={{ fontSize: "0.75rem", color: "#94a3b8" }}>
                ไม่รองรับเมื่อใช้ Uploaded Video / External Video Link
              </Typography>
            ) : (
              <Select
                size="small"
                value={item.ken_burns_direction || "off"}
                onChange={(e) => updateScriptItem(index, "ken_burns_direction", e.target.value)}
                disabled={isTextDisabled}
                MenuProps={{
                  disablePortal: true,
                }}
                sx={{
                  flex: 1,
                  borderRadius: "0.5rem",
                  fontSize: "0.8rem",
                  backgroundColor: "#f8fafc",
                  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e2e8f0" },
                  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#f9a8d4" },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#f382a0" },
                  "& .MuiSelect-icon": { color: "#f382a0" },
                }}
              >
                {KEN_BURNS_EFFECTS.map((effect) => (
                  <MenuItem key={effect.key} value={effect.value}>
                    {effect.text}
                  </MenuItem>
                ))}
              </Select>
            )}
          </Box>
        </Box>

        {/* PREVIEW divider */}
        {hasAnyPreview && (
          <Box sx={{ mt: 3, mb: 2, display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box sx={{ flex: 1, height: "1px", backgroundColor: "#e2e8f0" }} />
            <Typography sx={{ fontSize: "0.75rem", fontWeight: 700, color: "#94a3b8", letterSpacing: "0.08em", textTransform: "uppercase" }}>
              Preview
            </Typography>
            <Box sx={{ flex: 1, height: "1px", backgroundColor: "#e2e8f0" }} />
          </Box>
        )}

        {/* Generated Audio */}
        {outputItem?.audio_url && (
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <FormLabel sx={{ color: "#db2777", fontWeight: 700, mb: 0 }}>Generated Audio</FormLabel>
                {renderVersionBadge(outputItem?.audio_url, versionMap[item.id]?.["audio"]?.current)}
              </Box>
              {!isSelectingVersion(item.id, "audio") ? (
                <SuccessButton size="small" disabled={isDisabled} onClick={() => handleOpenSelectVersion(item.id, "audio")}
                  sx={{ fontSize: "0.7rem", py: 0.25, px: 1.5 }}>
                  Version
                </SuccessButton>
              ) : (
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Button size="small" variant="contained" onClick={() => handleSaveVersion(item.id, "audio")}
                    sx={{ textTransform: "none", fontSize: "0.7rem", borderRadius: "9999px", py: 0.25, px: 1.5, backgroundColor: "#db2777", "&:hover": { backgroundColor: "#be185d" } }}>
                    Save
                  </Button>
                  <IconButton size="small" onClick={() => setSelectingVersionKey(null)} sx={{ color: "#94a3b8", padding: "2px" }}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              )}
            </Box>
            <audio key={`${item.id}-audio-${getAssetVersionKey(outputItem.audio_url || item.audio_path) || "none"}`} controls src={buildOutputUrl(outputItem.audio_url)} style={{ width: "100%", height: 32 }} />
            {isSelectingVersion(item.id, "audio") && (
              <Box sx={{ mt: 1, border: "1px solid #f9a8d4", borderRadius: "0.5rem", backgroundColor: "#fdf2f8", overflow: "hidden" }}>
                {!versionMap[item.id]?.["audio"] ? (
                  <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}><CircularProgress size={20} sx={{ color: "#db2777" }} /></Box>
                ) : versionMap[item.id]["audio"].versions.map((v) => {
                  const isSelected = (pendingVersionMap[item.id]?.["audio"] ?? versionMap[item.id]["audio"].current) === v.version;
                  const isCurrent = versionMap[item.id]["audio"].current === v.version;
                  return (
                    <Box key={v.version} onClick={() => setPendingVersionMap((prev) => ({ ...prev, [item.id]: { ...prev[item.id], audio: v.version } }))}
                      sx={{ display: "flex", flexDirection: "column", gap: 0.5, px: 1.5, py: 1, cursor: "pointer", borderBottom: "1px solid #fce7f3", backgroundColor: isSelected ? "#fce7f3" : "transparent", "&:hover": { backgroundColor: "#fce7f3" } }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Box sx={{ width: 16, height: 16, borderRadius: "50%", border: `2px solid ${isSelected ? "#db2777" : "#cbd5e1"}`, backgroundColor: isSelected ? "#db2777" : "transparent", flexShrink: 0 }} />
                        <Typography sx={{ fontSize: "0.8125rem", fontWeight: isSelected ? 700 : 500, color: "#1e293b" }}>Version {v.version}{v.meta?.voice ? `: ${v.meta.voice}` : ""}</Typography>
                        {isCurrent && <Box sx={{ fontSize: "0.65rem", fontWeight: 700, color: "#db2777", backgroundColor: "#fce7f3", border: "1px solid #f9a8d4", borderRadius: "9999px", px: 0.75, py: 0.125, lineHeight: 1.5 }}>Current</Box>}
                      </Box>
                      <audio controls src={buildUrl(v.path)} style={{ width: "100%", height: 28 }} onClick={(e) => e.stopPropagation()} />
                    </Box>
                  );
                })}
              </Box>
            )}
          </Box>
        )}

        {/* Media Type — แสดงตลอด (เลือก Video ได้แม้ยังไม่มีวิดีโอ) */}
        <Box sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
          <FormLabel sx={{ mb: 0, fontWeight: 600, color: "#db2777", whiteSpace: "nowrap" }}>Media Type</FormLabel>
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={!useAnimation}
                onChange={() => setLocalUseAnimated((prev) => ({ ...prev, [item.id]: false }))}
                sx={{ "&.Mui-checked": { color: "#e91e63" }, p: 0.5 }}
                disabled={isTextDisabled}
              />
            }
            label={<Typography sx={{ fontSize: "0.8125rem" }}>Image</Typography>}
            sx={{ m: 0 }}
          />
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={useAnimation}
                onChange={() => setLocalUseAnimated((prev) => ({ ...prev, [item.id]: true }))}
                sx={{ "&.Mui-checked": { color: "#e91e63" }, p: 0.5 }}
                disabled={isTextDisabled}
              />
            }
            label={<Typography sx={{ fontSize: "0.8125rem" }}>Video</Typography>}
            sx={{ m: 0 }}
          />
        </Box>

        {/* Visual Preview | Scene Preview */}
        {(hasImageAsset || hasVideoAsset || hasUploadedVideo || hasUploadedImage || !!outputItem?.segment_video_url || useAnimation || !useAnimation) && (
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2 }}>

            {/* LEFT: Visual — ตาม Media Type */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>

              {/* === IMAGE MODE (default หรือ useAnimation = false) === */}
              {!useAnimation && (
                <>
                  {/* Generated Image — ซ่อนเมื่อมี uploaded image */}
                  {!item.uploaded_image_url && hasImageAsset && (
                    <Box>
                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <FormLabel sx={{ color: "#db2777", fontWeight: 700, mb: 0 }}>Generated Image</FormLabel>
                          {renderVersionBadge(outputItem?.image_url, versionMap[item.id]?.["image"]?.current)}
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>

                          {/* Upload My Image Checkbox */}
                          {renderUploadImageButton(item, index, isDisabled, uploadingImageItemId, handleUploadImageFile, updateScriptItem)}

                          {/* Select Image Version */}
                          {!isSelectingVersion(item.id, "image") ? (
                            <SuccessButton size="small" disabled={isDisabled} onClick={() => handleOpenSelectVersion(item.id, "image")}
                              sx={{ fontSize: "0.7rem", py: 0.25, px: 1.5 }}>
                              Version
                            </SuccessButton>
                          ) : (
                            <Box sx={{ display: "flex", gap: 1 }}>
                              <Button size="small" variant="contained" onClick={() => handleSaveVersion(item.id, "image")}
                                sx={{ textTransform: "none", fontSize: "0.7rem", borderRadius: "9999px", py: 0.25, px: 1.5, backgroundColor: "#db2777", "&:hover": { backgroundColor: "#be185d" } }}>Save</Button>
                              <IconButton size="small" onClick={() => setSelectingVersionKey(null)} sx={{ color: "#94a3b8", padding: "2px" }}><CloseIcon fontSize="small" /></IconButton>
                            </Box>
                          )}
                        </Box>
                      </Box>
                      <img key={`${item.id}-image-${getAssetVersionKey(outputItem!.uploaded_image_url || outputItem!.image_url || item.image_path) || "none"}`}
                        src={buildOutputUrl(outputItem!.uploaded_image_url || outputItem!.image_url)}
                        alt="Generated Image"
                        style={{ width: "100%", borderRadius: "0.5rem", boxShadow: "0 1px 3px rgba(0,0,0,0.1)", display: "block", margin: "0 auto", ...(aspectRatio === "9:16" ? { maxWidth: "300px" } : {}) }} />
                      {isSelectingVersion(item.id, "image") && (
                        <Box sx={{ mt: 1, border: "1px solid #f9a8d4", borderRadius: "0.5rem", backgroundColor: "#fdf2f8", overflow: "hidden" }}>
                          {!versionMap[item.id]?.["image"] ? (
                            <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}><CircularProgress size={20} sx={{ color: "#db2777" }} /></Box>
                          ) : versionMap[item.id]["image"].versions.map((v) => {
                            const isSelected = (pendingVersionMap[item.id]?.["image"] ?? versionMap[item.id]["image"].current) === v.version;
                            const isCurrent = versionMap[item.id]["image"].current === v.version;
                            return (
                              <Box key={v.version} onClick={() => setPendingVersionMap((prev) => ({ ...prev, [item.id]: { ...prev[item.id], image: v.version } }))}
                                sx={{ display: "flex", flexDirection: "column", gap: 1, px: 1.5, py: 1, cursor: "pointer", borderBottom: "1px solid #fce7f3", backgroundColor: isSelected ? "#fce7f3" : "transparent", "&:hover": { backgroundColor: "#fce7f3" } }}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                  <Box sx={{ width: 16, height: 16, borderRadius: "50%", border: `2px solid ${isSelected ? "#db2777" : "#cbd5e1"}`, backgroundColor: isSelected ? "#db2777" : "transparent", flexShrink: 0 }} />
                                  <Typography sx={{ fontSize: "0.8125rem", fontWeight: isSelected ? 700 : 500, color: "#1e293b" }}>Version {v.version}</Typography>
                                  {isCurrent && <Box sx={{ fontSize: "0.65rem", fontWeight: 700, color: "#db2777", backgroundColor: "#fce7f3", border: "1px solid #f9a8d4", borderRadius: "9999px", px: 0.75, lineHeight: 1.5 }}>Current</Box>}
                                </Box>
                                <img src={buildUrl(v.path)} alt={`Version ${v.version}`}
                                  style={{ width: "100%", borderRadius: "0.375rem", border: isSelected ? "2px solid #db2777" : "2px solid transparent", pointerEvents: "none" }}
                                  onClick={(e) => e.stopPropagation()} />
                              </Box>
                            );
                          })}
                        </Box>
                      )}
                    </Box>
                  )}

                  {/* Empty state — เลือก Image แต่ยังไม่มีรูปภาพใด ๆ */}
                  {!hasImageAsset && !hasUploadedImage && (
                    <Box>
                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5 }}>
                        <FormLabel sx={{ color: "#db2777", fontWeight: 700, mb: 0 }}>Generated Image</FormLabel>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          {renderUploadImageButton(item, index, isDisabled, uploadingImageItemId, handleUploadImageFile, updateScriptItem)}
                        </Box>
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 0.75,
                          py: 11.3,
                          px: 2,
                          borderRadius: "0.5rem",
                          border: "1.5px dashed #ffa9c6",
                          backgroundColor: "#fff5f9",
                        }}
                      >
                        <Typography sx={{ fontSize: "0.875rem", color: "#e91e63", fontWeight: 600 }}>
                          ยังไม่มี Image ใน Scene นี้
                        </Typography>
                        <Typography sx={{ fontSize: "0.75rem", color: "#94a3b8", textAlign: "center" }}>
                          อัปโหลดรูปภาพของคุณเองได้เลย
                        </Typography>
                      </Box>
                    </Box>
                  )}

                  {/* Uploaded Image preview */}
                  {hasUploadedImage && (
                    <Box>
                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <FormLabel sx={{ color: "#e91e63", fontWeight: 700, mb: 0 }}>Uploaded Image</FormLabel>
                          {hasImageAsset && renderVersionBadge(outputItem?.image_url, versionMap[item.id]?.["image"]?.current)}
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          {/* เปลี่ยนรูปภาพ (อัปโหลดทับ) */}
                          {renderUploadImageButton(item, index, isDisabled, uploadingImageItemId, handleUploadImageFile, updateScriptItem)}

                          {/* Select Image Version (ถ้ามี Generated Image) */}
                          {hasImageAsset && (
                            !isSelectingVersion(item.id, "image") ? (
                              <SuccessButton size="small" disabled={isDisabled} onClick={() => handleOpenSelectVersion(item.id, "image")}
                                sx={{ fontSize: "0.7rem", py: 0.25, px: 1.5 }}>
                                Version
                              </SuccessButton>
                            ) : (
                              <Box sx={{ display: "flex", gap: 1 }}>
                                <Button size="small" variant="contained" onClick={() => handleSaveVersion(item.id, "image")}
                                  sx={{ textTransform: "none", fontSize: "0.7rem", borderRadius: "9999px", py: 0.25, px: 1.5, backgroundColor: "#db2777", "&:hover": { backgroundColor: "#be185d" } }}>Save</Button>
                                <IconButton size="small" onClick={() => setSelectingVersionKey(null)} sx={{ color: "#94a3b8", padding: "2px" }}><CloseIcon fontSize="small" /></IconButton>
                              </Box>
                            )
                          )}

                          {/* ลบรูปภาพที่อัปโหลด */}
                          <Button
                            size="small"
                            onClick={() => handleRemoveUploadedImage(index)}
                            disabled={isDisabled || uploadingImageItemId === item.id}
                            sx={{
                              textTransform: "none",
                              fontSize: "0.6rem",
                              fontWeight: 600,
                              color: "#dc2626",
                              border: "1px solid #fecaca",
                              backgroundColor: "#fef2f2",
                              borderRadius: "9999px",
                              py: 0.375,
                              px: 1.5,
                              "&:hover": { backgroundColor: "#fee2e2", borderColor: "#dc2626" },
                            }}
                          >
                            ลบรูปภาพ
                          </Button>
                        </Box>
                      </Box>
                      <img src={getUploadedStaticUrl(item.uploaded_image_url) || ""} alt="My Image"
                        style={{ width: "100%", borderRadius: "0.5rem", border: "2px solid #ffa9c6" }} />
                      {hasImageAsset && isSelectingVersion(item.id, "image") && (
                        <Box sx={{ mt: 1, border: "1px solid #f9a8d4", borderRadius: "0.5rem", backgroundColor: "#fdf2f8", overflow: "hidden" }}>
                          {!versionMap[item.id]?.["image"] ? (
                            <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}><CircularProgress size={20} sx={{ color: "#db2777" }} /></Box>
                          ) : versionMap[item.id]["image"].versions.map((v) => {
                            const isSelected = (pendingVersionMap[item.id]?.["image"] ?? versionMap[item.id]["image"].current) === v.version;
                            const isCurrent = versionMap[item.id]["image"].current === v.version;
                            return (
                              <Box key={v.version} onClick={() => setPendingVersionMap((prev) => ({ ...prev, [item.id]: { ...prev[item.id], image: v.version } }))}
                                sx={{ display: "flex", flexDirection: "column", gap: 1, px: 1.5, py: 1, cursor: "pointer", borderBottom: "1px solid #fce7f3", backgroundColor: isSelected ? "#fce7f3" : "transparent", "&:hover": { backgroundColor: "#fce7f3" } }}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                  <Box sx={{ width: 16, height: 16, borderRadius: "50%", border: `2px solid ${isSelected ? "#db2777" : "#cbd5e1"}`, backgroundColor: isSelected ? "#db2777" : "transparent", flexShrink: 0 }} />
                                  <Typography sx={{ fontSize: "0.8125rem", fontWeight: isSelected ? 700 : 500, color: "#1e293b" }}>Version {v.version}</Typography>
                                  {isCurrent && <Box sx={{ fontSize: "0.65rem", fontWeight: 700, color: "#db2777", backgroundColor: "#fce7f3", border: "1px solid #f9a8d4", borderRadius: "9999px", px: 0.75, lineHeight: 1.5 }}>Current</Box>}
                                </Box>
                                <img src={buildUrl(v.path)} alt={`Version ${v.version}`}
                                  style={{ width: "100%", borderRadius: "0.375rem", border: isSelected ? "2px solid #db2777" : "2px solid transparent", pointerEvents: "none" }}
                                  onClick={(e) => e.stopPropagation()} />
                              </Box>
                            );
                          })}
                        </Box>
                      )}
                    </Box>
                  )}
                </>
              )}

              {/* === VIDEO MODE (useAnimation = true) === */}
              {useAnimation && (
                <>
                  {/* Generated Video — ซ่อนเมื่อมี uploaded video */}
                  {!item.uploaded_video_url && hasVideoAsset && (
                    <Box>
                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <FormLabel sx={{ color: "#db2777", fontWeight: 700, mb: 0 }}>Generated Video</FormLabel>
                          {renderVersionBadge(outputItem?.ai_video_url, versionMap[item.id]?.["video"]?.current)}
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          {/* Upload My Video Checkbox */}
                          {renderUploadVideoButton(item, index, isDisabled, isItemUploading, handleUploadVideoFile, updateScriptItem)}

                          {/* Select Video Version */}
                          {!isSelectingVersion(item.id, "video") ? (
                            <SuccessButton size="small" disabled={isDisabled} onClick={() => handleOpenSelectVersion(item.id, "video")}
                              sx={{ fontSize: "0.7rem", py: 0.25, px: 1.5 }}>
                              Version
                            </SuccessButton>
                          ) : (
                            <Box sx={{ display: "flex", gap: 1 }}>
                              <Button size="small" variant="contained" onClick={() => handleSaveVersion(item.id, "video")}
                                sx={{ textTransform: "none", fontSize: "0.7rem", borderRadius: "9999px", py: 0.25, px: 1.5, backgroundColor: "#db2777", "&:hover": { backgroundColor: "#be185d" } }}>Save</Button>
                              <IconButton size="small" onClick={() => setSelectingVersionKey(null)} sx={{ color: "#94a3b8", padding: "2px" }}><CloseIcon fontSize="small" /></IconButton>
                            </Box>
                          )}
                        </Box>
                      </Box>
                      <Box key={`${item.id}-video-${mediaVersion}`} component="video" controls
                        src={buildOutputUrl(outputItem!.uploaded_video_url || outputItem!.ai_video_url)}
                        sx={{ width: "100%", borderRadius: "0.5rem" }} />
                      {isSelectingVersion(item.id, "video") && (
                        <Box sx={{ mt: 1, border: "1px solid #f9a8d4", borderRadius: "0.5rem", backgroundColor: "#fdf2f8", overflow: "hidden" }}>
                          {!versionMap[item.id]?.["video"] ? (
                            <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}><CircularProgress size={20} sx={{ color: "#db2777" }} /></Box>
                          ) : versionMap[item.id]["video"].versions.map((v) => {
                            const isSelected = (pendingVersionMap[item.id]?.["video"] ?? versionMap[item.id]["video"].current) === v.version;
                            const isCurrent = versionMap[item.id]["video"].current === v.version;
                            return (
                              <Box key={v.version} onClick={() => setPendingVersionMap((prev) => ({ ...prev, [item.id]: { ...prev[item.id], video: v.version } }))}
                                sx={{ display: "flex", flexDirection: "column", gap: 1, px: 1.5, py: 1, cursor: "pointer", borderBottom: "1px solid #fce7f3", backgroundColor: isSelected ? "#fce7f3" : "transparent", "&:hover": { backgroundColor: "#fce7f3" } }}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                  <Box sx={{ width: 16, height: 16, borderRadius: "50%", border: `2px solid ${isSelected ? "#db2777" : "#cbd5e1"}`, backgroundColor: isSelected ? "#db2777" : "transparent", flexShrink: 0 }} />
                                  <Typography sx={{ fontSize: "0.8125rem", fontWeight: isSelected ? 700 : 500, color: "#1e293b" }}>Version {v.version}</Typography>
                                  {isCurrent && <Box sx={{ fontSize: "0.65rem", fontWeight: 700, color: "#db2777", backgroundColor: "#fce7f3", border: "1px solid #f9a8d4", borderRadius: "9999px", px: 0.75, lineHeight: 1.5 }}>Current</Box>}
                                </Box>
                                <Box component="video" controls src={buildUrl(v.path)} onClick={(e: React.MouseEvent) => e.stopPropagation()}
                                  sx={{ width: "100%", borderRadius: "0.375rem", border: isSelected ? "2px solid #db2777" : "2px solid transparent" }} />
                              </Box>
                            );
                          })}
                        </Box>
                      )}
                    </Box>
                  )}

                  {/* Empty state — เลือก Video แต่ยังไม่มีวิดีโอใด ๆ */}
                  {!hasVideoAsset && !hasUploadedVideo && (
                    <Box>
                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <FormLabel sx={{ color: "#db2777", fontWeight: 700, mb: 0 }}>Generated Video</FormLabel>
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          {renderUploadVideoButton(item, index, isDisabled, isItemUploading, handleUploadVideoFile, updateScriptItem)}
                        </Box>
                      </Box>

                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 0.75,
                          py: 11.3,
                          px: 2,
                          borderRadius: "0.5rem",
                          border: "1.5px dashed #ffa9c6",
                          backgroundColor: "#fff5f9",
                        }}
                      >
                        <Typography sx={{ fontSize: "0.875rem", color: "#e91e63", fontWeight: 600 }}>
                          ยังไม่มี Video ใน Scene นี้
                        </Typography>
                        <Typography sx={{ fontSize: "0.75rem", color: "#94a3b8", textAlign: "center" }}>
                          อัปโหลดวิดีโอของคุณเองได้เลย
                        </Typography>
                      </Box>
                    </Box>
                  )}

                  {/* Uploaded Video preview */}
                  {hasUploadedVideo && (
                    <Box>
                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <FormLabel sx={{ color: "#e91e63", fontWeight: 700, mb: 0 }}>Uploaded Video</FormLabel>
                          {hasVideoAsset && renderVersionBadge(outputItem?.ai_video_url, versionMap[item.id]?.["video"]?.current)}
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          {/* เปลี่ยนวิดีโอ (อัปโหลดทับ) */}
                          {renderUploadVideoButton(item, index, isDisabled, isItemUploading, handleUploadVideoFile, updateScriptItem)}

                          {/* Select Video Version (ถ้ามี Generated Video) */}
                          {hasVideoAsset && (
                            !isSelectingVersion(item.id, "video") ? (
                              <SuccessButton size="small" disabled={isDisabled} onClick={() => handleOpenSelectVersion(item.id, "video")}
                                sx={{ fontSize: "0.7rem", py: 0.25, px: 1.5 }}>
                                Version
                              </SuccessButton>
                            ) : (
                              <Box sx={{ display: "flex", gap: 1 }}>
                                <Button size="small" variant="contained" onClick={() => handleSaveVersion(item.id, "video")}
                                  sx={{ textTransform: "none", fontSize: "0.7rem", borderRadius: "9999px", py: 0.25, px: 1.5, backgroundColor: "#db2777", "&:hover": { backgroundColor: "#be185d" } }}>Save</Button>
                                <IconButton size="small" onClick={() => setSelectingVersionKey(null)} sx={{ color: "#94a3b8", padding: "2px" }}><CloseIcon fontSize="small" /></IconButton>
                              </Box>
                            )
                          )}

                          {/* ลบวิดีโอที่อัปโหลด */}
                          <Button
                            size="small"
                            onClick={() => handleRemoveUploadedVideo(index)}
                            disabled={isDisabled || isItemUploading}
                            sx={{
                              textTransform: "none",
                              fontSize: "0.6rem",
                              fontWeight: 600,
                              color: "#dc2626",
                              border: "1px solid #fecaca",
                              backgroundColor: "#fef2f2",
                              borderRadius: "9999px",
                              py: 0.375,
                              px: 1.5,
                              "&:hover": { backgroundColor: "#fee2e2", borderColor: "#dc2626" },
                            }}
                          >
                            ลบวิดีโอ
                          </Button>
                        </Box>
                      </Box>
                      <Box component="video" controls src={getUploadedStaticUrl(item.uploaded_video_url) || ""}
                        sx={{ width: "100%", borderRadius: "0.5rem", border: "2px solid #ffa9c6" }} />
                      {hasVideoAsset && isSelectingVersion(item.id, "video") && (
                        <Box sx={{ mt: 1, border: "1px solid #f9a8d4", borderRadius: "0.5rem", backgroundColor: "#fdf2f8", overflow: "hidden" }}>
                          {!versionMap[item.id]?.["video"] ? (
                            <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}><CircularProgress size={20} sx={{ color: "#db2777" }} /></Box>
                          ) : versionMap[item.id]["video"].versions.map((v) => {
                            const isSelected = (pendingVersionMap[item.id]?.["video"] ?? versionMap[item.id]["video"].current) === v.version;
                            const isCurrent = versionMap[item.id]["video"].current === v.version;
                            return (
                              <Box key={v.version} onClick={() => setPendingVersionMap((prev) => ({ ...prev, [item.id]: { ...prev[item.id], video: v.version } }))}
                                sx={{ display: "flex", flexDirection: "column", gap: 1, px: 1.5, py: 1, cursor: "pointer", borderBottom: "1px solid #fce7f3", backgroundColor: isSelected ? "#fce7f3" : "transparent", "&:hover": { backgroundColor: "#fce7f3" } }}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                  <Box sx={{ width: 16, height: 16, borderRadius: "50%", border: `2px solid ${isSelected ? "#db2777" : "#cbd5e1"}`, backgroundColor: isSelected ? "#db2777" : "transparent", flexShrink: 0 }} />
                                  <Typography sx={{ fontSize: "0.8125rem", fontWeight: isSelected ? 700 : 500, color: "#1e293b" }}>Version {v.version}</Typography>
                                  {isCurrent && <Box sx={{ fontSize: "0.65rem", fontWeight: 700, color: "#db2777", backgroundColor: "#fce7f3", border: "1px solid #f9a8d4", borderRadius: "9999px", px: 0.75, lineHeight: 1.5 }}>Current</Box>}
                                </Box>
                                <Box component="video" controls src={buildUrl(v.path)} onClick={(e: React.MouseEvent) => e.stopPropagation()}
                                  sx={{ width: "100%", borderRadius: "0.375rem", border: isSelected ? "2px solid #db2777" : "2px solid transparent" }} />
                              </Box>
                            );
                          })}
                        </Box>
                      )}
                    </Box>
                  )}
                </>
              )}
            </Box>

            {/* Scene Preview */}
            <Box>
              {outputItem?.segment_video_url ? (
                <Box>
                  <FormLabel sx={{ color: "#7c3aed", fontWeight: 700 }}>
                    Scene Preview{directTitle ? `: ` : ""}
                    {directTitle && <span style={{ fontSize: "1rem", fontWeight: 400 }}>{directTitle}</span>}
                    {aspectRatio && <span style={{ fontSize: "1rem", fontWeight: 400 }}> ({aspectRatio})</span>}
                  </FormLabel>
                  <Box key={`${item.id}-segment-${mediaVersion}`} component="video" controls
                    src={buildVideoUrl(outputItem.segment_video_url)}
                    sx={{ width: "100%", borderRadius: "0.5rem", border: "2px solid #ddd6fe", display: "block", mx: "auto", ...(aspectRatio === "9:16" ? { maxWidth: "300px" } : {}) }} />
                </Box>
              ) : (
                <Box sx={{ height: "100%", minHeight: 120, display: "flex", alignItems: "center", justifyContent: "center", border: "2px dashed #e2e8f0", borderRadius: "0.5rem", p: 3 }}>
                  <Typography sx={{ fontSize: "0.8125rem", color: "#94a3b8", textAlign: "center" }}>
                    Scene Preview<br />จะแสดงหลังจากสร้างวิดีโอแล้ว
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        )}
      </SceneCard>
    );
  };

  // ─── Render: Review Items (vertical list, used by edit mode fallback) ───────
  const renderReviewItems = () => {
    if (!scriptWithImage?.items) return null;
    return scriptWithImage.items.map((item, index) => renderSingleReviewScene(item, index));
  };

  // ─── Render: Scene List Sidebar (PowerPoint left panel) ─────────────────────
  const handleSelectScene = (index: number | "final") => {
    setSelectedSceneIndex(index);
    setSelectingVersionKey(null);
  };

  // ─── Drag-to-resize handlers ────────────────────────────────────────────────
  const handleDragStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    isDraggingRef.current = true;
    dragStartXRef.current = e.clientX;
    dragStartWidthRef.current = panelWidth;

    const handleMouseMove = (ev: MouseEvent) => {
      if (!isDraggingRef.current) return;
      const delta = ev.clientX - dragStartXRef.current;
      const newWidth = Math.max(256, Math.min(500, dragStartWidthRef.current + delta));
      setPanelWidth(newWidth);
    };

    const handleMouseUp = () => {
      isDraggingRef.current = false;
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
  }, [panelWidth]);

  // ─── Panel drag-and-drop sensors (need activation distance so clicks still work) ──
  const panelSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handlePanelDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id || !scriptWithImage?.items) return;
    const oldIndex = scriptWithImage.items.findIndex((item) => item.id === active.id);
    const newIndex = scriptWithImage.items.findIndex((item) => item.id === over.id);
    handleEditModeReorder(oldIndex, newIndex);
    // Update selected scene index to follow the moved item
    if (typeof selectedSceneIndex === "number") {
      if (selectedSceneIndex === oldIndex) {
        setSelectedSceneIndex(newIndex);
      } else if (oldIndex < selectedSceneIndex && newIndex >= selectedSceneIndex) {
        setSelectedSceneIndex(selectedSceneIndex - 1);
      } else if (oldIndex > selectedSceneIndex && newIndex <= selectedSceneIndex) {
        setSelectedSceneIndex(selectedSceneIndex + 1);
      }
    }
  };

  const renderSceneItems = () => {
    if (!scriptWithImage?.items) return null;

    return scriptWithImage.items.map((item, index) => {
      const isActive = selectedSceneIndex === index;
      const isSceneActive = item.active !== false;

      // List View: ซ่อน scene ที่ disabled (return null คงไว้เพื่อไม่ให้ index เพี้ยน)
      if (!isGridMode && !isSceneActive) return null;

      const outputItem = outputData?.items.find((o: any) => o.id === item.id);

      // ฉากที่ใช้คลิปจากลิงก์ภายนอก (MeLab) — ติดป้ายไว้บน thumbnail ให้เห็นทันทีทั้ง list/grid view โดยไม่ต้องเปิดฉาก
      const externalVideoLink = externalVideoLinkOf(item, outputItem);

      // Thumbnail: prefer uploaded image, then generated image
      const thumbnailUrl = item.uploaded_image_url
        ? getUploadedStaticUrl(item.uploaded_image_url) || ""
        : outputItem?.uploaded_image_url
          ? buildOutputUrl(outputItem.uploaded_image_url)
          : outputItem?.image_url
            ? buildOutputUrl(outputItem.image_url)
            : "";

      return (
        <CardMeGenSortableScene key={item.id} id={item.id}>
          {(dragHandleProps) => (
            <SceneListItem
              id={`megen-gen-direct-scene-item-${item.id}`}
              isActive={isActive}
              sx={isGridMode && !isSceneActive ? { opacity: 0.5 } : undefined}
              onClick={() => {
                // scene ที่ disable คลิกเข้าไปแก้ไม่ได้
                if (isGridMode && !isSceneActive) return;
                handleSelectScene(index);
                if (isGridMode) setIsGridMode(false);
              }}
              {...dragHandleProps}
            >
              <Box sx={{
                width: "100%",
                display: "flex",
                flexDirection: "column",
                gap: "0.25rem",
              }}>
                {/* Scene label + delete */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>

                  {/* Checkbox เปิด/ปิด scene — เฉพาะ Grid View, อยู่หน้า Scene Number Badge */}
                  {isGridMode && (
                    <Checkbox
                      size="small"
                      checked={isSceneActive}
                      disabled={loadingVideo || isRunning}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => handleToggleSceneActive(item.id, e.target.checked)}
                      sx={{ p: 0, mr: 0.25, "&.Mui-checked": { color: "#db2777" } }}
                    />
                  )}

                  <Box sx={{
                    width: 20, height: 20, borderRadius: "50%",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    backgroundColor: isActive ? "#db2777" : "#e2e8f0",
                    color: isActive ? "white" : "#64748b",
                    fontSize: "0.625rem", fontWeight: 700, flexShrink: 0,
                  }}>
                    {index + 1}
                  </Box>
                  <Typography sx={{
                    fontSize: "0.75rem", fontWeight: isActive ? 600 : 500,
                    color: isActive ? "#db2777" : "#1e293b",
                    flex: 1,
                  }}>
                    Scene {index + 1}
                  </Typography>

                  {/* ป้ายบอกสถานะ disabled — เฉพาะ Grid View */}
                  {isGridMode && !isSceneActive && (
                    <Typography sx={{ fontSize: "0.6rem", fontWeight: 700, color: "#f97316", flexShrink: 0 }}>
                      ปิดใช้งาน
                    </Typography>
                  )}

                  {/* Delete button — hidden if only 1 scene or during loading */}
                  {scriptWithImage!.items.length > 1 && !(loadingVideo || isRunning) && (
                    <IconButton
                      size="small"
                      className="scene-delete-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        setDeleteModalItemId(item.id);
                      }}
                      sx={{
                        p: "2px",
                        color: "#cbd5e1",
                        transition: "color 0.15s",
                        "&:hover": { color: "#ef4444", backgroundColor: "#fef2f2" },
                      }}
                    >
                      <DeleteIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  )}
                </Box>

                {/* Thumbnail image */}
                <Box sx={{ position: "relative", width: "100%", display: "flex", flexDirection: "column" }}>
                  {thumbnailUrl ? (
                    <Box
                      component="img"
                      src={thumbnailUrl}
                      alt={`Scene ${index + 1}`}
                      sx={{
                        width: "100%",
                        aspectRatio: aspectRatio.replace(":", "/"),
                        objectFit: "cover",
                        borderRadius: "0.375rem",
                        border: isActive && !isGridMode ? "2px solid #f9a8d4" : "2px solid #e2e8f0",
                      }}
                    />
                  ) : (
                    <Box sx={{
                      width: "100%",
                      aspectRatio: aspectRatio.replace(":", "/"),
                      backgroundColor: isActive ? "#fce7f3" : "#f1f5f9",
                      borderRadius: "0.375rem",
                      border: isActive && !isGridMode ? "2px solid #f9a8d4" : "2px solid #e2e8f0",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}>
                      <Typography sx={{ fontSize: "0.6875rem", color: "#94a3b8" }}>
                        No image
                      </Typography>
                    </Box>
                  )}
                  {/* ป้ายชมพู/ไอคอนขาวมุมบนขวา ให้เห็นบนทั้งภาพและ placeholder; คลิกปล่อยให้ bubble ไปเลือกฉากตามเดิม */}
                  {externalVideoLink !== null && (
                    <Tooltip title={`ใช้วิดีโอจากลิงก์ภายนอก: ${externalVideoLink}`}>
                      <Box
                        id={`megen-gen-direct-scene-external-badge-${item.id}`}
                        sx={{
                          position: "absolute",
                          top: 6,
                          right: 6,
                          width: 22,
                          height: 22,
                          borderRadius: "50%",
                          backgroundColor: "#db2777",
                          color: "white",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          boxShadow: "0 1px 2px rgba(0,0,0,0.3)",
                        }}
                      >
                        <LinkIcon sx={{ fontSize: 14 }} />
                      </Box>
                    </Tooltip>
                  )}
                </Box>
              </Box>
            </SceneListItem>
          )}
        </CardMeGenSortableScene>
      );
    });
  };

  // ─── Add new scene and navigate to it ─────────────────────────────────────────
  const handleAddNewScene = () => {
    if (!scriptWithImage) return;
    handleEditModeAdd();
    // Navigate to the newly added scene (will be last item)
    const newIndex = scriptWithImage.items.length; // after add, this will be the index
    setSelectedSceneIndex(newIndex);
    if (isGridMode) setIsGridMode(false);
  };

  // ─── Toolbar for left panel (New Scene, Final Output, Grid toggle) ──────────
  const renderPanelToolbar = () => {
    const isDisabled = loadingVideo || isRunning;

    return (
      <Box sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.5,
        px: 1,
        py: 0.5,
        borderBottom: "1px solid #e2e8f0",
        backgroundColor: "#f8fafc",
      }}>
        {/* Grid / List toggle */}
        <Tooltip title={isGridMode ? "List view" : "Grid view"}>
          <IconButton
            size="small"
            onClick={() => setIsGridMode((prev) => !prev)}
            sx={{
              color: isGridMode ? "#db2777" : "#64748b",
              backgroundColor: isGridMode ? "#fdf2f8" : "transparent",
              borderRadius: "0.375rem",
              "&:hover": { backgroundColor: "#fdf2f8", color: "#db2777" },
            }}
          >
            {isGridMode ? <ViewListIcon sx={{ fontSize: 20 }} /> : <GridViewIcon sx={{ fontSize: 20 }} />}
          </IconButton>
        </Tooltip>

        <Box sx={{ flex: 1 }} />

        {/* ดาวน์โหลดของทั้งตอน: สคริปต์เป็น .txt และ "ทุกฉาก" เป็น zip ของไฟล์รายฉาก
            (เสียง / วิดีโอ segment ที่เรนเดอร์เสร็จแล้ว)
            ตามที่ผู้ใช้ตัดสินใจไว้ 2026-09-09 — ไฟล์ final ของทั้งตอนอยู่ที่ปุ่มในมุมมอง Final Output ไม่ใช่ที่นี่
            ปุ่ม Script = ข้อความที่อ่านออกเสียงของ scene ที่เปิดใช้ ต่อกันตามลำดับบนจอ (ตัดบล็อก DIRECTOR_NOTES ออก)
            ประกอบจากสคริปต์ที่อยู่ในหน้าจอแล้ว ไม่ยิง request จึงกดได้แม้กำลัง generate ต่างจากปุ่ม zip
            แสดงเฉพาะ grid view (list view แคบเกินกว่าจะใส่ปุ่มเพิ่ม)
            ปุ่มโชว์ตลอดแม้กดไม่ได้ เพื่อให้ layout นิ่งและ tooltip บอกเหตุผลได้ */}
        {isGridMode && (
          <>
            <Tooltip
              title={
                downloadActionsState.script.reason ??
                "ดาวน์โหลดข้อความที่อ่านออกเสียงของทุก scene ที่เปิดใช้ (ตัดคำสั่งเสียง DIRECTOR_NOTES ออก) เป็นไฟล์ .txt"
              }
            >
              {/* span จำเป็น: MUI Button ที่ disabled ไม่ยิง event ⇒ Tooltip ไม่มีทางรู้ว่าเมาส์ชี้อยู่ */}
              <span>
                <Button
                  size="small"
                  id="megen-gen-direct-download-script-btn"
                  startIcon={
                    downloadJob === "script" ? (
                      <CircularProgress size={14} sx={{ color: "inherit" }} />
                    ) : (
                      <DescriptionIcon sx={{ fontSize: 18 }} />
                    )
                  }
                  onClick={handleDownloadScript}
                  disabled={!downloadActionsState.script.enabled}
                  sx={PANEL_TOOLBAR_BUTTON_SX}
                >
                  Download Script
                </Button>
              </span>
            </Tooltip>

            <Tooltip
              title={
                downloadJob === "audio-zip" && downloadProgress
                  ? `กำลังรวมไฟล์ ${downloadProgress.done}/${downloadProgress.total}`
                  : downloadActionsState.audioZip.reason ?? "ดาวน์โหลดไฟล์เสียงของทุกฉากรวมเป็น .zip"
              }
            >
              <span>
                <Button
                  size="small"
                  id="megen-gen-direct-download-all-audio-btn"
                  startIcon={
                    downloadJob === "audio-zip" ? (
                      <CircularProgress size={14} sx={{ color: "inherit" }} />
                    ) : (
                      <AudioFileIcon sx={{ fontSize: 18 }} />
                    )
                  }
                  onClick={handleDownloadAllAudio}
                  disabled={!downloadActionsState.audioZip.enabled}
                  sx={PANEL_TOOLBAR_BUTTON_SX}
                >
                  Download All Audio
                </Button>
              </span>
            </Tooltip>

            <Tooltip
              title={
                downloadJob === "video-zip" && downloadProgress
                  ? `กำลังรวมไฟล์ ${downloadProgress.done}/${downloadProgress.total}`
                  : downloadActionsState.videoZip.reason ??
                    "ดาวน์โหลดวิดีโอ (segment) ของทุกฉากรวมเป็น .zip"
              }
            >
              <span>
                <Button
                  size="small"
                  id="megen-gen-direct-download-all-video-btn"
                  startIcon={
                    downloadJob === "video-zip" ? (
                      <CircularProgress size={14} sx={{ color: "inherit" }} />
                    ) : (
                      <VideoFileIcon sx={{ fontSize: 18 }} />
                    )
                  }
                  onClick={handleDownloadAllVideo}
                  disabled={!downloadActionsState.videoZip.enabled}
                  sx={PANEL_TOOLBAR_BUTTON_SX}
                >
                  Download All Video
                </Button>
              </span>
            </Tooltip>
          </>
        )}

        {/* New Scene — right-aligned */}
        <Button
          size="small"
          startIcon={<AutoAwesomeMotionIcon sx={{ fontSize: 18 }} />}
          onClick={handleAddNewScene}
          disabled={isDisabled}
          sx={PANEL_TOOLBAR_BUTTON_SX}
        >
          New Scene
        </Button>
      </Box>
    );
  };

  const renderSceneListSidebar = () => {
    if (!scriptWithImage?.items) return null;

    return (
      <SceneListPanel sx={{ width: isGridMode ? "100%" : panelWidth }}>
        {renderPanelToolbar()}
        <SceneListScrollArea sx={isGridMode ? {
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
          gap: "0.5rem",
          padding: "0.75rem",
        } : undefined}>
          <DndContext
            sensors={panelSensors}
            collisionDetection={closestCenter}
            onDragEnd={handlePanelDragEnd}
          >
            <SortableContext
              items={scriptWithImage.items
                .filter((item) => isGridMode || item.active !== false)
                .map((item) => item.id)}
              strategy={isGridMode ? rectSortingStrategy : verticalListSortingStrategy}
            >
              {renderSceneItems()}
            </SortableContext>
          </DndContext>
        </SceneListScrollArea>
      </SceneListPanel>
    );
  };

  // ─── Render: Selected Scene Content (PowerPoint right panel) ────────────────
  const renderSelectedSceneContent = () => {
    if (selectedSceneIndex === "final") {
      if (!finalVideoUrl) return null;
      return (
        <SceneContentPanel>
          <CardMeGenFinalVideoDirectScript
            runName={runName}
            videoPath={finalVideoUrl}
            isReadyForUpload={isReadyForUpload}
            onUploadToYoutube={handleUploadYoutube}
          />
        </SceneContentPanel>
      );
    }

    if (!scriptWithImage?.items || typeof selectedSceneIndex !== "number" || selectedSceneIndex >= scriptWithImage.items.length) return null;

    const activeItem = scriptWithImage.items[selectedSceneIndex];
    const isCheckingThisScene = loadingCostCheck && pendingGenerateSceneItemId === activeItem.id;
    const isGeneratingThisScene = generatingSceneItemId === activeItem.id;
    const isThisSceneBusy = isCheckingThisScene || isGeneratingThisScene;

    return (
      <SceneContentPanel>
        {/* Scene navigation + Generate button */}
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1, px: 0.5, gap: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Button
              size="small"
              startIcon={<NavigateBeforeIcon />}
              disabled={selectedSceneIndex === 0}
              onClick={() => handleSelectScene(selectedSceneIndex - 1)}
              sx={{ textTransform: "none", fontSize: "0.8125rem", color: "#64748b" }}
            >
              Previous
            </Button>
            <Typography sx={{ fontSize: "1rem", fontWeight: 700, color: "#1e293b" }}>
              Scene {selectedSceneIndex + 1} / {scriptWithImage.items.length}
            </Typography>
            <Button
              size="small"
              endIcon={<NavigateNextIcon />}
              disabled={selectedSceneIndex >= scriptWithImage.items.length - 1}
              onClick={() => handleSelectScene(selectedSceneIndex + 1)}
              sx={{ textTransform: "none", fontSize: "0.8125rem", color: "#64748b" }}
            >
              Next
            </Button>
          </Box>
          {finalVideoUrl ? <SuccessButton
            ref={genSceneBtnRef}
            id="megen-gen-direct-generate-video-btn"
            startIcon={
              isThisSceneBusy
                ? <CircularProgress size={16} sx={{ color: "white" }} />
                : <MovieIcon />
            }
            onClick={() => handleClickGenerateScene(activeItem.id)}
            disabled={isThisSceneBusy || loadingVideo || isRunning}
            sx={
              generateAsFinal
                ? { backgroundColor: "#7c3aed", "&:hover": { backgroundColor: "#6d28d9" } }
                : {}
            }
          >
            {isCheckingThisScene
              ? "กำลังตรวจสอบ..."
              : isGeneratingThisScene
                ? "Generating..."
                : `Generate Scene ${selectedSceneIndex + 1}`}
          </SuccessButton> : null}
        </Box>
        {renderSingleReviewScene(activeItem, selectedSceneIndex)}
      </SceneContentPanel>
    );
  };

  // ─── Render: PowerPoint Layout ──────────────────────────────────────────────
  const renderPowerPointLayout = () => {
    return (
      <ReviewPanelContainer>
        {renderSceneListSidebar()}
        {!isGridMode && <PanelDragHandle onMouseDown={handleDragStart} />}
        {!isGridMode && renderSelectedSceneContent()}
      </ReviewPanelContainer>
    );
  };

  // ─── Render: Full-width Final Output View ─────────────────────────────────────
  const renderFinalOutputView = () => {
    if (!finalVideoUrl) return null;

    // ปุ่มนี้เรียก runner ตัวเดียวกับปุ่มบน toolbar ซึ่งจะ "ปฏิเสธเงียบ ๆ" เมื่อ action ถูกปิดอยู่
    // ⇒ ต้องอ่านสถานะชุดเดียวกันมาแสดง ไม่งั้นผู้ใช้กดแล้วไม่มีอะไรเกิดขึ้นและไม่รู้เหตุผล
    const legacyAction = downloadActionsState.final;

    return (
      <Box sx={{ px: 1, py: 2 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <SectionTitle sx={{ mb: 0 }}>Final Output: {runName}</SectionTitle>
          {/* run Audio Only ส่ง mp3 มาใน final_video_url ⇒ ต้องเปลี่ยนทั้งป้ายและตัวจัดการให้ตรงชนิดไฟล์
              (ของเดิมตั้งชื่อไฟล์เป็น .mp4 เสมอ) ส่วนสถานะปิด/เหตุผล ใช้ชุดเดียวกับปุ่มบน toolbar
              เพราะ runner จะไม่ทำอะไรเลยเมื่อ action ถูกปิด (โหมด YouTube ไม่เกี่ยว ⇒ กดได้ตลอด) */}
          {isReadyForUpload && (
            <Tooltip title={CONFIG.MEGEN_USE_DOWNLOAD_URL_BTN ? (legacyAction.reason ?? "") : ""}>
              {/* span จำเป็น: MUI Button ที่ disabled ไม่ยิง event ⇒ Tooltip ไม่มีทางรู้ว่าเมาส์ชี้อยู่ */}
              <span>
                <DangerButton
                  startIcon={
                    // ระหว่างโหลด (ไฟล์ final ใหญ่ได้เป็นร้อย MB) ต้องเห็นว่ากดติดแล้ว ไม่งั้นผู้ใช้กดซ้ำ
                    // — runner ปฏิเสธการกดซ้ำเงียบ ๆ อยู่แล้ว จึงดูเหมือนปุ่มเสีย
                    CONFIG.MEGEN_USE_DOWNLOAD_URL_BTN ? (
                      downloadJob === "final" ? (
                        <CircularProgress size={14} sx={{ color: "inherit" }} />
                      ) : (
                        <DownloadIcon />
                      )
                    ) : (
                      <UploadIcon />
                    )
                  }
                  onClick={
                    CONFIG.MEGEN_USE_DOWNLOAD_URL_BTN ? handleDownloadFinalFile : handleUploadYoutube
                  }
                  disabled={CONFIG.MEGEN_USE_DOWNLOAD_URL_BTN && !legacyAction.enabled}
                >
                  {CONFIG.MEGEN_USE_DOWNLOAD_URL_BTN
                    ? finalMediaKind === "audio"
                      ? "Download Audio"
                      : "Download Video"
                    : "Upload to YouTube"}
                </DangerButton>
              </span>
            </Tooltip>
          )}
        </Box>
        <Box
          component="video"
          controls
          src={finalVideoUrl}
          sx={{
            width: "100%",
            maxHeight: "70vh",
            borderRadius: "0.75rem",
            backgroundColor: "black",
            aspectRatio: "16/9",
          }}
        />
      </Box>
    );
  };

  // ─── Main Render ──────────────────────────────────────────────────────────────

  return (
    <>
      <Container id="megen-gen-direct-root">
        {/* --- Breadcrumb + Title Edit + Thread ID --- */}
        <Box id="megen-gen-direct-header" sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: "1rem", flexWrap: "wrap", gap: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0, flex: 1 }}>
            {/* Remove breadcrumb's own margin so it aligns vertically */}
            <Box sx={{ "& > *": { mb: "0 !important", pb: 0 } }}>
              <MeGenBreadcrumbs items={breadcrumbItems} onNavigate={() => props.onNavigateBack?.()} />
            </Box>

            {/* Edit title (inline after breadcrumb) */}
            {hasReviewData && !loadingDraft && !isRunning && !isEditingTitle && (
              <Tooltip title="Edit title & re-generate">
                <IconButton
                  id="megen-gen-direct-edit-title-btn"
                  size="small"
                  onClick={() => {
                    setTitleDraft(directTitle || runName);
                    setIsEditingTitle(true);
                  }}
                  sx={{
                    color: "#94a3b8",
                    padding: "4px",
                    "&:hover": { color: "#f382a0", backgroundColor: "#fdf2f8" },
                  }}
                >
                  <EditIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            )}

            {/* Inline title editing */}
            {isEditingTitle && (
              <Box id="megen-gen-direct-title-edit-form" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <TextField
                  id="megen-gen-direct-title-input"
                  size="small"
                  value={titleDraft}
                  onChange={(e) => setTitleDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleSaveTitle();
                    if (e.key === "Escape") setIsEditingTitle(false);
                  }}
                  autoFocus
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      fontSize: "0.875rem",
                      fontWeight: 600,
                      color: "#1e293b",
                      "& fieldset": { borderColor: "#f9a8d4" },
                      "&.Mui-focused fieldset": { borderColor: "#f382a0" },
                    },
                    minWidth: 220,
                  }}
                />
                <Tooltip title="Save Title">
                  <IconButton
                    size="small"
                    onClick={handleSaveTitle}
                    disabled={!titleDraft.trim() || loadingDraft}
                    sx={{
                      color: "#f382a0",
                      "&:hover": { backgroundColor: "#fdf2f8", color: "#db2777" },
                    }}
                  >
                    <CheckCircleIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Cancel">
                  <IconButton
                    size="small"
                    onClick={() => setIsEditingTitle(false)}
                    sx={{ color: "#94a3b8", "&:hover": { backgroundColor: "#f1f5f9" } }}
                  >
                    <CloseIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            )}
          </Box>

          {/* Right side: Thread ID + Running indicator */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexShrink: 0 }}>
            <AiUsageButton
              id="megen-gen-direct-ai-usage-btn"
              threadId={props.threadId}
              canViewAiUsage={props.canViewAiUsage}
              onFetchAiUsage={props.onFetchAiUsage}
              onOpen={() => setIsAiUsageOpen(true)}
            />
            <Typography sx={{ fontSize: "0.75rem", color: "#94a3b8" }}>
              Thread: {props.threadId || "ยังไม่ได้เริ่ม"}
            </Typography>
            {isRunning && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, color: "#f382a0" }}>
                <CircularProgress size={16} sx={{ color: "#f382a0" }} />
                <Typography sx={{ fontSize: "0.8125rem", fontWeight: 500 }}>Running...</Typography>
              </Box>
            )}
          </Box>
        </Box>

        {/* --- Progress Steps --- */}
        {renderProgressSteps()}

        {/* --- Input Section (ซ่อนระหว่าง isRunning) --- */}
        {!hasReviewData && !isRunning && (
          <SectionCard id="megen-gen-direct-input-card">
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: isInputCollapsed ? 0 : 2,
                pb: isInputCollapsed ? 0 : 1,
                borderBottom: isInputCollapsed ? "none" : "1px solid #e2e8f0",
                cursor: hasReviewData ? "pointer" : "default",
              }}
              onClick={() => {
                if (hasReviewData) setIsInputCollapsed((prev) => !prev);
              }}
            >
              <SectionTitle sx={{ mb: 0 }}>
                {isScriptToSceneDone ? "Prompt Kits" : "Create Scenes"}
              </SectionTitle>
              {hasReviewData && (
                <IconButton
                  size="small"
                  sx={{ color: "#64748b" }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsInputCollapsed((prev) => !prev);
                  }}
                >
                  {isInputCollapsed ? <ExpandMoreIcon /> : <ExpandLessIcon />}
                </IconButton>
              )}
            </Box>

            <Collapse in={!isInputCollapsed}>
              {!isScriptToSceneDone ? renderScript2SceneContent() : renderDirectScriptContent()}
            </Collapse>
          </SectionCard>
        )}

        {/* --- Draft & Review Button --- */}
        {!hasReviewData && !isRunning && isScriptToSceneDone && (
          <SectionCard id="megen-gen-direct-draft-card">
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end" }}>
              <SuccessButton
                id="megen-gen-direct-draft-btn"
                startIcon={
                  loadingDraft ? (
                    <CircularProgress size={16} sx={{ color: "white" }} />
                  ) : (
                    <PlayArrowIcon />
                  )
                }
                onClick={handleDraft}
                disabled={loadingDraft || !directTitle?.trim()}
              >
                {loadingDraft ? "กำลังสร้าง Prompts..." : "สร้าง Prompt"} {/** TODO: Edit */}
              </SuccessButton>
            </Box>
          </SectionCard>
        )}

        {/* --- Review Section (แสดงเมื่อมี data) --- */}
        {hasReviewData && (
          <SectionCard id="megen-gen-direct-review-card">
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: isReviewCollapsed && !isEditMode && !isFinalOutputMode ? 0 : 2,
                pb: isReviewCollapsed && !isEditMode && !isFinalOutputMode ? 0 : 1,
                borderBottom: isReviewCollapsed && !isEditMode && !isFinalOutputMode ? "none" : "1px solid #e2e8f0",
                cursor: isEditMode || isFinalOutputMode ? "default" : "pointer",
              }}
              onClick={() => { if (!isEditMode && !isFinalOutputMode) setIsReviewCollapsed((prev) => !prev); }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <SectionTitle sx={{ mb: 0 }}>
                  Scene Preview{directTitle ? `: ` : ""}
                  {directTitle && <span style={{ fontSize: "1rem", fontWeight: 400 }}>{directTitle}</span>}
                  {aspectRatio && <span style={{ fontSize: "1rem", fontWeight: 400 }}> ({aspectRatio})</span>}
                </SectionTitle>
                {(loadingVideo || isRunning) && !isEditMode && !isFinalOutputMode && (
                  <Typography
                    component="span"
                    sx={{ fontSize: "0.75rem", fontWeight: 500, color: "#94a3b8" }}
                  >
                    (read-only ขณะ Generate)
                  </Typography>
                )}
              </Box>

              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {/* Final Output Button */}
                {(
                  <SuccessButton
                    id="megen-gen-direct-final-output-toggle-btn"
                    startIcon={<OndemandVideoIcon />}
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsFinalOutputMode((prev) => {
                        if (!prev) {
                          setIsReviewCollapsed(false);
                        }
                        return !prev;
                      });
                    }}
                    disabled={!finalVideoUrl || isRunning}
                    sx={{ "&.Mui-disabled": { backgroundColor: "#f382a0", color: "white", opacity: 0.5 } }}
                  >
                    {isFinalOutputMode ? "Exit Final Output" : "Final Output"}
                  </SuccessButton>
                )}

                {/* Collapse Toggle — hidden during Edit Mode and Final Output Mode */}
                {!isEditMode && !isFinalOutputMode && (
                  <IconButton
                    size="small"
                    sx={{ color: "#64748b" }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsReviewCollapsed((prev) => !prev);
                    }}
                  >
                    {isReviewCollapsed ? <ExpandMoreIcon /> : <ExpandLessIcon />}
                  </IconButton>
                )}
              </Box>
            </Box>

            <CollapseCard in={!isReviewCollapsed || isEditMode || isFinalOutputMode}>
              {isFinalOutputMode
                ? renderFinalOutputView()
                : isEditMode
                  ? renderEditModeItems()
                  : renderPowerPointLayout()}
            </CollapseCard>
          </SectionCard>
        )}

        {/* --- Generate Section --- */}
        {hasReviewData && !isRunning && (
          <SectionCard id="megen-gen-direct-generate-card">
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 2, flexWrap: "wrap" }}>
              {/* Run-level default Audio Padding — ใช้กับทุก scene ที่ไม่ได้กำหนดเอง */}
              <Box id="megen-gen-direct-audio-padding" sx={{ display: "flex", alignItems: "center", gap: 1, mr: "auto" }}>
                <FormControlLabel
                  sx={{ mr: 0 }}
                  control={
                    <Checkbox
                      id="megen-gen-direct-padding-use-global"
                      checked={useGlobalPadding}
                      onChange={(e) => setUseGlobalPadding(e.target.checked)}
                      disabled={loadingVideo || loadingCostCheck}
                      sx={{ "&.Mui-checked": { color: "#f382a0" } }}
                    />
                  }
                  label={
                    <Box>
                      <Typography sx={{ fontSize: "0.8rem", color: "#334155", fontWeight: 600, whiteSpace: "nowrap" }}>
                        Audio Padding — All Scenes (sec)
                      </Typography>
                      <Typography sx={{ fontSize: "0.65rem", color: "#94a3b8", whiteSpace: "nowrap" }}>
                        {useGlobalPadding
                          ? "ใช้ค่ากลางนี้กับทุก scene — ช่องใน scene ถูกปิด"
                          : "ไม่ใช้ค่ากลาง — กำหนดอิสระในแต่ละ scene"}
                      </Typography>
                    </Box>
                  }
                />
                {(
                  [
                    { key: "start", label: "Start", value: globalPaddingStart, set: setGlobalPaddingStart },
                    { key: "end", label: "End", value: globalPaddingEnd, set: setGlobalPaddingEnd },
                  ] as const
                ).map((field) => (
                  <TextField
                    key={field.key}
                    id={`megen-gen-direct-padding-${field.key}`}
                    size="small"
                    type="number"
                    label={field.label}
                    value={field.value}
                    onChange={(e) => field.set(e.target.value)}
                    onBlur={() => field.set(String(parsePadding(field.value) ?? 0))}
                    disabled={!useGlobalPadding || loadingVideo || loadingCostCheck}
                    inputProps={{ min: 0, max: AUDIO_PADDING_MAX, step: 0.1 }}
                    sx={{
                      width: 90,
                      "& .MuiOutlinedInput-root": { borderRadius: "0.5rem", fontSize: "0.85rem" },
                    }}
                  />
                ))}
              </Box>

              <FormControlLabel
                control={
                  <Checkbox
                    checked={generateAsAudioOnly}
                    onChange={(e) => handleToggleAudioOnly(e.target.checked)}
                    disabled={loadingVideo}
                    sx={{ "&.Mui-checked": { color: "#f382a0" } }}
                  />
                }
                label={<Typography>Generate Audio Only</Typography>}
              />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={generateAsFinal}
                    onChange={(e) => setGenerateAsFinal(e.target.checked)}
                    disabled={loadingVideo}
                    sx={{ "&.Mui-checked": { color: "#f382a0" } }}
                  />
                }
                label={<Typography>Final Output (HD)</Typography>}
              />

              <SuccessButton
                id="megen-gen-direct-generate-video-btn"
                startIcon={
                  (loadingVideo || loadingCostCheck)
                    ? <CircularProgress size={16} sx={{ color: "white" }} />
                    : <MovieIcon />
                }
                onClick={handleClickGenerateVideo}
                disabled={loadingVideo || loadingCostCheck}
                sx={
                  generateAsFinal
                    ? { backgroundColor: "#7c3aed", "&:hover": { backgroundColor: "#6d28d9" } }
                    : {}
                }
              >
                {loadingVideo
                  ? "กำลังสร้างวิดีโอ..."
                  : loadingCostCheck
                    ? "กำลังตรวจสอบ..."
                    : generateAsFinal
                      ? "สร้างวิดีโอ Final"
                      : "สร้างวิดีโอ Review"}
              </SuccessButton>
            </Box>
          </SectionCard>
        )}

        {/* --- Final Video Output (standalone, only when no review panel) --- */}
        {!hasReviewData && !isRunning && finalVideoUrl && (
          <CardMeGenFinalVideoDirectScript
            runName={runName}
            videoPath={finalVideoUrl}
            isReadyForUpload={isReadyForUpload}
            onUploadToYoutube={handleUploadYoutube}
          />
        )}

        {/* YouTube Upload Modal */}
        {showUploadModal && (
          <Box
            id="megen-gen-direct-upload-modal-overlay"
            sx={{
              position: "fixed",
              inset: 0,
              zIndex: 50,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "rgba(0,0,0,0.5)",
            }}
            onClick={() => setShowUploadModal(false)}
          >
            <Box
              id="megen-gen-direct-upload-modal"
              sx={{
                backgroundColor: "white",
                borderRadius: "0.75rem",
                padding: "1.5rem",
                minWidth: "320px",
                maxWidth: "420px",
                width: "100%",
                boxShadow: "0 20px 40px rgba(0,0,0,0.15)",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <Box
                id="megen-gen-direct-upload-modal-header"
                sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <YouTubeIcon sx={{ color: "#e91e1e", fontSize: "1.5rem" }} />
                  <Typography sx={{ fontSize: "1rem", fontWeight: 600, color: "#1e293b" }}>
                    Select YouTube Channel
                  </Typography>
                </Box>
                <IconButton id="megen-gen-direct-upload-modal-close-btn" size="small" onClick={() => setShowUploadModal(false)}>
                  <CloseIcon sx={{ fontSize: "1.1rem" }} />
                </IconButton>
              </Box>
              <Typography sx={{ fontSize: "0.8rem", color: "#64748b", mb: 1.5 }}>
                Choose which channel to upload this video to:
              </Typography>
              <Box id="megen-gen-direct-upload-modal-channel-list" sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {youtubeChannels.map((ch) => (
                  <Box
                    id={`megen-gen-direct-upload-channel-${ch.id}`}
                    key={ch.id}
                    onClick={() => {
                      if (ch.status !== "connected") return;
                      setShowUploadModal(false);
                      props.onUploadYoutube?.(props.threadId!, ch._id, (newState: DirectScriptGraphState) => {
                        setGraphState((prev) => {
                          if (prev && JSON.stringify(prev) === JSON.stringify(newState)) return prev;
                          return newState;
                        });
                      });
                    }}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      padding: "0.75rem 1rem",
                      border: "1px solid #e2e8f0",
                      borderRadius: "0.5rem",
                      cursor: ch.status === "connected" ? "pointer" : "not-allowed",
                      opacity: ch.status === "connected" ? 1 : 0.5,
                      "&:hover": ch.status === "connected" ? { backgroundColor: "#f8fafc" } : {},
                    }}
                  >
                    <YouTubeIcon sx={{ color: "#e91e1e", fontSize: "1.25rem", flexShrink: 0 }} />
                    <Box sx={{ flex: 1 }}>
                      <Typography sx={{ fontSize: "0.875rem", fontWeight: 500, color: "#1e293b" }}>
                        {ch.channel_name || "Unknown Channel"}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "0.7rem",
                          color: ch.status === "connected" ? "#22c55e" : "#f59e0b",
                        }}
                      >
                        {ch.status}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
              <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
                <Button
                  id="megen-gen-direct-upload-modal-cancel-btn"
                  onClick={() => setShowUploadModal(false)}
                  sx={{ textTransform: "none", color: "#64748b" }}
                >
                  Cancel
                </Button>
              </Box>
            </Box>
          </Box>
        )}
      </Container>

      {/* Generate Scene Mode */}
      <Popper
        open={showGenActionModal}
        anchorEl={genSceneBtnRef.current}
        placement="bottom-end"
      >
        <ClickAwayListener onClickAway={() => setShowGenActionModal(false)}>
          <Paper elevation={4} sx={{ borderRadius: "0.75rem", overflow: "hidden", minWidth: 220, mt: 0.5, padding: 1.25 }}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <Button
                fullWidth
                startIcon={<MovieIcon />}
                onClick={() => {
                  handleConfirmGenAction(["gen"]);
                }}
                sx={{
                  textTransform: "none", fontWeight: 600, borderRadius: "0.75rem",
                  py: 1.25, justifyContent: "flex-start", px: 2,
                  backgroundColor: "#fdf2f8", color: "#db2777",
                  "&:hover": { backgroundColor: "#fce7f3" },
                }}
              >
                <Box sx={{ textAlign: "left" }}>
                  <Typography sx={{ fontWeight: 700, fontSize: "0.875rem", color: "#db2777" }}>
                    สร้างเฉพาะ Scene นี้
                  </Typography>
                  <Typography sx={{ fontSize: "0.75rem", color: "#94a3b8" }}>
                    Generate ภาพ / เสียง / วิดีโอ ของ Scene นี้เท่านั้น
                  </Typography>
                </Box>
              </Button>

              <Button
                fullWidth
                startIcon={<AutoAwesomeMotionIcon />}
                onClick={() => {
                  handleConfirmGenAction(["gen", "merge"]);
                }}
                sx={{
                  textTransform: "none", fontWeight: 600, borderRadius: "0.75rem",
                  py: 1.25, justifyContent: "flex-start", px: 2,
                  backgroundColor: "#f5f3ff", color: "#7c3aed",
                  "&:hover": { backgroundColor: "#ede9fe" },
                }}
              >
                <Box sx={{ textAlign: "left" }}>
                  <Typography sx={{ fontWeight: 700, fontSize: "0.875rem", color: "#7c3aed" }}>
                    สร้าง Scene นี้และรวมวิดีโอ
                  </Typography>
                  <Typography sx={{ fontSize: "0.75rem", color: "#94a3b8" }}>
                    Generate แล้วรวมเป็นวิดีโอสมบูรณ์ทันที
                  </Typography>
                </Box>
              </Button>
            </Box>
          </Paper>
        </ClickAwayListener>
      </Popper>

      {deleteModalItemId && (
        <Box id="megen-gen-direct-delete-modal-overlay" sx={{ position: "fixed", inset: 0, zIndex: 999, display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: "rgba(0,0,0,0.4)" }}
          onClick={() => setDeleteModalItemId(null)}
        >
          <Box id="megen-gen-direct-delete-modal" sx={{ backgroundColor: "white", borderRadius: "0.75rem", p: 3, maxWidth: 360, width: "100%", boxShadow: "0 20px 40px rgba(0,0,0,0.15)" }}
            onClick={(e) => e.stopPropagation()}
          >
            <Typography sx={{ fontWeight: 700, fontSize: "1rem", color: "#1e293b", mb: 1 }}>
              ลบ Scene นี้?
            </Typography>
            <Typography sx={{ fontSize: "0.875rem", color: "#64748b", mb: 3 }}>
              Scene {scriptWithImage?.items.findIndex(i => i.id === deleteModalItemId)! + 1} จะถูกลบออกถาวร
            </Typography>
            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
              <Button id="megen-gen-direct-delete-modal-cancel-btn" onClick={() => setDeleteModalItemId(null)} sx={{ textTransform: "none", color: "#64748b" }}>ยกเลิก</Button>
              <Button id="megen-gen-direct-delete-modal-confirm-btn" onClick={() => handleEditModeDelete(deleteModalItemId)}
                sx={{ textTransform: "none", backgroundColor: "#ef4444", color: "white", borderRadius: "9999px", px: 2, "&:hover": { backgroundColor: "#dc2626" } }}>
                ลบ
              </Button>
            </Box>
          </Box>
        </Box>
      )}

      {/* Cost Modal */}
      <CardMeGenCostModal
        open={showCostModal}
        costDetails={costDetails}
        regenerateItems={regenerateItems}
        sceneIndexMap={sceneIndexMap}
        onClose={() => {
          setShowCostModal(false)
          setPendingGeneratePayload(null)
        }}
        onConfirm={handleConfirmGenerate}
      />

      {/* ยืนยันลบ Image Asset */}
      <ModConfirmDelete
        open={!!pendingDeleteAsset}
        onClose={() => setPendingDeleteAsset(null)}
        onConfirm={handleConfirmDeleteAsset}
        title="ยืนยันการลบ Image Asset"
        confirmText="ลบ"
        cancelText="ยกเลิก"
        message={
          `ต้องการลบ "${pendingDeleteAsset?.original_name || ""}" หรือไม่?\n` +
          `รูปนี้จะถูกนำออกจาก Scene ที่เลือกไว้ทั้งหมด\n` +
          `และไม่สามารถกู้คืนได้`
        }
      />

      {/* Alert: Media Type = Video แต่ยังไม่มีวิดีโอ */}
      <ModConfirmDelete
        open={!!videoModeIssueScenes?.length}
        onClose={() => setVideoModeIssueScenes(null)}
        title="ไม่สามารถสร้างวิดีโอได้"
        cancelText="รับทราบ"
        message={
          <Box sx={{ width: "100%", textAlign: "left" }}>
            <Typography sx={{ fontSize: "0.875rem", color: "#64748b", lineHeight: 1.6, mb: 1.5 }}>
              Scene ต่อไปนี้เลือก Media Type เป็น "Video" แต่ยังไม่มีวิดีโอ
            </Typography>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, mb: 2 }}>
              {(videoModeIssueScenes || []).map((sceneNo) => (
                <Box
                  key={sceneNo}
                  sx={{
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    px: 1.5, py: 1,
                    backgroundColor: "#f8fafc", borderRadius: "0.375rem",
                    border: "1px solid #f1f5f9",
                  }}
                >
                  <Typography sx={{ fontSize: "0.8125rem", fontWeight: 600, color: "#1e293b" }}>
                    Scene {sceneNo}
                  </Typography>
                  <Box
                    sx={{
                      fontSize: "0.65rem", fontWeight: 700,
                      px: 0.75, py: 0.25,
                      backgroundColor: "#fef2f2",
                      color: "#dc2626",
                      borderRadius: "9999px",
                      border: "1px solid #fecaca",
                    }}
                  >
                    ไม่มี Video
                  </Box>
                </Box>
              ))}
            </Box>

            <Typography sx={{ fontSize: "0.8125rem", color: "#94a3b8", lineHeight: 1.6 }}>
              กรุณา Upload Video หรือระบุ Video Prompt ก่อนสร้างวิดีโอ
            </Typography>
          </Box>
        }
      />

      {/* --- AI Usage (See more) --- */}
      <ModMeGenAiUsage
        open={isAiUsageOpen}
        threadId={props.threadId}
        userNameMap={props.aiUsageUserNameMap}
        threadTitle={directTitle || props.runTitle}
        onClose={() => setIsAiUsageOpen(false)}
        onFetchUsage={props.onFetchAiUsage}
      />
    </>
  );
};

export default CardGenerateDirectScript;
